import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Package, X, CheckCircle, ArrowUp, ArrowDown, Download, Upload, AlertCircle, ChevronLeft, ChevronRight, Zap, Truck, Store, MoreVertical, Link2, Ban, Boxes, RefreshCw, RotateCcw, PackagePlus, Clock, PackageOpen, Plus, Trash2, Printer, Loader2, Tag, PackageCheck } from "lucide-react";
import { useTranslation } from "../i18n";
import { toast } from "react-toastify";
import { type OrderStatus } from "../components/dispatch/orderStatus";
import type { DispatchOrder, Parcel } from "../types/Dispatch";
import AdminPageHeader from "../components/admin-page-header";
import SkuMappingModal from "../components/dispatch/sku-mapping-modal";
import { displayBarcodes } from "../components/dispatch/itemBarcodes";
import CreateOrderModal from "../components/dispatch/create-order-modal";
import OrderProductPicker from "../components/dispatch/order-product-picker";
import type { PickedProduct } from "../components/dispatch/order-product-picker";
import OrderAttachments from "../components/dispatch/order-attachments";
import BulkLabelUploadModal from "../components/dispatch/bulk-label-upload-modal";
import BulkFulfilUploadModal, { isSingleWarehouse } from "../components/dispatch/bulk-fulfil-upload-modal";
import ParcelLabelButtons, { useParcelLabels, printableParcels, isCollectionParcel } from "../components/dispatch/parcel-label-actions";
import CollectionLabel from "../components/dispatch/collection-label";
import { buildPrintDocument } from "../components/helpers/printAgent";
import DispatchPrinterButton from "../components/dispatch/dispatch-printer-button";
import OrderNotes from "../components/dispatch/order-notes";
import OrderPackClaim from "../components/dispatch/order-pack-claim";
import OrderReturnsPanel from "../components/returns/OrderReturnsPanel";
import OrderReturnsPaymentPanel from "../components/returns/OrderReturnsPaymentPanel";
import OrderClaims from "../components/dispatch/order-claims";
import OrderChannelDocuments from "../components/dispatch/order-channel-documents";
import ReplacementOrderModal from "../components/dispatch/replacement-order-modal";
import ExtraBoxModal from "../components/dispatch/extra-box-modal";
import SendToPackPanel from "../components/dispatch/send-to-pack-panel";
import ManualFulfilModal from "../components/dispatch/manual-fulfil-modal";
import type { ManualFulfilTarget, ManualTrackingEntry } from "../components/dispatch/manual-fulfil-modal";
import type { ReplacementParcelScope } from "../components/dispatch/replacement-order-modal";
import type { DispatchOrderItem } from "../types/Dispatch";
import { EU_COUNTRY_OPTIONS, dialCodeForCountry, isValidNationalForDial, EU_PHONE_FORMATS } from "../components/helpers/phoneFormat";
import { dueClockStart, isDueByToday, isExpressDeliveryType, londonDateOf } from "../components/helpers/dispatchDue";
import { refundsViaChannel } from "../components/helpers/channelRefunds";
import { summariseCarrierVoids, carrierVoidFailureDetail, carrierReissueDetail } from "../components/helpers/carrierVoidResult";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { apiFetch } from "../api/apiFetch";
import { isManagerPlus } from "../access/positions";
import { useReadOnly } from "../access/ReadOnly";
import type { LogRow } from "../types/Log";
import CustomerEmailLogModal, { customerEmailOf, customerEmailChips } from "../components/dispatch/customer-email-log";
import "../styles/dispatch.css";
import "../styles/admin-settings.css";
import "../styles/logs.css";
import { useWarehouseScope } from "../access/useWarehouseScope";
import { useAccess } from "../access/AccessContext";
import { resolveAttribution } from "../components/dispatch/resolve-api";

// Column order for CSV download / template / upload. Mirrors the BE
// bulkUpdateOrders shape so a round-trip download → upload is lossless.
// Each row is either an order-header row (line_position blank) or a line
// item row (line_position set) — never both, mirroring "one row per thing
// being changed".
const CSV_COLUMNS = [
    "action", "order_id", "line_position",
    "order_number", "sales_channel", "warehouse", "status", "delivery_type",
    "currency", "total", "shipping_paid", "tax_amount", "tax_rate",
    "delivery_name", "delivery_line_1", "delivery_line_2", "delivery_city",
    "delivery_county", "delivery_postcode", "delivery_country",
    "delivery_phone_country", "delivery_phone_number", "delivery_email",
    "billing_name", "billing_line_1", "billing_line_2", "billing_city",
    "billing_county", "billing_postcode", "billing_country",
    "billing_phone_country", "billing_phone_number", "billing_email",
    "notes", "channel_created_at",
    "sku", "quantity", "unit_price",
    "scanned_count", "missing_count", "damaged_count",
    "wrong_item_count", "barcode_damaged_count", "refund_count",
] as const;

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
    // false when the BE's atomic validation rejected the whole file
    // (nothing was applied). Absent on responses from older paths.
    applied?: boolean;
    // Orders this batch released from On hold that the BE then merged
    // into a consolidated shipment (the match they missed at ingest,
    // when held orders are skipped). consolidated_number is the CON
    // number the list shows.
    consolidated?: { order_id: string; consolidated_into: string; consolidated_number: string | null }[];
}

const toCsv = (rows: string[][]): string =>
    rows.map(r => r.map(cell => {
        const s = String(cell ?? "");
        if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    }).join(",")).join("\r\n");

const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let field = "";
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else { inQuotes = false; }
            } else {
                field += ch;
            }
        } else {
            if (ch === '"') inQuotes = true;
            else if (ch === ",") { row.push(field); field = ""; }
            else if (ch === "\r") { /* skip */ }
            else if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
            else field += ch;
        }
    }
    if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(c => c.trim() !== ""));
};

const UTF8_BOM = "﻿";
const triggerDownload = (filename: string, contents: string) => {
    const blob = new Blob([UTF8_BOM + contents], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const numStr = (v: number | null | undefined) => v == null ? "" : String(v);

// Tracking URLs originate outside the FE (courier APIs, bulk uploads,
// integration partners). Only http(s) may be rendered as a clickable
// href — a javascript:/data: URI would execute in the staff browser.
const safeHttpUrl = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    try {
        const u = new URL(raw);
        return u.protocol === "http:" || u.protocol === "https:" ? raw : null;
    } catch {
        return null;
    }
};

// A manual fulfilment recorded with a carrier or a link but no tracking
// code still needs a primary key for its parcel row, so the BE stores a
// synthesised 'MANUAL-<order id>' (see order-manual-tracking.js). That is
// bookkeeping — the sales channel is sent no tracking number at all — so
// it is shown as "no tracking number" rather than as a code somebody
// might try to type into a courier's website.
export const displayTracking = (tracking: string | null | undefined): string => {
    if (!tracking) return "—";
    return tracking.startsWith("MANUAL-") ? "—" : tracking;
};

// Parcel status chip. `status` is the BE's coarse label_status enum;
// anything unrecognised (APC add scan codes over time, and the enum may
// gain values) falls back to a neutral chip showing the raw value rather
// than rendering nothing.
const PARCEL_STATUS_CHIPS: Record<string, { cls: string; key: string }> = {
    created:          { cls: "is-created",   key: "parcel_status_created" },
    in_transit:       { cls: "is-transit",   key: "parcel_status_in_transit" },
    out_for_delivery: { cls: "is-outfordel", key: "parcel_status_out_for_delivery" },
    delivered:        { cls: "is-delivered", key: "parcel_status_delivered" },
    exception:        { cls: "is-exception", key: "parcel_status_exception" },
    returned:         { cls: "is-returned",  key: "parcel_status_returned" },
    voided:           { cls: "is-voided",    key: "parcel_status_voided" },
};

type Translate = (key: string, params?: Record<string, string | number>) => string;

// How long ago the carrier was last asked about a parcel, in words.
// Coarse on purpose: the poller ticks every 15 minutes, so minute-exact
// wording past the first hour would advertise a freshness we don't have.
const carrierCheckedAgo = (iso: string, t: Translate): string => {
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms)) return "";
    const mins = Math.max(0, Math.round(ms / 60_000));
    if (mins < 1)  return t("carrier_checked_just_now");
    if (mins < 60) return t("carrier_checked_minutes", { n: mins });
    const hours = Math.round(mins / 60);
    if (hours < 24) return t("carrier_checked_hours", { n: hours });
    return t("carrier_checked_days", { n: Math.round(hours / 24) });
};

// The parcel status chip's tooltip. Shared by the two places that render
// a chip — the Shipment table and the Shipped cards — which previously
// built the same expression twice.
//
// The checked-at clause is the reason this exists. `status` alone cannot
// tell "APC has nothing newer than LABEL CREATED" apart from "nobody has
// asked APC since the parcel was packed": both render an identical grey
// chip. Every parcel was silently in the second state until 2026-08-20
// because the poller's opt-in flag had never been set on any instance,
// and nothing on screen said so.
const parcelStatusTooltip = (p: Parcel, t: Translate): string | undefined => {
    const tip = [
        p.carrier_status_description,
        p.status_updated_at ? new Date(p.status_updated_at).toLocaleString() : null,
        p.carrier_status_checked_at
            ? t("carrier_status_checked", { ago: carrierCheckedAgo(p.carrier_status_checked_at, t) })
            : t("carrier_status_never_checked"),
    ].filter(Boolean).join(" — ");
    return tip || undefined;
};

// Newest poll across an order's parcels, or null when none has ever been
// polled. Compared as epoch ms rather than by string order so a mixed
// timestamp format can't silently pick the wrong one.
const lastCarrierCheck = (parcels: Parcel[]): string | null => {
    let best: string | null = null;
    for (const p of parcels) {
        if (!p.carrier_status_checked_at) continue;
        if (!best || Date.parse(p.carrier_status_checked_at) > Date.parse(best)) {
            best = p.carrier_status_checked_at;
        }
    }
    return best;
};

const formatMoney = (value: number, currency: string) => {
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value); }
    catch { return `${currency} ${value.toFixed(2)}`; }
};

// The list loads with ?items=summary (2026-10-28: Poland's open orders were
// 24.5 MB a load, 20 MB of it item rows) — those rows carry the line totals
// below instead of items until the detail fetch fills them in. Every
// helper reads the summary only while the items are omitted.
const lineSummary = (o: DispatchOrder) => (o.items_omitted ? o.items_summary : undefined);

// Units in the order (Σ quantity), not SKU line count — operators read
// "Items" as how many physical units the order contains.
const orderItemCount = (o: DispatchOrder) =>
    lineSummary(o)?.units ?? o.items.reduce((sum, i) => sum + (i.quantity || 0), 0);

// Gross order value — what the customer actually paid. The channel
// reports this as `total` (items incl. tax, plus shipping). Orders
// ingested without channel financials store total = 0, so reconstruct
// from the lines (unit prices are tax-inclusive) plus shipping rather
// than showing £0.00 for an order that clearly has paid items.
const orderValue = (o: DispatchOrder) => {
    const total = Number(o.total) || 0;
    if (total > 0) return total;
    const lineSum = lineSummary(o)?.value ?? o.items.reduce(
        (sum, i) => sum + (i.unit_price || 0) * (i.quantity || 1),
        0,
    );
    return lineSum + (o.shipping_paid || 0);
};

// Value refunded back to the customer. There is no monetary refund
// record — flagged-order resolution writes per-line order_items
// .refund_count (capped at the line quantity), so the refunded value
// derives from the line's tax-inclusive unit price. Subtracting it from
// the gross keeps items + shipping + tax − refund = total in the modal.
export const orderRefundTotal = (o: DispatchOrder): number => {
    const counted = lineSummary(o)?.refunded_value
        ?? o.items.reduce((sum, i) => sum + (i.unit_price || 0) * (i.refundCount ?? 0), 0);
    // A refund still pending (migration 238) is not "no per-line counts":
    // the order is 'refunded' because of it, and nothing has gone out yet.
    if (counted > 0 || o.status !== "refunded" || orderPendingRefundUnits(o) > 0) return counted;
    // Fully refunded order with no per-line counts (refunded before
    // refund_count existed, or channel-side): the whole item value came
    // back, so the display treats every line as refunded in full.
    return lineSummary(o)?.value ?? o.items.reduce((sum, i) => sum + (i.unit_price || 0) * (i.quantity || 1), 0);
};

// Pending Refund (2026-10-23, per Alex): units decided for refund at
// resolve that the refund manager has not issued yet. Exported for tests.
export const orderPendingRefundUnits = (o: DispatchOrder): number =>
    lineSummary(o)?.refund_pending_units ?? (o.items ?? []).reduce((sum, i) => sum + (i.refundPending ?? 0), 0);
export const orderPendingRefundTotal = (o: DispatchOrder): number =>
    lineSummary(o)?.refund_pending_value ?? o.items.reduce((sum, i) => sum + (i.unit_price || 0) * (i.refundPending ?? 0), 0);

// Replacement links for the detail modal's item list: SKU → the
// "Send replacement" orders that resend it. A replacement's lines are
// clones of the original's, so SKU is the shared identity (two original
// lines with the same SKU both link — the replacement covers the SKU,
// not one line). replacement_orders[].items is detail-only, so this is
// empty until the modal's detail fetch lands.
export const replacementLinksBySku = (o: DispatchOrder) => {
    const bySku = new Map<string, { order_id: string; order_number: string; quantity: number }[]>();
    for (const rep of o.replacement_orders ?? []) {
        for (const ri of rep.items ?? []) {
            const list = bySku.get(ri.sku) ?? [];
            list.push({ order_id: rep.order_id, order_number: rep.order_number, quantity: ri.quantity });
            bySku.set(ri.sku, list);
        }
    }
    return bySku;
};

// Units already shipped = Σ parcel-item quantities recorded at pack time.
const shippedUnits = (o: DispatchOrder) =>
    (o.parcels ?? []).reduce(
        (sum, p) => sum + p.items.reduce((s, i) => s + (i.quantity || 0), 0),
        0,
    );

// Fulfilled with units still outstanding. Status stays 'fulfilled' in the
// DB (packing flips it regardless of coverage), so partial fulfilment is
// derived from the parcel records. A fulfilled order with no per-parcel
// item breakdown is treated as complete — that's the legacy-data shape.
const isPartiallyFulfilled = (o: DispatchOrder) => {
    if (o.status !== "fulfilled") return false;
    const shipped = shippedUnits(o);
    return shipped > 0 && shipped < orderItemCount(o);
};

// Fully fulfilled = complete and shipped → the order is read-only.
// Partially fulfilled orders stay editable for the unshipped remainder.
const isOrderLocked = (o: DispatchOrder) =>
    o.status === "fulfilled" && !isPartiallyFulfilled(o);

// Today as YYYY-MM-DD for date inputs
const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
const todayStr = () => toDateStr(new Date());

// Get the user's default warehouse from session
const getUserWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (raw) return JSON.parse(raw).warehouse || "Newport";
    } catch { /* fallthrough */ }
    return "Newport";
};

type SpecialQueueId = 1 | 2 | 3;
type SpecialQueueNames = Record<SpecialQueueId, string>;
// Queue 4 = Pick to tote: fixed label (i18n key `pick_to_tote`), never
// warehouse-renamable — hence outside SpecialQueueNames. Queue 5 = Pallet
// Picks (`pallet_picks`), likewise fixed. Queue 6 = Bulk Pick (`bulk_pick`):
// a named special queue whose stock is taken from the fewest locations.
type PickQueueId = SpecialQueueId | 4 | 5 | 6;

const DEFAULT_SPECIAL_QUEUE_NAMES: SpecialQueueNames = {
    1: "Bodysocks",
    2: "Collections",
    3: "Misc",
};

// One-shot read of `?status=…&date=…` so the admin home KPI cards can
// deep-link to a pre-filtered orders view. Falls back to the page's
// own defaults (status=all, date=today) when the param is absent or
// unrecognised. SSR guard via `typeof window` keeps this safe in any
// non-browser context.
const readSearchParam = (key: string): string | null => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get(key);
};
// Pill order for the status filter row. The partial states are folded
// into their full counterparts to keep the row compact — the Fulfilled
// pill matches fulfilled + partially fulfilled (same DB status) and the
// Refunded pill matches refunded + partially_refunded. Row badges still
// distinguish the partial variants (see orderStatusLabel/Class).
const FILTER_STATUSES = [
    // On hold leads the row (per Alex, 2026-08-25): held orders are the
    // ones that need a human first.
    "on_hold",
    // Exchange (BE migration 263): a returns-portal exchange order waiting,
    // with its stock held, for the customer's return to be sent / delivered
    // / received; then it moves to Unfulfilled on its own (or "Release now"
    // on the Returns page). Next to On hold: both wait, neither is picked.
    "exchange",
    "unfulfilled",
    // Unfulfilled orders that fail the wave-candidacy stock gate
    // (BE-derived `stock_blocked`): they will never reach picking until
    // stock arrives or an admin releases them short. Their OWN bucket —
    // they are not counted under Unfulfilled (per Alex, 2026-08-25), so
    // the Unfulfilled pill is exactly "will pick when a wave comes".
    "stock_blocked",
    "picking",
    "picked",
    "sorting",
    "dispatch",
    "fulfilled",
    "flagged",
    // Refund decided, money not sent yet — the refund manager's queue
    // (2026-10-23, per Alex). Marking it issued moves it to Refunded.
    "pending_refund",
    "refunded",
    "cancelled",
    "consolidated",
] as const;
type StatusFilter = (typeof FILTER_STATUSES)[number];

// Pills kept out of the rendered row (per Alex, 2026-08-25 — the
// Consolidated view is rarely needed). They stay in FILTER_STATUSES so
// a `?status=consolidated` deep-link and matchesStatusFilter still work.
const HIDDEN_FILTER_PILLS: ReadonlySet<StatusFilter> = new Set<StatusFilter>(["consolidated"]);

// The "Unshipped" toggle: every state an order sits in before it ships
// (per Alex, 2026-08-25: unfulfilled incl. stock-blocked, picking, picked,
// sorting, dispatch, on hold; and exchange, which waits like on hold).
// Flagged / refunded / cancelled / fulfilled are deliberately outside it.
const UNSHIPPED_STATUSES: ReadonlySet<string> = new Set([
    "unfulfilled", "picking", "picked", "sorting", "dispatch", "on_hold", "exchange",
]);
// Waiting on something outside the warehouse: not due, not work yet.
const WAITING_STATUSES: ReadonlySet<string> = new Set(["on_hold", "exchange"]);
// No "Send replacement" for these (the BE refuses them too, order-replacement.js):
// cancelled / refunded were made good already; an Exchange order hasn't shipped.
const NOT_REPLACEABLE_STATUSES: ReadonlySet<string> = new Set(["cancelled", "refunded", "exchange"]);
export const isUnshippedOrder = (o: DispatchOrder): boolean => UNSHIPPED_STATUSES.has(o.status);

// The "Ready" tick column: an unfulfilled order the BE projected as
// pickable entirely from low-level storage (`low_ready`, computed by
// listOrders next to stock_blocked — see stockReadinessOrders). Only
// unfulfilled rows carry the flag; anything already waved, held or
// shipped shows no tick.
export const isLowReady = (o: DispatchOrder): boolean =>
    o.status === "unfulfilled" && o.low_ready === true;

// The "Needs to go" (clock) toggle: unshipped AND due to ship today or
// earlier under the dispatch cut-off rule (see helpers/dispatchDue.ts —
// mirrors the Team dashboard's due-date derivation). `created_at` on a
// list row is the channel placed time. A consolidation master carries
// the oldest child's date + the fastest child's service, which is not
// any real promise — the BE sends its real due day (`due_day`, the
// earliest of the children's own due days) and that wins when present.
// An order on hold is not due (per Alex, 2026-10-02): nobody can work
// it, and once released its due clock restarts at the release
// (`hold_released_at`). Neither is an Exchange order: it waits for the
// customer's return, and its move to Unfulfilled stamps the same clock.
export const needsToGoToday = (o: DispatchOrder, nowMs: number): boolean =>
    isUnshippedOrder(o) && !WAITING_STATUSES.has(o.status) && (
        o.due_day
            ? o.due_day <= londonDateOf(nowMs)
            : isDueByToday(
                dueClockStart(o.created_at, o.hold_released_at),
                isExpressDeliveryType(o.delivery_type),
                nowMs,
            )
    );

// "collection" = counter pick-up (never a courier) — its own segment so
// the counter can list what is waiting to be handed over.
type DeliveryFilter = "all" | "express" | "standard" | "collection";

// The returns portal's channel ('bargainfox-returns', migration 252): its
// replacement (REP) and exchange (EXC) orders keep the original order's
// sales_channel — picking, cut-offs and dispatch key on it — but show as
// "BargainFox Returns" here (Jack, 2026-10-30). Display only.
export const RETURNS_CHANNEL_KEY = "bargainfox-returns";

// Sales-channel facet key — the lower-cased slug the DB stores, so the
// dropdown value, the row's facet and CHANNEL_LABELS all agree. Orders
// without a channel key as "" and only ever appear under "All channels".
// An order the returns portal raised keys as RETURNS_CHANNEL_KEY (the
// list carries the raising integration's code, integration_channel_code).
export const channelOf = (o: { sales_channel?: string | null; integration_channel_code?: string | null }): string =>
    (o.integration_channel_code || "").trim().toLowerCase() === RETURNS_CHANNEL_KEY
        ? RETURNS_CHANNEL_KEY
        : (o.sales_channel || "").trim().toLowerCase();

// Options for the Channel dropdown: every channel present in the
// faceted results (all OTHER filters applied), A→Z by display label,
// each with the count it would show if selected. The currently
// selected channel stays listed even when its count has dropped to 0
// (another filter squeezed it out) so the select still shows what is
// applied and the user can clear it.
export const channelFacetOptions = (
    counts: Record<string, number>,
    selected: string,
    label: (c: string) => string = c => c,
): Array<{ key: string; count: number }> => {
    const keys = new Set(Object.keys(counts).filter(k => k !== ""));
    if (selected) keys.add(selected);
    return [...keys]
        .map(key => ({ key, count: counts[key] || 0 }))
        .sort((a, b) => label(a.key).localeCompare(label(b.key)) || a.key.localeCompare(b.key));
};

// Server-owned fields this hydrate is allowed to refresh from the
// detail response. All are read-only in the modal (they drive the
// status badge, the fulfilment stamps and which action buttons are
// offered), so patching them cannot clobber an in-flight edit — the
// editable fields all live in their own edit* state.
//
// `status` is the one that matters. The list is a one-shot fetch and
// deliberately does not auto-poll (see the refresh button), so a row
// keeps whatever status it had at load time. Before this, opening the
// order made a request whose response carried the live status and then
// discarded it: order 337572 was flagged at the pack desk at 14:22 and
// still read "Dispatch" in both the table and its own detail modal
// (2026-08-21). The fulfilment stamps ride along because they move in
// the same instant — a "Flagged" badge above an empty Packed by is a
// worse half-state than the stale one it replaces.
// BargainFox's answers to Issue Refund that mean the money already went
// back by hand in BargainFox admin (2026-10-31): the refund manager may then
// mark the refund issued without asking BargainFox again.
const ALREADY_REFUNDED_CODES = new Set(["NOTHING_LEFT_TO_REFUND", "EXCEEDS_REFUNDABLE"]);

const LIVE_FIELDS = [
    "status",
    "packed_at", "packed_by_uid", "packed_by_name",
    "resolved_at", "resolved_by_uid", "resolved_by_name",
    "on_hold_reason",
    "refund_pending_at", "refund_issued_at", "refund_issued_by_name", "refunds_via_channel",
] as const;

// Exported for tests.
export const pickLiveFields = (order: Partial<DispatchOrder> | undefined) => {
    const out: Partial<DispatchOrder> = {};
    if (!order) return out;
    for (const k of LIVE_FIELDS) {
        if (order[k] !== undefined) (out as Record<string, unknown>)[k] = order[k];
    }
    return out;
};

// Bucket an order under exactly one pill. Partially fulfilled orders are
// DB status 'fulfilled' so they fall under Fulfilled naturally; partial
// refunds collapse onto the Refunded pill.
//
// Consolidation: children never reach this page (the BE hides them —
// they live inside the master's detail modal), and the Consolidated
// pill is a cross-cutting view of MASTERS, matched separately in
// matchesStatusFilter — a master also buckets under its real status.
// An order settled by a resolve whose refund has not been issued yet
// buckets under Pending Refund, whatever its settled status (fulfilled
// after a shipped-short refund, partially_refunded, refunded). An order
// reopened for a re-pack keeps its live status. Exported for tests.
const REFUND_SETTLED: ReadonlySet<string> = new Set(["fulfilled", "partially_refunded", "refunded"]);
export const hasPendingRefund = (o: DispatchOrder): boolean =>
    REFUND_SETTLED.has(o.status)
    && (!!o.refund_pending_at || orderPendingRefundUnits(o) > 0);

const statusFilterKey = (o: DispatchOrder): StatusFilter =>
    hasPendingRefund(o) ? "pending_refund"
    : o.status === "partially_refunded" ? "refunded"
        : o.status === "unfulfilled" && o.stock_blocked === true ? "stock_blocked"
            : (o.status as StatusFilter);

const initialStatusFromQuery = (): StatusFilter | "all" => {
    const v = readSearchParam("status");
    if (v === "all" || v === null) return "all";
    // Legacy deep-link params from before the pills were combined —
    // land each on its parent pill instead of falling back to "all".
    if (v === "fulfilled_all" || v === "partially_fulfilled") return "fulfilled";
    if (v === "partially_refunded") return "refunded";
    return FILTER_STATUSES.includes(v as StatusFilter) ? (v as StatusFilter) : "all";
};

// Does an order fall under the current status filter? The Consolidated
// pill shows masters (whatever their live status) — children are hidden
// from the page entirely. Stock blocked is an ordinary bucket via
// statusFilterKey (a blocked order is NOT under Unfulfilled).
// Exported for tests.
// Pending Refund lists each channel order the refund manager pays (Jack,
// 2026-11-03: "we need to refund against the specific order not the
// consolidated one"). A consolidated PART with pending units of its own is
// listed under Pending Refund, and only there (everywhere else its master
// stands for it); a master whose pending units are its parts' buckets under
// its settled status instead, so the same money is never listed twice.
export const isPendingRefundPart = (o: DispatchOrder): boolean => !!o.consolidated_into && hasPendingRefund(o);
export const refundListKey = (o: DispatchOrder, mastersWithPendingParts: ReadonlySet<string>): StatusFilter =>
    o.is_consolidation_master === true && mastersWithPendingParts.has(o.order_id) && hasPendingRefund(o)
        ? (o.status === "partially_refunded" ? "refunded" : (o.status as StatusFilter))
        : statusFilterKey(o);

export const matchesStatusFilter = (o: DispatchOrder, f: StatusFilter | "all"): boolean =>
    f === "consolidated"
        ? o.is_consolidation_master === true
        : f === "all" || statusFilterKey(o) === f;

// matchesStatusFilter for a list row, by its Pending Refund-aware key (refundListKey).
const rowMatchesStatus = (r: { o: DispatchOrder; key: StatusFilter }, f: StatusFilter | "all"): boolean =>
    f === "consolidated"
        ? r.o.is_consolidation_master === true
        : f === "all" || r.key === f;

// One line of the release-short dry-run: how short the line is against
// current warehouse availability. Shared by the release-short confirm
// modal and the detail-view row highlighting.
export type ReleaseShortLine = {
    line_position: number;
    sku: string;
    title: string;
    need: number;
    available: number;
    shortfall: number;
};

// Key the dry-run's shortfall lines by line_position so the item list can
// annotate exactly the short rows — line_position rather than SKU because
// dup-SKU orders split one SKU across lines and availability is granted
// to the earlier lines, leaving only the later ones short. Exported for
// tests.
export const shortfallLineMap = (lines: ReleaseShortLine[]): Map<number, ReleaseShortLine> =>
    new Map(lines.map(l => [l.line_position, l]));

// A held order the release-short action also applies to: ingest parked it
// for a stock shortfall. Manual and unmapped holds are excluded — one is
// deliberate, the other is the mapping flow's job (and the BE refuses
// both). Exported for tests.
export const heldInsufficientStock = (o: DispatchOrder | null | undefined): boolean =>
    o?.status === "on_hold" && o?.on_hold_reason === "insufficient_stock";

type DatePreset = "all" | "today" | "yesterday" | "last_7" | "last_30" | "last_60" | "custom";
const DATE_PRESETS: DatePreset[] = ["all", "today", "yesterday", "last_7", "last_30"];

const daysAgo = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return toDateStr(d);
};

const presetRange = (p: DatePreset): { from: string; to: string } => {
    const today = todayStr();
    switch (p) {
        case "today": return { from: today, to: today };
        case "yesterday": { const y = daysAgo(1); return { from: y, to: y }; }
        case "last_7": return { from: daysAgo(6), to: today };
        case "last_30": return { from: daysAgo(29), to: today };
        case "last_60": return { from: daysAgo(59), to: today };
        default: return { from: "", to: "" };
    }
};

// The page keeps TWO date ranges (per Alex, 2026-10-03): one for open
// orders — blank by default, so every in-flight order shows whatever its
// date, future-dated included — and one for closed orders (fulfilled /
// refunded / cancelled), last 7 days by default because that history is
// the only part big enough to need a window. The date control shows and
// edits whichever range governs the selected pill.
export type DateRange = { preset: DatePreset; from: string; to: string };
export type OrderDates = { open: DateRange; closed: DateRange };

const BLANK_DATES: DateRange = { preset: "all", from: "", to: "" };
const defaultClosedDates = (): DateRange => ({ preset: "last_7", ...presetRange("last_7") });
const isBlankRange = (r: DateRange) => !r.from && !r.to;

const CLOSED_FILTERS: ReadonlySet<string> = new Set(["fulfilled", "refunded", "cancelled"]);

// Which range a pill's date control drives. All / Consolidated span both
// groups: they show the open range and a pick there applies to both.
// Exported for tests.
export const dateScopeOf = (f: StatusFilter | "all"): "open" | "closed" | "both" =>
    f === "all" || f === "consolidated" ? "both" : CLOSED_FILTERS.has(f) ? "closed" : "open";

// Apply a date pick made under `scope`. On a both-group pill a blank
// pick means "the default view" — every open order plus the default
// closed window — rather than pulling the whole fulfilled history; all
// dates for closed orders is still one click away on the Fulfilled pill.
// Exported for tests.
export const applyDateRange = (dates: OrderDates, scope: "open" | "closed" | "both", next: DateRange): OrderDates => {
    if (scope === "open") return { ...dates, open: next };
    if (scope === "closed") return { ...dates, closed: next };
    return { open: next, closed: isBlankRange(next) ? defaultClosedDates() : next };
};

// Which closed orders (fulfilled / refunded / cancelled) the list request
// asks the BE for (per Jack, 2026-10-20):
//   none   — the default. Every open order loads; no closed history at
//            all. The fulfilled history is the only part of the table
//            big enough to make the page slow, and most visits never
//            look at it, so it is not fetched until asked for.
//   window — a closed pill is selected: the closed date range applies
//            (last 7 days by default, the pill's date control edits it).
//   search — a search is committed: every closed order matching it,
//            whatever its date, via the BE's `q`. The operator typed an
//            order number and expects to find it; the local filter then
//            narrows the result exactly as before.
// Exported for tests.
export type ClosedOrdersScope = "none" | "window" | "search";
export const closedOrdersScope = (statusFilter: StatusFilter | "all", query: string): ClosedOrdersScope =>
    query.trim() !== "" ? "search" : CLOSED_FILTERS.has(statusFilter) ? "window" : "none";

const formatDate = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
};

// Compact date — no time, and no YEAR: the table is a working queue, so
// every row is within days of today and the year only ate width the wider
// columns needed.
export const formatDateShort = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
    } catch { return iso; }
};

// Free-text columns (Customer, Dispatched by, Notes) are clipped to a fixed
// character count so no single long value can stretch the table sideways.
// Callers put the untruncated value on the cell's `title` so hovering still
// reveals it in full.
const CELL_TEXT_MAX = 15;
export const truncateCell = (text: string) =>
    text.length > CELL_TEXT_MAX ? `${text.slice(0, CELL_TEXT_MAX)}...` : text;

// Time-of-day companion for the Order date column — rendered as a
// muted second line so the column keeps its compact width.
const formatTimeShort = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
};

// Sales-channel display names. The DB stores slugs ("bargainfox",
// "jobalots") so the table can match-and-filter on a stable key while
// users still see the branded label. Unknown channels pass through
// title-cased so a new channel is readable before it's mapped here.
const CHANNEL_LABELS: Record<string, string> = {
    bargainfox: "BargainFox",
    jobalots: "Jobalots",
};
// Labels with words in them come from i18n (en + pl).
const CHANNEL_LABEL_KEYS: Record<string, string> = {
    [RETURNS_CHANNEL_KEY]: "channel_bargainfox_returns",
};
export const channelLabel = (c?: string, t?: (key: string) => string) => {
    if (!c) return "—";
    const key = CHANNEL_LABEL_KEYS[c.toLowerCase()];
    if (key) return t ? t(key) : "BargainFox Returns";
    const mapped = CHANNEL_LABELS[c.toLowerCase()];
    if (mapped) return mapped;
    return c.charAt(0).toUpperCase() + c.slice(1);
};

// Sort columns. Order matters — used to populate the Sort By dropdown
// in the same order the columns appear in the table. Default sort is
// created_at desc (newest order first), the most useful starting view
// for an operator scanning the day's queue.
type SortKey =
    | "order_number" | "status" | "customer" | "channel"
    | "items" | "value" | "created_at" | "packed_at" | "packed_by"
    | "delivery_type" | "notes";
type SortDir = "asc" | "desc";

const SORT_OPTIONS: SortKey[] = [
    "order_number", "status", "customer", "channel",
    "items", "value", "created_at", "packed_at", "packed_by",
    "delivery_type", "notes",
];

// Map each sort key to the table-header i18n key so the dropdown labels
// match the column headers users see.
const SORT_LABEL_KEY: Record<SortKey, string> = {
    order_number:  "col_order_number",
    status:        "col_status",
    customer:      "col_customer",
    channel:       "col_channel",
    items:         "col_items",
    value:         "col_value",
    created_at:    "col_order_date",
    packed_at:     "col_fulfilled_date",
    packed_by:     "col_dispatched_by",
    delivery_type: "col_delivery_type",
    notes:         "col_notes",
};

// Resolve the comparable value for a given sort key. Strings are
// lower-cased to make sort case-insensitive; numerics return numbers;
// missing values return null and are pushed to the bottom by compareSort.
// Exported (with compareSort) so the column ordering rules can be tested
// without mounting the whole page.
export const sortValue = (o: DispatchOrder, key: SortKey): string | number | null => {
    switch (key) {
        case "order_number": return (o.order_number || "").toLowerCase();
        case "status":       return o.status || "";
        case "customer":     return (o.customer?.name || "").toLowerCase();
        case "channel":      return channelOf(o);
        case "items":        return orderItemCount(o);
        case "value":        return orderValue(o);
        case "created_at":    return new Date(o.created_at).getTime();
        // packed_at is when the order was fulfilled — null on anything not
        // yet packed, which compareSort parks at the bottom either way so
        // the newest/oldest fulfilled orders always lead the list.
        case "packed_at":     return o.packed_at ? new Date(o.packed_at).getTime() : null;
        // Keyed off packed_at, not the name, so the column sorts and reads
        // identically to Fulfilled date: an order whose fulfilment was
        // reversed keeps packed_by_name in the DB but has no packed_at, and
        // must not still claim a packer.
        case "packed_by":     return o.packed_at ? (o.packed_by_name || "").toLowerCase() : null;
        case "delivery_type": return (o.delivery_type || "").toString();
        case "notes": {
            const n = o.latest_flag?.flagged_reason || o.notes || "";
            return n.toLowerCase();
        }
    }
};

// Page-size options users can pick from. 25 is the default — small enough
// to scan, big enough that the average day's queue fits without paging.
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;
const DEFAULT_PAGE_SIZE = 25;

// Statuses an admin can set via the bulk-status dropdown. The other
// statuses are system-managed: `sorting` / `dispatch` are flipped by the
// picking + dispatch flows, `flagged` needs a reason from the packer,
// and the refund statuses are owned by the refund endpoints. Manually
// bulk-setting any of these would desync the workflow, so we hide them
// from the dropdown.
// `fulfilled` IS offered: orders that shipped outside the pack desk
// (hand-carried, collected, courier booked off-system) otherwise sit
// open forever. It behaves like a desk fulfilment — reservation
// consumed, fulfilled date stamped, sales channel notified on that
// warehouse's Settings > Dispatch release schedule — except that
// nobody recorded a label, so the carrier, tracking code and link are
// whatever the operator types into the confirm modal, if anything (see
// manual-fulfil-modal.tsx). Left blank, the channel is told the order
// shipped and nothing more; a channel that cannot accept a
// tracking-less notification (Amazon) comes back as a per-row error in
// the result panel, the fulfil stands, and somebody closes that order
// off on the channel by hand.
const BULK_STATUS_TARGETS: OrderStatus[] = ["unfulfilled", "fulfilled", "cancelled", "on_hold"];

// Comparator that respects direction and parks null values (missing TTS,
// missing notes, etc.) at the bottom regardless of asc/desc.
export const compareSort = (a: DispatchOrder, b: DispatchOrder, key: SortKey, dir: SortDir): number => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb));
    return dir === "asc" ? cmp : -cmp;
};

// Sortable column header. Renders a button-styled cell with the active
// sort direction's arrow next to the label; clicking toggles direction
// when the column is already active, otherwise switches to this column.
const SortableTh = ({
    sk, sortKey, sortDir, onSort, className, children,
}: {
    sk: SortKey;
    sortKey: SortKey;
    sortDir: SortDir;
    onSort: (k: SortKey) => void;
    className?: string;
    children: React.ReactNode;
}) => {
    const active = sortKey === sk;
    return (
        <th className={`dispatch-orders-th-sort${active ? " dispatch-orders-th-sort--active" : ""}${className ? " " + className : ""}`}>
            <button type="button" onClick={() => onSort(sk)} className="dispatch-orders-th-sort-btn">
                <span>{children}</span>
                <span className="dispatch-orders-th-sort-arrow" aria-hidden="true">
                    {active ? (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null}
                </span>
            </button>
        </th>
    );
};

const AdminOrders = () => {
    const { t } = useTranslation();
    // `?search=` URL param lets AdminSearch deep-link in pre-filtered.
    // useUrlSearchParam reacts to URL changes too, so a soft-nav to
    // ?search=NEW updates the field even if we're already on the page.
    // `query` is the applied filter (also kept in the URL via useUrlSearchParam
    // so AdminSearch deep-links land on a pre-filtered view). `queryDraft` is
    // what the operator is typing — committed to `query` only on Enter so the
    // result list doesn't churn keystroke-by-keystroke.
    const readOnly = useReadOnly();
    const [query, setQuery] = useUrlSearchParam("search");
    const [queryDraft, setQueryDraft] = useState(query);
    // Re-sync the input when the URL drives `query` (e.g. AdminSearch
    // soft-navigates here with ?search=…). User typing doesn't push back, so
    // this only fires on external changes.
    useEffect(() => { setQueryDraft(query); }, [query]);
    const [statusFilter, setStatusFilter] = useState<StatusFilter | "all">(initialStatusFromQuery);
    const [warehouse, setWarehouse] = useState(getUserWarehouse);
    // D15: without cross_warehouse the filter is pinned to the caller's own warehouse.
    const warehouseScope = useWarehouseScope();
    useEffect(() => {
        if (!warehouseScope.canCross && warehouseScope.own && warehouse !== warehouseScope.own) setWarehouse(warehouseScope.own);
    }, [warehouseScope.canCross, warehouseScope.own, warehouse]);
    // Default landing view: every open order and no closed history (see
    // closedOrdersScope — closed orders load on a closed pill or a
    // search). The closed range still defaults to the last 7 days so the
    // Fulfilled / Refunded / Cancelled pills open on a bounded window. A
    // `date` query param is a pick made on the deep-linked `status` pill,
    // so it lands on the range that pill drives —
    // `?status=fulfilled&date=today` windows closed orders,
    // `?status=unfulfilled&date=all` leaves the open backlog unbounded.
    // A `?search=` deep-link needs no special date handling any more: a
    // committed search fetches matching closed orders of every date.
    const [dates, setDates] = useState<OrderDates>(() => {
        const defaults: OrderDates = { open: BLANK_DATES, closed: defaultClosedDates() };
        const raw = readSearchParam("date");
        const presets: DatePreset[] = ["all", "today", "yesterday", "last_7", "last_30", "last_60"];
        if (raw && (presets as string[]).includes(raw)) {
            const picked = { preset: raw as DatePreset, ...presetRange(raw as DatePreset) };
            return applyDateRange(defaults, dateScopeOf(initialStatusFromQuery()), picked);
        }
        return defaults;
    });
    const dateScope = dateScopeOf(statusFilter);
    const shownDate = dateScope === "closed" ? dates.closed : dates.open;
    const setShownDate = (next: DateRange) => setDates(d => applyDateRange(d, dateScope, next));
    const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>("all");
    // Sales-channel filter — "" = all channels. Options come from the
    // faceted results, so only channels actually present are offered.
    const [channelFilter, setChannelFilter] = useState("");
    // Shipping-state toggles. Both are independent of each other and of
    // the delivery toggle, so any combination can be active at once.
    const [unshippedOnly, setUnshippedOnly] = useState(false);
    const [dueTodayOnly, setDueTodayOnly] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<DispatchOrder | null>(null);
    // SKU mapping modal — opened from an unmapped line in the dispatch detail view.
    const [mappingItem, setMappingItem] = useState<DispatchOrderItem | null>(null);
    // Manual order creation (New order button) and the Add-product
    // picker for the open order's lines.
    const [createOpen, setCreateOpen] = useState(false);
    const [addingProduct, setAddingProduct] = useState(false);
    const [lineEditBusy, setLineEditBusy] = useState(false);
    const [orders, setOrders] = useState<DispatchOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [sortKey, setSortKey] = useState<SortKey>("created_at");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    // Pagination — page size is configurable via the per-page dropdown in
    // the table footer. Page resets to 0 whenever a filter changes (see the
    // useEffect below the filter state).
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [page, setPage] = useState(0);

    // CSV bulk upload state.
    const [uploading, setUploading] = useState(false);
    const [lastResult, setLastResult] = useState<BulkResult | null>(null);
    // Bulk-actions popover (Download Template / Download Current / Upload).
    // Tucked behind a single toolbar icon so the toolbar stays compact.
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
    const actionsMenuRef = useRef<HTMLDivElement>(null);
    // Custom-date popover — shown when the user picks "Custom" from the
    // date dropdown so the From/To inputs aren't permanently parked on the
    // toolbar. Anchored off the date wrapper, dismisses on outside click.
    const [customDateOpen, setCustomDateOpen] = useState(false);
    const customDateRef = useRef<HTMLDivElement>(null);
    // Close the popover on outside-click or Escape — mirrors the
    // picking-custom-popover pattern so it feels native to the rest of Portal.
    useEffect(() => {
        if (!actionsMenuOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!actionsMenuRef.current?.contains(e.target as Node)) {
                setActionsMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setActionsMenuOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [actionsMenuOpen]);

    useEffect(() => {
        if (!customDateOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!customDateRef.current?.contains(e.target as Node)) {
                setCustomDateOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setCustomDateOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [customDateOpen]);
    // Tracks which status was applied when the last result came from the
    // bulk-status-change dropdown. Null when the last result came from a
    // CSV upload (CSVs do mixed adds/updates/removes; the count breakdown
    // is the right summary). Drives the toast + result-panel wording.
    const [lastResultStatus, setLastResultStatus] = useState<OrderStatus | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Bulk shipping-label upload: the picked files open the review modal.
    const labelFilesInputRef = useRef<HTMLInputElement>(null);
    const fulfilFileInputRef = useRef<HTMLInputElement>(null);
    const [bulkLabelFiles, setBulkLabelFiles] = useState<File[] | null>(null);
    // Bulk fulfilment sheet (⋯ menu → Upload fulfilments): one CSV/TSV of
    // order number, courier, tracking; resolved and applied in its modal.
    const [bulkFulfilFile, setBulkFulfilFile] = useState<File | null>(null);

    // Row-selection state for the bulk-status-change dropdown. The "select
    // all" checkbox toggles only the rows on the current page (per the
    // user's "visible rows only" requirement); selections reset whenever
    // the visible window itself changes — filter/page/page-size — so a
    // stale selection can't surprise the user later.
    const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(() => new Set());
    const [bulkStatus, setBulkStatus] = useState<OrderStatus | "">("");
    const [bulkApplying, setBulkApplying] = useState(false);
    // Orders awaiting the fulfil confirmation. Non-null while the modal
    // is open; the fulfil itself doesn't start until it comes back.
    const [fulfilConfirm, setFulfilConfirm] = useState<ManualFulfilTarget[] | null>(null);

    // Header click: same column toggles direction; new column resets to
    // desc, which is the natural starting direction for dates / numerics
    // and isn't worse than asc for strings (operators can flip if needed).
    const onHeaderSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("desc");
        }
    };

    const applyPreset = (p: DatePreset) => {
        setShownDate(p === "custom" ? { ...shownDate, preset: "custom" } : { preset: p, ...presetRange(p) });
    };

    // Edit-modal state — loaded when a card is clicked. Two address blocks
    // (delivery + billing); the modal exposes a tab toggle to flip between
    // them. Both go in the PATCH body together when the user saves.
    const [editName, setEditName] = useState("");
    const [editCompany, setEditCompany] = useState("");
    const [editLine1, setEditLine1] = useState("");
    const [editLine2, setEditLine2] = useState("");
    const [editCity, setEditCity] = useState("");
    const [editCounty, setEditCounty] = useState("");
    const [editPostcode, setEditPostcode] = useState("");
    const [editCountry, setEditCountry] = useState("");
    const [editPhoneCountry, setEditPhoneCountry] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editBillingName, setEditBillingName] = useState("");
    const [editBillingCompany, setEditBillingCompany] = useState("");
    const [editBillingLine1, setEditBillingLine1] = useState("");
    const [editBillingLine2, setEditBillingLine2] = useState("");
    const [editBillingCity, setEditBillingCity] = useState("");
    const [editBillingCounty, setEditBillingCounty] = useState("");
    const [editBillingPostcode, setEditBillingPostcode] = useState("");
    const [editBillingCountry, setEditBillingCountry] = useState("");
    const [editBillingPhoneCountry, setEditBillingPhoneCountry] = useState("");
    const [editBillingPhone, setEditBillingPhone] = useState("");
    const [editBillingEmail, setEditBillingEmail] = useState("");
    const [editDeliveryType, setEditDeliveryType] = useState<"" | "standard" | "priority" | "express" | "collection">("");
    const [editPickQueue, setEditPickQueue] = useState<PickQueueId | null>(null);
    const [activeAddressTab, setActiveAddressTab] = useState<"delivery" | "billing">("delivery");
    // Top-level modal tabs — Details (current address/items view) vs Logs
    // (per-order audit trail). Resets to "details" whenever the user opens
    // a different order so we don't strand them on the Logs tab.
    const [activeDetailTab, setActiveDetailTab] = useState<"details" | "logs" | "attachments" | "claims">("details");
    // The order a replacement draft is open for, or null. Held separately
    // from selectedOrder so the detail modal stays mounted underneath and
    // the operator lands back on it when they cancel.
    const [replacementFor, setReplacementFor] = useState<DispatchOrder | null>(null);
    // Set alongside replacementFor when the draft was opened from ONE
    // shipped parcel ("this parcel never arrived") rather than from the
    // header. Narrows the draft to that parcel's contents. Null = the
    // whole-order replacement.
    const [replacementParcel, setReplacementParcel] = useState<ReplacementParcelScope | null>(null);
    // Send extra box: the shipped order the dialog is open for, and the
    // extra box being voided (tracking number) while its request runs.
    const [extraBoxFor, setExtraBoxFor] = useState<DispatchOrder | null>(null);
    const [voidingExtraBox, setVoidingExtraBox] = useState<string | null>(null);
    const [voidExtraBoxPrompt, setVoidExtraBoxPrompt] = useState<string | null>(null);
    // Tracking-number of the parcel whose item breakdown is currently
    // expanded inline in the shipment table. Null = nothing expanded.
    // Reset whenever a new order is opened so we don't carry state from
    // the previous detail view.
    const [expandedParcel, setExpandedParcel] = useState<string | null>(null);
    // Reprint / download shipped parcels' labels (Shipment table). Owns the
    // in-flight state and the printer-mode detection for the modal.
    // Collection parcels have no stored label: the desk built it in the
    // browser. The modal renders the same <CollectionLabel> per parcel into
    // an off-screen area (keyed by tracking number) and the reprint reads
    // that element's HTML after a frame so react-barcode has drawn.
    const collectionPrintRef = useRef<HTMLDivElement>(null);
    const parcelLabels = useParcelLabels(selectedOrder?.order_id, selectedOrder?.parcels, {
        collectionLabelHtml: async (p) => {
            await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
            const root = collectionPrintRef.current;
            if (!root) return null;
            const el = Array.from(root.children).find(
                c => (c as HTMLElement).dataset.tracking === p.tracking_number,
            );
            return el ? buildPrintDocument(el.innerHTML) : null;
        },
    });
    const [cancellingLabel, setCancellingLabel] = useState(false);
    // "Why are you cancelling this label?" — the answer decides whether
    // the order dies with the label or goes back to dispatch for re-ship.
    const [cancelLabelPrompt, setCancelLabelPrompt] = useState(false);
    // Release-short confirm: the BE dry-run's per-line shortfall, shown so
    // the admin sees exactly what gets marked missing before confirming.
    const [releasePrompt, setReleasePrompt] = useState<{ lines: ReleaseShortLine[] } | null>(null);
    const [releasing, setReleasing] = useState(false);
    // Remove-line confirm: the line the admin clicked Remove on. Deleting
    // a SKU from an order is one click with no undo, so it stops for a
    // prompt that names the SKU and the quantity that will go.
    const [removePrompt, setRemovePrompt] = useState<DispatchOrderItem | null>(null);
    // Remove-from-shipment confirm: pulling an order out of a consolidated
    // shipment changes what ships (and can cancel an emptied master), so
    // it stops for a prompt naming the order and the shipment. Opened from
    // either side of the link — the master's chip list or the child's bar.
    const [detachPrompt, setDetachPrompt] = useState<{
        masterId: string; masterNumber: string; childId: string; childNumber: string; last: boolean;
    } | null>(null);
    const [detachBusy, setDetachBusy] = useState(false);
    // Reduce-quantity confirm: lowering a line's quantity is a partial
    // removal, and the qty box saves on blur, so a decrease stops for the
    // same prompt (SKU, old → new, units going). Increases are additive
    // and stock-gated by the BE, so they still apply straight away.
    const [qtyPrompt, setQtyPrompt] = useState<{ item: DispatchOrderItem; quantity: number } | null>(null);
    // Bumped when the reduce prompt is dismissed without applying, so the
    // (uncontrolled) qty inputs remount and snap back to the real quantity
    // instead of showing the number the admin typed but did not confirm.
    const [qtyResetTick, setQtyResetTick] = useState(0);
    // line_position → shortfall for the OPEN detail view of a stock-blocked
    // order, so the item list highlights which SKUs are short and by how
    // many. Loaded via the release-short dry-run (apply:false mutates
    // nothing). Null while loading / not blocked / fetch failed — the rows
    // then just aren't annotated; the banner still explains the block.
    const [shortLines, setShortLines] = useState<Map<number, ReleaseShortLine> | null>(null);

    // All warehouses grouped by country, fetched once from /settings. Drives
    // the warehouse dropdown so admins can filter to a single warehouse, an
    // entire country, or all warehouses regardless of who they are. Falls
    // back to an empty list if /settings is unreachable — the dropdown then
    // collapses to "All warehouses" only.
    const [warehousesByCountry, setWarehousesByCountry] = useState<{ country: string; warehouses: string[] }[]>([]);
    const [queueNamesByWarehouse, setQueueNamesByWarehouse] = useState<Record<string, SpecialQueueNames>>({});
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const rows: {
                    warehouse: string;
                    country: string | null;
                    picking_queue_1_name?: string;
                    picking_queue_2_name?: string;
                    picking_queue_3_name?: string;
                }[] = await res.json();
                const map = new Map<string, string[]>();
                const queueNameMap: Record<string, SpecialQueueNames> = {};
                for (const row of rows) {
                    const country = row.country || "—";
                    if (!map.has(country)) map.set(country, []);
                    map.get(country)!.push(row.warehouse);
                    const names = [
                        row.picking_queue_1_name?.trim(),
                        row.picking_queue_2_name?.trim(),
                        row.picking_queue_3_name?.trim(),
                    ];
                    if (names.every(Boolean)) {
                        queueNameMap[row.warehouse] = {
                            1: names[0] as string,
                            2: names[1] as string,
                            3: names[2] as string,
                        };
                    }
                }
                const groups = Array.from(map.entries())
                    .map(([country, ws]) => ({ country, warehouses: [...ws].sort() }))
                    .sort((a, b) => a.country.localeCompare(b.country));
                setWarehousesByCountry(groups);
                setQueueNamesByWarehouse(queueNameMap);
            } catch { /* leave empty */ }
        })();
    }, []);

    const loadQueueNamesForOrder = async (orderWarehouse: string) => {
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(orderWarehouse)}`, {});
            if (!res.ok) return;
            const settings: {
                picking_queue_1_name?: string;
                picking_queue_2_name?: string;
                picking_queue_3_name?: string;
            } = await res.json();
            const names: SpecialQueueNames = {
                1: settings.picking_queue_1_name?.trim() || DEFAULT_SPECIAL_QUEUE_NAMES[1],
                2: settings.picking_queue_2_name?.trim() || DEFAULT_SPECIAL_QUEUE_NAMES[2],
                3: settings.picking_queue_3_name?.trim() || DEFAULT_SPECIAL_QUEUE_NAMES[3],
            };
            setQueueNamesByWarehouse(current => ({ ...current, [orderWarehouse]: names }));
        } catch {
            // Defaults keep the field usable if settings are temporarily unavailable.
        }
    };

    // Translate the dropdown value (one of "" / "country:UK" / "Newport")
    // into the comma-separated `warehouse` param the BE expects. The BE's
    // listOrders accepts a single name OR a list; expanding the country
    // here keeps the BE simple and avoids a JOIN against settings.
    const warehouseParam = useMemo(() => {
        if (!warehouse) return "";
        if (warehouse.startsWith("country:")) {
            const country = warehouse.slice("country:".length);
            const grp = warehousesByCountry.find(g => g.country === country);
            return grp ? grp.warehouses.join(",") : "";
        }
        return warehouse;
    }, [warehouse, warehousesByCountry]);

    // Fetch orders from /orders. Date + warehouse filters happen server-side
    // so we don't pull the whole table; status pills and the other facets
    // are applied client-side. Every open order loads whatever its date;
    // the closed history only when asked for (closedOrdersScope): none by
    // default, the closed window on a closed pill, or — once a search is
    // committed — every closed order matching the BE's `q` (order number /
    // customer / postcode / SKU, any date). The local matcher still runs on
    // top of whatever came back, so the pills and counts read as before.
    const closedScope = closedOrdersScope(statusFilter, query);
    // The last list request's params (without items=summary).
    const listParamsRef = useRef<string>("");
    const searchTerm = closedScope === "search" ? query.trim() : "";
    const closedFrom = closedScope === "window" ? dates.closed.from : "";
    const closedTo = closedScope === "window" ? dates.closed.to : "";
    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const params = new URLSearchParams();
                if (warehouseParam) params.set("warehouse", warehouseParam);
                if (dates.open.from)   params.set("open_from", dates.open.from);
                if (dates.open.to)     params.set("open_to", dates.open.to);
                if (closedScope === "none") params.set("closed", "none");
                else if (closedScope === "search") params.set("q", searchTerm);
                else {
                    if (closedFrom) params.set("closed_from", closedFrom);
                    if (closedTo)   params.set("closed_to", closedTo);
                }
                // The export re-asks for these orders with full item rows.
                listParamsRef.current = params.toString();
                // Line totals instead of item rows (see lineSummary) —
                // except for a search, whose local matcher reads every
                // line's SKU / barcodes and whose result is small.
                if (closedScope !== "search") params.set("items", "summary");
                const res = await apiFetch(`/orders?${params}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive) setOrders(Array.isArray(data.orders) ? data.orders : []);
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "search-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseParam, dates.open.from, dates.open.to, closedScope, closedFrom, closedTo, searchTerm, refreshKey]);

    // ── Faceted filter model ──────────────────────────────────────────
    // One pass over the date+warehouse-filtered set computes, per row,
    // every predicate the toolbar can apply. `filtered` and the counts
    // shown on each control both derive from it, so a control's number
    // is always "what you'd see by clicking it": every OTHER active
    // filter (status pill, delivery toggle, Unshipped / Needs-to-go
    // toggles, free-text search) is applied, but not the control itself
    // (per Alex, 2026-08-25 — counts follow the filters).
    //
    // Free-text search: consolidation children never appear in the
    // browsable lists (the master is the order) — but they surface via
    // search so a customer's real order number still finds the child
    // (which links to its master in the modal). Express bundles
    // "express" and "priority" since both are expedited from a
    // fulfilment standpoint — mirrors the picking page.
    const rowFacets = useMemo(() => {
        const q = query.trim().toLowerCase();
        // Postcodes are compared with spaces stripped so "cf101aa" finds
        // "CF10 1AA" and vice versa, whichever way it was typed or stored.
        const qPostcode = q.replace(/\s+/g, "");
        const postcodeMatches = (pc?: string) =>
            !!qPostcode && !!pc && pc.replace(/\s+/g, "").toLowerCase().includes(qPostcode);
        const nowMs = Date.now();
        const matchesQuery = (order: DispatchOrder): boolean => {
            if (order.order_number?.toLowerCase().includes(q)) return true;
            if (order.customer?.name?.toLowerCase().includes(q)) return true;
            if (postcodeMatches(order.delivery_address?.postcode)
                || postcodeMatches(order.billing_address?.postcode)) return true;
            return order.items.some(i => i.sku?.toLowerCase().includes(q)
                || i.barcode?.toLowerCase().includes(q)
                // Scanned barcodes are often an alternate (FNSKU / case-pack
                // EAN), not the product's primary — match the full set.
                || i.barcodes?.some(b => b.toLowerCase().includes(q)));
        };
        const mastersWithPendingParts = new Set(orders.filter(isPendingRefundPart).map(o => o.consolidated_into as string));
        return orders.map(o => ({
            o,
            // Passes the search + child gate (everything else is a facet).
            base: (!o.consolidated_into || q !== "") && (q === "" || matchesQuery(o)),
            // A consolidated part on Pending Refund: listed under that pill even without a search.
            refundPart: isPendingRefundPart(o) && (q === "" || matchesQuery(o)),
            key: refundListKey(o, mastersWithPendingParts),
            express: isExpressDeliveryType(o.delivery_type),
            standard: (o.delivery_type || "").toLowerCase() === "standard",
            collection: (o.delivery_type || "").toLowerCase() === "collection",
            unshipped: isUnshippedOrder(o),
            due: needsToGoToday(o, nowMs),
            channel: channelOf(o),
        }));
    }, [orders, query]);

    const facetCounts = useMemo(() => {
        const status: Record<string, number> = {};
        const channels: Record<string, number> = {};
        const bump = (k: string) => { status[k] = (status[k] || 0) + 1; };
        let all = 0, express = 0, standard = 0, collection = 0, unshipped = 0, due = 0;
        for (const r of rowFacets) {
            if (!r.base && !r.refundPart) continue;
            // In the list under the current pill: a part on Pending Refund only under that pill.
            const listed = r.base || statusFilter === "pending_refund";
            const st = listed && rowMatchesStatus(r, statusFilter);
            const dl = deliveryFilter === "all" || (deliveryFilter === "express" ? r.express : deliveryFilter === "collection" ? r.collection : r.standard);
            const un = !unshippedOnly || r.unshipped;
            const du = !dueTodayOnly || r.due;
            const ch = channelFilter === "" || r.channel === channelFilter;
            // Channel dropdown: every other filter applied, not the
            // channel itself — so each option's number is what the list
            // would show if picked, and the option list is exactly the
            // channels present in the current results.
            if (st && dl && un && du) channels[r.channel] = (channels[r.channel] || 0) + 1;
            if (!ch) continue;
            // Status pills: everything but the pill itself. Children are
            // search-only and never count toward a pill (their units are
            // already represented by their master), except a part on
            // Pending Refund, which counts there instead of its master;
            // they do count under All, which is exactly the list that pill
            // shows.
            if (dl && un && du) {
                if (r.base) all++;
                if (!r.o.consolidated_into) {
                    bump(r.key);
                    // Masters also count under the cross-cutting
                    // Consolidated pill (they bucket under their live
                    // status above).
                    if (r.o.is_consolidation_master) bump("consolidated");
                } else if (r.refundPart) {
                    bump("pending_refund");
                }
            }
            if (st && un && du) {
                if (r.express) express++;
                if (r.standard) standard++;
                if (r.collection) collection++;
            }
            if (st && dl && du && r.unshipped) unshipped++;
            if (st && dl && un && r.due) due++;
        }
        return { all, status, express, standard, collection, unshipped, due, channels };
    }, [rowFacets, statusFilter, deliveryFilter, unshippedOnly, dueTodayOnly, channelFilter]);

    const channelOptions = useMemo(
        () => channelFacetOptions(facetCounts.channels, channelFilter, (c) => channelLabel(c, t)),
        [facetCounts.channels, channelFilter, t],
    );
    // "All channels" = the list with no channel applied (every other
    // filter still on), including orders that carry no channel at all.
    const channelTotal = useMemo(
        () => Object.values(facetCounts.channels).reduce((a, b) => a + b, 0),
        [facetCounts.channels],
    );

    // Filtered list (every facet applied) before sorting.
    const filtered = useMemo(() => rowFacets
        .filter(r => (r.base || (r.refundPart && statusFilter === "pending_refund"))
            && rowMatchesStatus(r, statusFilter)
            && (deliveryFilter === "all" || (deliveryFilter === "express" ? r.express : deliveryFilter === "collection" ? r.collection : r.standard))
            && (!unshippedOnly || r.unshipped)
            && (!dueTodayOnly || r.due)
            && (channelFilter === "" || r.channel === channelFilter))
        .map(r => r.o),
    [rowFacets, statusFilter, deliveryFilter, unshippedOnly, dueTodayOnly, channelFilter]);

    // Sorted view of the filtered list. New array each pass so React
    // sees the change; sort is stable enough for our purposes (users
    // see consistent order between renders).
    const results = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => compareSort(a, b, sortKey, sortDir));
        return arr;
    }, [filtered, sortKey, sortDir]);

    // Reset to the first page whenever the visible list could shrink — any
    // change to filters or page size could push the current page past the
    // end of the new result set.
    useEffect(() => {
        setPage(0);
    }, [query, statusFilter, deliveryFilter, unshippedOnly, dueTodayOnly, channelFilter, warehouse, dates, pageSize]);

    // Drop the row selection whenever the visible window changes so an
    // off-screen selection can't sneak into the next bulk action. A manual
    // refresh counts too — the underlying rows may have changed server-side.
    useEffect(() => {
        setSelectedOrderIds(new Set());
    }, [query, statusFilter, deliveryFilter, unshippedOnly, dueTodayOnly, warehouse, dates, page, pageSize, refreshKey]);

    const totalPages = Math.max(1, Math.ceil(results.length / pageSize));
    const safePage = Math.min(page, totalPages - 1);
    const rangeFrom = results.length === 0 ? 0 : safePage * pageSize + 1;
    const rangeTo   = Math.min((safePage + 1) * pageSize, results.length);
    const pagedResults = useMemo(
        () => results.slice(safePage * pageSize, safePage * pageSize + pageSize),
        [results, safePage, pageSize],
    );

    // Selection helpers — "select all" toggles only the rows on the current
    // page so a single click can't accidentally pull in thousands of
    // off-screen orders.
    const allVisibleSelected = pagedResults.length > 0
        && pagedResults.every(o => selectedOrderIds.has(o.order_id));
    const toggleSelectAllVisible = () => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                for (const o of pagedResults) next.delete(o.order_id);
            } else {
                for (const o of pagedResults) next.add(o.order_id);
            }
            return next;
        });
    };
    const toggleSelectOne = (orderId: string) => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    // "{n} released orders merged into CON1088" — one line naming every
    // distinct shipment the batch's hold releases were merged into.
    const releaseConsolidatedText = (rows: NonNullable<BulkResult["consolidated"]>) => {
        const masters = Array.from(new Set(rows.map(r => r.consolidated_number ?? r.consolidated_into)));
        return t("bulk_release_consolidated", { n: String(rows.length), masters: masters.join(", ") });
    };

    // Bulk status change — POSTs one update row per selected order_id to
    // /orders/bulk. The BE refuses status changes on `dispatch` and
    // `consolidated` orders (mid-pack desk lock / master-managed) so those
    // come back as row errors and the result panel surfaces them.
    //
    // `tracking` is the manual-fulfil modal's optional per-order entry
    // (carrier / code / link), keyed by order_id. It rides along on the
    // same rows: the BE records it as the order's shipment and forwards
    // it to the sales channel with the fulfilment notification.
    const applyBulkStatus = async (
        target: OrderStatus,
        tracking?: Map<string, ManualTrackingEntry> | null,
    ) => {
        if (selectedOrderIds.size === 0) return;
        setBulkApplying(true);
        try {
            const rows = Array.from(selectedOrderIds).map(orderId => {
                const tr = tracking?.get(orderId);
                return {
                    action: "update",
                    order_id: orderId,
                    status: target,
                    // Omitted entirely when the operator typed nothing —
                    // an empty cell is "no change" to the BE either way,
                    // but sending only what exists keeps the row honest.
                    ...(tr?.carrier        ? { tracking_carrier: tr.carrier } : {}),
                    ...(tr?.tracking_number ? { tracking_number: tr.tracking_number } : {}),
                    ...(tr?.tracking_url   ? { tracking_url: tr.tracking_url } : {}),
                };
            });
            const res = await apiFetch(`/orders/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows }),
            });
            // 413 = body exceeds the BE's JSON limit (an admin selected
            // tens of thousands of orders for a status change in one go).
            // Surface as a size issue rather than the generic server toast.
            if (res.status === 413) {
                toast.error(t("bulk_upload_too_large"), { toastId: "orders-bulk-413" });
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult = await res.json();
            setLastResult(result);
            setLastResultStatus(target);
            setRefreshKey(k => k + 1);
            setSelectedOrderIds(new Set());
            setBulkStatus("");
            setFulfilConfirm(null);
            toast.success(
                t("bulk_status_summary", {
                    n: String(result.updated),
                    status: t(`status_${target}`),
                }),
                { toastId: "bulk-status-ok" },
            );
            // A release from On hold can fold the order into a consolidated
            // shipment with the buyer's other open orders — say so, or the
            // operator looks for an unfulfilled order that is now a CON.
            if (result.consolidated && result.consolidated.length > 0) {
                toast.info(releaseConsolidatedText(result.consolidated), { toastId: "bulk-status-consolidated" });
            }
        } catch {
            toast.error(t("server_error"), { toastId: "bulk-status-err" });
        } finally {
            setBulkApplying(false);
        }
    };

    // Apply. Fulfilling by hand consumes stock and tells the sales
    // channel, so it stops for a confirmation that also offers the
    // tracking details Portal has no way to know — see
    // manual-fulfil-modal.tsx. Every other target applies straight away:
    // they are all reversible from this same dropdown.
    const handleBulkStatusChange = () => {
        if (selectedOrderIds.size === 0 || !bulkStatus || readOnly) return;
        const target = bulkStatus as OrderStatus;
        if (target !== "fulfilled") {
            void applyBulkStatus(target);
            return;
        }
        // Listed in the order they appear on screen rather than in
        // selection order, so the modal reads like the table behind it.
        const targets: ManualFulfilTarget[] = results
            .filter(o => selectedOrderIds.has(o.order_id))
            .map(o => ({ order_id: o.order_id, order_number: o.order_number || o.order_id }));
        if (targets.length === 0) return;
        setFulfilConfirm(targets);
    };

    // ─── Release short ────────────────────────────────────────────
    // Per-line shortage detail for the open order. Keyed on order_id +
    // stock_blocked so a successful release (which flips the flag false)
    // clears the row highlights on the spot. The dry-run can 409 (e.g.
    // the order got waved since the list loaded) — that just leaves the
    // rows un-annotated.
    const detailOrderId = selectedOrder?.order_id ?? null;
    const detailStockBlocked = selectedOrder?.status === "unfulfilled" && selectedOrder?.stock_blocked === true;
    // Stock-held orders are the same shortage one step earlier — the
    // dry-run runs for them too so the item rows show which SKUs are
    // short and by how many.
    const detailHeldShort = heldInsufficientStock(selectedOrder);
    useEffect(() => {
        setShortLines(null);
        if (!detailOrderId || (!detailStockBlocked && !detailHeldShort)) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/orders/${encodeURIComponent(detailOrderId)}/release-short`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ apply: false }),
                });
                const body = await res.json().catch(() => ({}));
                if (cancelled || !res.ok || body?.ok !== true || body?.blocked !== true) return;
                setShortLines(shortfallLineMap(body.lines ?? []));
            } catch { /* rows stay un-annotated; the banner still explains the block */ }
        })();
        return () => { cancelled = true; };
    }, [detailOrderId, detailStockBlocked, detailHeldShort]);

    // Free a stock-blocked order to pick without its missing units. The
    // first call is a BE dry-run (apply:false) that returns the per-line
    // shortfall for the confirm modal; confirming re-posts with
    // apply:true, which pre-marks order_items.missing_count so wave
    // candidacy stops requiring those units and dispatch shows them as
    // missing for the refund.
    const openReleaseShort = async (orderId: string) => {
        if (releasing || readOnly) return;
        setReleasing(true);
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/release-short`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ apply: false }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body?.ok) {
                toast.error(t("orders_release_short_failed"));
                return;
            }
            if (!body.blocked && !heldInsufficientStock(selectedOrder)) {
                // Stock arrived since the list loaded — nothing to release.
                toast.info(t("orders_release_short_not_blocked"));
                setSelectedOrder(prev => prev ? { ...prev, stock_blocked: false } : prev);
                setRefreshKey(k => k + 1);
                return;
            }
            // A held order with zero physical shortfall still releases —
            // its shortage is other orders' reserved demand, so the modal
            // opens with no lines to mark and the confirm just un-holds it.
            setReleasePrompt({ lines: body.lines ?? [] });
        } catch {
            toast.error(t("orders_release_short_failed"));
        } finally {
            setReleasing(false);
        }
    };

    const confirmReleaseShort = async () => {
        if (!selectedOrder || releasing || readOnly) return;
        setReleasing(true);
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(selectedOrder.order_id)}/release-short`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ apply: true }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body?.ok) {
                toast.error(t("orders_release_short_failed"));
                return;
            }
            const units = (body.applied ?? [])
                .reduce((s: number, l: { shortfall?: number }) => s + Number(l.shortfall ?? 0), 0);
            toast.success(units > 0
                ? t("orders_release_short_done", { count: String(units) })
                : t("orders_release_hold_done"));
            setReleasePrompt(null);
            // A held order comes off hold as part of the release.
            setSelectedOrder(prev => prev
                ? {
                    ...prev,
                    stock_blocked: false,
                    ...(prev.status === "on_hold"
                        ? { status: "unfulfilled" as const, on_hold_reason: null }
                        : {}),
                }
                : prev);
            setRefreshKey(k => k + 1);
        } catch {
            toast.error(t("orders_release_short_failed"));
        } finally {
            setReleasing(false);
        }
    };

    // Accepts the pseudo pill values too (stock_blocked / consolidated) —
    // both resolve to status_* i18n keys and --kebab badge classes.
    const statusLabel = (s: OrderStatus | StatusFilter) => t(`status_${s}`);
    const statusClass = (s: OrderStatus | StatusFilter) => `dispatch-status-badge dispatch-status-badge--${s.replace(/_/g, "-")}`;

    // Per-order variants — a fulfilled order that only partially shipped
    // keeps status 'fulfilled' in the DB (it lists under the combined
    // Fulfilled pill) and must read as "Partially Fulfilled" wherever
    // its badge is shown.
    const orderStatusLabel = (o: DispatchOrder) =>
        hasPendingRefund(o) ? t("status_pending_refund")
            : isPartiallyFulfilled(o) ? t("status_partially_fulfilled") : statusLabel(o.status as OrderStatus);
    const orderStatusClass = (o: DispatchOrder) =>
        hasPendingRefund(o) ? statusClass("pending_refund")
        : isPartiallyFulfilled(o)
            ? "dispatch-status-badge dispatch-status-badge--partially-fulfilled"
            : statusClass(o.status as OrderStatus);

    const openOrderDetail = (order: DispatchOrder) => {
        const a = order.delivery_address;
        const b = order.billing_address;
        setEditName(a?.name || "");
        setEditCompany(a?.company || "");
        setEditLine1(a?.line_1 || "");
        setEditLine2(a?.line_2 || "");
        setEditCity(a?.city || "");
        setEditCounty(a?.county || "");
        setEditPostcode(a?.postcode || "");
        setEditCountry((a?.country || "").toUpperCase());
        setEditPhoneCountry(a?.phone_country || dialCodeForCountry(a?.country) || "");
        setEditPhone(a?.phone || order.customer?.phone || "");
        setEditEmail(a?.email || order.customer?.email || "");
        setEditBillingName(b?.name || "");
        setEditBillingCompany(b?.company || "");
        setEditBillingLine1(b?.line_1 || "");
        setEditBillingLine2(b?.line_2 || "");
        setEditBillingCity(b?.city || "");
        setEditBillingCounty(b?.county || "");
        setEditBillingPostcode(b?.postcode || "");
        setEditBillingCountry((b?.country || "").toUpperCase());
        setEditBillingPhoneCountry(b?.phone_country || dialCodeForCountry(b?.country) || "");
        setEditBillingPhone(b?.phone || "");
        setEditBillingEmail(b?.email || "");
        const dtRaw = (order.delivery_type || "").toLowerCase();
        setEditDeliveryType(
            dtRaw === "standard" || dtRaw === "priority" || dtRaw === "express" || dtRaw === "collection"
                ? dtRaw
                : "",
        );
        setEditPickQueue(order.pick_queue ?? null);
        setActiveAddressTab("delivery");
        setActiveDetailTab("details");
        setExpandedParcel(null);
        setSelectedOrder(order);
        void loadQueueNamesForOrder(order.warehouse || getUserWarehouse());
        // Split orders + consolidation: the list object carries the
        // linkage ids but not the linked rows (detail-only). Hydrate
        // them so the modal can show clickable links to the related
        // sub-orders / consolidated orders.
        // Always hydrate the detail: carrier_booking (estimated cost +
        // cancellability) is detail-only, and related/consolidation
        // links are merged in the same call.
        void hydrateRelatedOrders(order.order_id);
    };

    // Fetch the linked orders (split siblings + consolidation children/
    // master) for the currently-open order and merge them in without
    // clobbering any in-flight edits (only the linkage fields and the
    // read-only LIVE_FIELDS above are patched, and only if the same order
    // is still open).
    const hydrateRelatedOrders = async (orderId: string) => {
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {});
            if (!res.ok) return;
            const json = await res.json();
            const related = json?.order?.related_orders;
            const consolidatedOrders = json?.order?.consolidated_orders;
            const consolidatedMaster = json?.order?.consolidated_master;
            // carrier_booking is detail-only — merge it so the modal can
            // show the estimated cost and the Cancel-label button.
            const carrierBooking = json?.order?.carrier_booking;
            // Replacement linkage is detail-only too. Miss these and the
            // band only ever appears on the path that replaces the whole
            // order object (openRelatedOrder), never on a plain open or
            // straight after raising a replacement.
            const replacementOrders = json?.order?.replacement_orders;
            const replacementOfOrder = json?.order?.replacement_of_order;
            // Parcels are server-owned (never edited in the modal) and the
            // detail copy carries label_available — the list's copy does
            // not — so the Shipment table's Print / Download buttons only
            // appear once this merge lands.
            const detailParcels = json?.order?.parcels;
            const live = pickLiveFields(json?.order);
            setSelectedOrder(prev =>
                prev && prev.order_id === orderId
                    ? {
                        ...prev,
                        ...live,
                        ...(Array.isArray(detailParcels) ? { parcels: detailParcels } : {}),
                        ...(Array.isArray(related) ? { related_orders: related } : {}),
                        ...(Array.isArray(consolidatedOrders) ? { consolidated_orders: consolidatedOrders } : {}),
                        ...(consolidatedMaster !== undefined ? { consolidated_master: consolidatedMaster } : {}),
                        ...(carrierBooking !== undefined ? { carrier_booking: carrierBooking } : {}),
                        ...(Array.isArray(replacementOrders) ? { replacement_orders: replacementOrders } : {}),
                        ...(replacementOfOrder !== undefined ? { replacement_of_order: replacementOfOrder } : {}),
                        // Send extra box switch for the order's warehouse (detail-only).
                        ...(typeof json?.order?.extra_box_enabled === "boolean" ? { extra_box_enabled: json.order.extra_box_enabled } : {}),
                        // Why its units are on Pending Refund (detail-only, BE migration 277).
                        ...(Array.isArray(json?.order?.refund_reasons) ? { refund_reasons: json.order.refund_reasons } : {}),
                        // A consolidated shipment's pending refund per customer order (detail-only).
                        ...(Array.isArray(json?.order?.refund_pending_parts) ? { refund_pending_parts: json.order.refund_pending_parts } : {}),
                        // A list row from ?items=summary has no lines — the
                        // detail's full lines arrive here (a full list row
                        // keeps its own, as before).
                        ...(prev.items_omitted && Array.isArray(json?.order?.items)
                            ? { items: json.order.items, items_omitted: false }
                            : {}),
                    }
                    : prev,
            );
            // Correct the row behind the modal too, so closing it doesn't
            // drop back to the stale badge you just disproved. Same patch
            // the cancel-label path already applies to the list. Note
            // stock_blocked is NOT in LIVE_FIELDS: it is computed only by
            // listOrders (for unfulfilled rows) and is absent from the
            // detail response, so it stays whatever the list said.
            if (Object.keys(live).length > 0) {
                let statusMoved = false;
                setOrders(prev => prev.map(o => {
                    if (o.order_id !== orderId) return o;
                    if (live.status !== undefined && live.status !== o.status) statusMoved = true;
                    return { ...o, ...live };
                }));
                // A corrected status can re-bucket the row under a different
                // pill, i.e. out of the visible window — and applyBulkStatus
                // iterates selectedOrderIds, not the rendered rows. Drop this
                // order from the selection so it can't sneak into the next
                // bulk action from a pill the user can no longer see it in.
                if (statusMoved) {
                    setSelectedOrderIds(prev => {
                        if (!prev.has(orderId)) return prev;
                        const next = new Set(prev);
                        next.delete(orderId);
                        return next;
                    });
                }
            }
        } catch { /* non-fatal — links just won't show */ }
    };

    // Send replacement ships free stock, so it is admin-and-above — the
    // BE enforces it too (auth("admin") on both replacement routes); this
    // only keeps the button out of a packer's way. Role comes from the same
    // localStorage key the rest of the app reads.
    // Manager+ from the same localStorage key the rest of the app reads — the pre-access-control rule,
    // used only until the access map is authoritative.
    const isManagerPlusUser = useMemo(() => {
        try {
            const raw = localStorage.getItem("user");
            if (!raw) return false;
            const u = JSON.parse(raw);
            return isManagerPlus(u?.position ?? u?.permissions);
        } catch {
            return false;
        }
    }, []);
    const { authoritative: accessAuthoritative, canWrite } = useAccess();
    // Send replacement has its own matrix row (admin.orders.replacement, Admin + Manager by default,
    // 2026-10-02): it creates a real order for the customer.
    const canSendReplacement = accessAuthoritative ? canWrite("admin.orders.replacement") : isManagerPlusUser;
    // Send extra box (admin.orders.extra_box, Team Leader and above, 2026-10-22): one more courier
    // parcel on a shipped order for a unit found after dispatch. Switched on per warehouse.
    const canSendExtraBox = accessAuthoritative ? canWrite("admin.orders.extra_box") : isManagerPlusUser;
    // Refund issued (admin.orders.refund_issue, Admin / Manager / Customer Service by default,
    // 2026-10-23): the refund manager's step on the Pending Refund pill.
    const canIssueRefund = accessAuthoritative ? canWrite("admin.orders.refund_issue") : isManagerPlusUser;
    const [issuingRefund, setIssuingRefund] = useState(false);
    // The order whose Issue Refund BargainFox answered "already refunded":
    // offer to mark it issued without asking again.
    const [markOnlyOffer, setMarkOnlyOffer] = useState<string | null>(null);
    const extraBoxOffered = (o: DispatchOrder) => canSendExtraBox
        && o.extra_box_enabled === true
        // A flagged order whose parcels already went out counts as shipped:
        // a found unit goes on its own (Alex, 2026-10-23 pm, JEU723161).
        && (o.status === "fulfilled" || o.status === "partially_refunded"
            || (o.status === "flagged" && Boolean(o.packed_at)))
        && !o.consolidated_into
        && o.delivery_type !== "collection"
        && (o.parcels?.length ?? 0) > 0;

    // Void ONE extra box (never the original shipment). The BE refuses once
    // the courier has scanned it.
    const voidExtraBox = async (o: DispatchOrder, trackingNumber: string) => {
        if (voidingExtraBox) return;
        setVoidingExtraBox(trackingNumber);
        try {
            const res = await apiFetch(
                `/orders/${encodeURIComponent(o.order_id)}/extra-box/${encodeURIComponent(trackingNumber)}/void`,
                { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data?.message || t("extra_box_void_failed"));
                return;
            }
            const failed = Array.isArray(data?.carrier_cancels_failed) ? data.carrier_cancels_failed : [];
            if (failed.length > 0) toast.warn(`${t("extra_box_voided")} — ${failed.map((f: { error?: string }) => f.error).filter(Boolean).join("; ")}`);
            else toast.success(t("extra_box_voided"));
            setVoidExtraBoxPrompt(null);
            hydrateRelatedOrders(o.order_id);
        } catch {
            toast.error(t("extra_box_void_failed"));
        } finally {
            setVoidingExtraBox(null);
        }
    };
    // Line add / remove / quantity and detaching a consolidated child follow Orders write, as the API does.
    const canEditOrderLines = accessAuthoritative ? canWrite("admin.orders") : isManagerPlusUser;

    // Open a sibling sub-order in the same modal. Fetches the full detail
    // (the list may be filtered to another warehouse, so the sibling
    // isn't guaranteed to be in memory).
    const openRelatedOrder = async (orderId: string) => {
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {});
            if (!res.ok) { toast.error(t("orders_related_open_failed")); return; }
            const json = await res.json();
            if (json?.order) openOrderDetail(json.order);
        } catch {
            toast.error(t("orders_related_open_failed"));
        }
    };

    // `?order=<order_id>` opens that order's detail once on arrival — the
    // Returns page links its orders here.
    const deepLinkedOrderRef = useRef<string | null>(readSearchParam("order"));
    useEffect(() => {
        const id = deepLinkedOrderRef.current;
        if (!id) return;
        deepLinkedOrderRef.current = null;
        void openRelatedOrder(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cancel the order's carrier label before the manifest cutoff. The
    // BE re-checks the window server-side (a stale modal can't cancel
    // past cutoff). `mode` is the operator's answer to WHY:
    //   reship       → label voided, order back to 'dispatch'. The packer
    //                  reaches it again by scanning the SHIPPING LABEL at
    //                  the desk (dispatch resolve-unit resolves a label to
    //                  its order) — the original tote is usually recycled
    //                  by then, so "re-scan the tote" often isn't possible;
    //   cancel_order → label voided AND the order is cancelled.
    // Refund issued: the refund manager has sent the money by hand in the
    // channel. The BE moves every pending unit onto refund_count and hands
    // back the order, which leaves Pending Refund for Refunded.
    // Issue Refund (2026-10-31): on a BargainFox order the BE asks BargainFox
    // to pay it first, and only moves the units of the refunds BargainFox
    // took (a consolidated order is one refund per part); every refusal
    // carries BargainFox's reason, shown as is. When BargainFox says nothing
    // is left to refund — it was refunded by hand in BargainFox admin — the
    // refund manager is offered to just mark it issued (markOnly).
    const markRefundIssued = async (order: DispatchOrder, { markOnly = false }: { markOnly?: boolean } = {}) => {
        const orderId = order.order_id;
        const channel = channelLabel(order.sales_channel, t);
        const money = (amount: number) => formatMoney(amount, order.currency || "GBP");
        const failed = (f: { code?: string; message?: string; channel_code?: string }) => {
            toast.error(t("refund_issue_channel_failed", { channel, reason: String(f.message ?? f.code) }));
            if ((f.channel_code && ALREADY_REFUNDED_CODES.has(f.channel_code)) || f.code === "channel_refund_unplannable") setMarkOnlyOffer(orderId);
        };
        setIssuingRefund(true);
        setMarkOnlyOffer(null);
        const who = resolveAttribution();
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/refund-issued`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ issued_by_uid: who.resolved_by_uid, issued_by_name: who.resolved_by_name, ...(markOnly ? { mark_only: true } : {}) }),
            });
            const json = await res.json().catch(() => null);
            if (res.status === 409 && json?.code === "no_pending_refund") {
                toast.info(t("refund_issue_nothing_pending"));
            } else if (!res.ok && typeof json?.code === "string" && (json.code.startsWith("channel_") || json.code === "use_master" || json.code === "refund_in_progress")) {
                failed(json);
            } else if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            } else if (Array.isArray(json?.channel_refunds)) {
                const paid = json.channel_refunds.reduce((sum: number, r: { amount?: string }) => sum + (Number(r.amount) || 0), 0);
                toast.success(t("refund_issue_channel_done", { channel, amount: money(paid) }));
                for (const f of json.channel_failures ?? []) failed(f);
            } else {
                toast.success(t("refund_issue_done", { n: String(json?.units ?? 0) }));
            }
            // A parcel that came back to us, refunded from the Returns page: its customer
            // email goes now the money has (Jack, 2026-11-03).
            if (Array.isArray(json?.returned_parcels) && json.returned_parcels.some((p: { email_queued?: boolean }) => p.email_queued)) {
                toast.info(t("refund_issue_email_queued"));
            }
            // Either way the row is stale: take the server's copy.
            const fresh: DispatchOrder | null = json?.order
                ?? await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {})
                    .then(r => (r.ok ? r.json() : null)).then(j => j?.order ?? null).catch(() => null);
            if (fresh) {
                setSelectedOrder(prev => (prev && prev.order_id === orderId ? fresh : prev));
                setOrders(prev => prev.map(o => (o.order_id === orderId ? fresh : o)));
            }
            // A consolidated part's refund also moved its shipment's counts: refresh that row.
            if (order.consolidated_into) {
                const masterId = order.consolidated_into;
                const master: DispatchOrder | null = await apiFetch(`/orders/${encodeURIComponent(masterId)}`, {})
                    .then(r => (r.ok ? r.json() : null)).then(j => j?.order ?? null).catch(() => null);
                if (master) setOrders(prev => prev.map(o => (o.order_id === masterId ? master : o)));
            }
        } catch {
            toast.error(t("refund_issue_failed"));
        } finally {
            setIssuingRefund(false);
        }
    };

    // Send to packing moved the order to 'dispatch' — patch the modal and
    // the row behind it, and drop it from the bulk selection (it leaves
    // the pill it was selected under).
    const markSentToPack = (orderId: string) => {
        const patch = (o: DispatchOrder) => (o.order_id === orderId ? { ...o, status: "dispatch" as DispatchOrder["status"] } : o);
        setSelectedOrder(prev => (prev ? patch(prev) : prev));
        setOrders(prev => prev.map(patch));
        setSelectedOrderIds(prev => {
            if (!prev.has(orderId)) return prev;
            const next = new Set(prev);
            next.delete(orderId);
            return next;
        });
    };

    const cancelCarrierLabel = async (orderId: string, mode: "reship" | "cancel_order") => {
        if (cancellingLabel || readOnly) return;
        setCancellingLabel(true);
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/cancel-carrier-label`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mode }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data?.message || t("cancel_carrier_label_failed"));
                return;
            }
            // What did APC actually say? The BE voids synchronously now,
            // so "queued" is no longer the answer — a label that could not
            // be voided is still live and must be chased, not celebrated.
            const voids = summariseCarrierVoids(data);
            // Named by the BE ("APC", "DPD Poland") — the toast used to say
            // APC whatever carrier booked the order.
            const carrier = voids.carrier ?? t("carrier_name_unknown");
            if (voids.failed.length > 0) {
                toast.error(
                    t("carrier_void_failed_live", {
                        carrier,
                        n: String(voids.failed.length),
                        detail: carrierVoidFailureDetail(voids.failed),
                    }),
                    { autoClose: false, toastId: "carrier-void-failed" },
                );
            } else if (voids.retrying.length > 0) {
                toast.warn(
                    t("carrier_void_retrying", { carrier, n: String(voids.retrying.length) }),
                    { autoClose: 15000, toastId: "carrier-void-retry" },
                );
            } else {
                // voidMode 'local' (DPD, no cancel API): the label is void
                // in Portal only — say so rather than "cancelled at DPD".
                const key = mode === "cancel_order"
                    ? (voids.localVoid ? "cancel_carrier_label_done_local_cancelled" : "cancel_carrier_label_done_cancelled")
                    : (voids.localVoid ? "cancel_carrier_label_done_local_reship" : "cancel_carrier_label_done_reship");
                toast.success(t(key, { carrier, n: String(voids.confirmed) }));
            }
            // Shared (add-box) consignment: the orders still riding it were
            // renumbered by the carrier when this order's boxes came off.
            // Their old labels are dead — say so, loudly and separately.
            if (voids.reissued.length > 0) {
                toast.warn(
                    t("carrier_labels_reissued", {
                        carrier,
                        n: String(voids.reissued.length),
                        detail: carrierReissueDetail(voids.reissued),
                    }),
                    { autoClose: false, toastId: "carrier-labels-reissued" },
                );
            }
            // Refresh the modal from the server so the button drops and
            // the order shows its reverted state, and patch the list row.
            const fresh = await apiFetch(`/orders/${encodeURIComponent(orderId)}`, {});
            if (fresh.ok) {
                const json = await fresh.json();
                if (json?.order) {
                    setSelectedOrder(json.order);
                    setOrders(prev => prev.map(o => (o.order_id === orderId ? json.order : o)));
                }
            }
        } catch {
            toast.error(t("cancel_carrier_label_failed"));
        } finally {
            setCancellingLabel(false);
        }
    };

    // Drives the Save button's disabled state on the edit modal — Save is
    // greyed out until the user has actually edited something so a "no-op"
    // save can't fire by accident. Mirrors the fallback chain in
    // openOrderDetail above for both delivery and billing fields.
    const editHasChanges = useMemo(() => {
        if (!selectedOrder) return false;
        const a = selectedOrder.delivery_address;
        const b = selectedOrder.billing_address;
        // Company, county and the dial-code dropdown were missing from this
        // list, so editing only one of them left Save greyed out even though
        // handleSave would have sent the change (see the bug fixed 2026-11-03).
        return editName            !== (a?.name     ?? "")
            || editCompany         !== (a?.company  ?? "")
            || editLine1           !== (a?.line_1   ?? "")
            || editLine2           !== (a?.line_2   ?? "")
            || editCity            !== (a?.city     ?? "")
            || editCounty          !== (a?.county   ?? "")
            || editPostcode        !== (a?.postcode ?? "")
            || editCountry         !== (a?.country  ?? "")
            || editPhoneCountry    !== (a?.phone_country || dialCodeForCountry(a?.country) || "")
            || editPhone           !== (a?.phone    ?? selectedOrder.customer?.phone ?? "")
            || editEmail           !== (a?.email    ?? selectedOrder.customer?.email ?? "")
            || editBillingName     !== (b?.name     ?? "")
            || editBillingCompany  !== (b?.company  ?? "")
            || editBillingLine1    !== (b?.line_1   ?? "")
            || editBillingLine2    !== (b?.line_2   ?? "")
            || editBillingCity     !== (b?.city     ?? "")
            || editBillingCounty   !== (b?.county   ?? "")
            || editBillingPostcode !== (b?.postcode ?? "")
            || editBillingCountry  !== (b?.country  ?? "")
            || editBillingPhoneCountry !== (b?.phone_country || dialCodeForCountry(b?.country) || "")
            || editBillingPhone    !== (b?.phone    ?? "")
            || editBillingEmail    !== (b?.email    ?? "")
            || editDeliveryType    !== ((selectedOrder.delivery_type ?? "").toLowerCase())
            || editPickQueue       !== (selectedOrder.pick_queue ?? null);
    }, [
        selectedOrder,
        editName, editCompany, editLine1, editLine2, editCity, editCounty, editPostcode, editCountry,
        editPhoneCountry, editPhone, editEmail,
        editBillingName, editBillingCompany, editBillingLine1, editBillingLine2, editBillingCity,
        editBillingCounty, editBillingPostcode, editBillingCountry, editBillingPhoneCountry,
        editBillingPhone, editBillingEmail,
        editDeliveryType, editPickQueue,
    ]);

    // The /orders feed returns weight_grams = 0 (it's hydrated FE-side from
    // the products catalogue — see orders.controller.js reshapeItem). Without
    // this fetch every item — and the order total — would render as 0.00 kg.
    // Triggers when the modal opens; idempotent per order_id.
    const hydratedOrderIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (!selectedOrder) return;
        if (hydratedOrderIdRef.current === selectedOrder.order_id) return;
        const skus = Array.from(new Set(selectedOrder.items.map(i => i.sku).filter(Boolean)));
        if (skus.length === 0) return;
        const allHaveWeight = selectedOrder.items.every(i => (i.weight_grams ?? 0) > 0);
        if (allHaveWeight) {
            hydratedOrderIdRef.current = selectedOrder.order_id;
            return;
        }
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/products/by-skus?skus=${encodeURIComponent(skus.join(","))}`,
                    {},
                );
                if (!res.ok || !alive) return;
                type ProdRow = { sku: string; title: string | null; image_url: string | null;
                                 weight_grams: number | null };
                const data: { products?: ProdRow[] } = await res.json();
                const bySku = new Map<string, ProdRow>();
                for (const p of (data.products ?? [])) bySku.set(p.sku, p);
                if (!alive) return;
                hydratedOrderIdRef.current = selectedOrder.order_id;
                setSelectedOrder(prev => {
                    if (!prev || prev.order_id !== selectedOrder.order_id) return prev;
                    return {
                        ...prev,
                        items: prev.items.map(i => {
                            const p = bySku.get(i.sku);
                            if (!p) return i;
                            return {
                                ...i,
                                title:     i.title     || p.title || i.sku,
                                image_url: i.image_url || p.image_url,
                                weight_grams: i.weight_grams || (p.weight_grams ?? 0),
                            };
                        }),
                    };
                });
            } catch { /* leave at 0 — UI just shows 0.00 kg */ }
        })();
        return () => { alive = false; };
    }, [selectedOrder]);

    // ---- CSV download / template / upload --------------------------------
    // The download mirrors the BE's bulk semantics: one header row per
    // order (line_position blank), then one row per line item. Round-trips
    // back into POST /orders/bulk losslessly so an admin can edit in Excel
    // and re-upload.

    const handleDownloadTemplate = () => {
        const sample: string[][] = [CSV_COLUMNS as unknown as string[]];
        const blank = () => Array(CSV_COLUMNS.length).fill("");
        const setCol = (
            row: string[],
            col: typeof CSV_COLUMNS[number],
            v: string,
        ) => { row[CSV_COLUMNS.indexOf(col)] = v; };

        // The template walks two example orders so every action variant is
        // covered without leaving the visible demo order in a destroyed
        // state:
        //   JL-EX-001 → ends as a normal unfulfilled order with one line.
        //               Used to demo `add` (header + item), `update`
        //               (header + item) and `remove` (item).
        //   JL-EX-002 → ends cancelled, used to demo `remove` (cancel
        //               whole order). Visible under the Cancelled pill.
        // Every header column is filled on JL-EX-001 so the template
        // doubles as documentation of what each field accepts.

        // ROW 2 — JL-EX-001: full `add` header (line_position blank). Billing
        // fields are shown filled, but the BE will default them from delivery
        // if you leave them blank.
        const addHeader = blank();
        setCol(addHeader, "action",                 "add");
        setCol(addHeader, "order_id",               "JL-EX-001");
        setCol(addHeader, "order_number",           "JL-EX-001");
        setCol(addHeader, "sales_channel",          "jobalots");
        setCol(addHeader, "warehouse",              "Newport");
        setCol(addHeader, "status",                 "unfulfilled");
        setCol(addHeader, "delivery_type",          "standard");
        setCol(addHeader, "currency",               "GBP");
        setCol(addHeader, "total",                  "59.97");
        setCol(addHeader, "shipping_paid",          "3.50");
        setCol(addHeader, "tax_amount",             "9.99");
        setCol(addHeader, "tax_rate",               "20.00");
        setCol(addHeader, "delivery_name",          "Example Customer");
        setCol(addHeader, "delivery_line_1",        "1 Example Street");
        setCol(addHeader, "delivery_line_2",        "Flat 2");
        setCol(addHeader, "delivery_city",          "Cardiff");
        setCol(addHeader, "delivery_county",        "South Glamorgan");
        setCol(addHeader, "delivery_postcode",      "CF10 1AA");
        setCol(addHeader, "delivery_country",       "GB");
        setCol(addHeader, "delivery_phone_country", "+44");
        setCol(addHeader, "delivery_phone_number",  "7700900000");
        setCol(addHeader, "delivery_email",         "customer@example.com");
        setCol(addHeader, "billing_name",           "Example Customer");
        setCol(addHeader, "billing_line_1",         "1 Example Street");
        setCol(addHeader, "billing_line_2",         "Flat 2");
        setCol(addHeader, "billing_city",           "Cardiff");
        setCol(addHeader, "billing_county",         "South Glamorgan");
        setCol(addHeader, "billing_postcode",       "CF10 1AA");
        setCol(addHeader, "billing_country",        "GB");
        setCol(addHeader, "billing_phone_country",  "+44");
        setCol(addHeader, "billing_phone_number",   "7700900000");
        setCol(addHeader, "billing_email",          "customer@example.com");
        setCol(addHeader, "notes",                  "Leave with neighbour at #2 if no answer");
        setCol(addHeader, "channel_created_at",     todayStr());
        sample.push(addHeader);

        // ROW 3 — `add` line 1 of JL-EX-001. line_position + sku + quantity
        // are required; unit_price and count fields are optional.
        const addItem1 = blank();
        setCol(addItem1, "action",        "add");
        setCol(addItem1, "order_id",      "JL-EX-001");
        setCol(addItem1, "line_position", "1");
        setCol(addItem1, "sku",           "JLI-EX-001");
        setCol(addItem1, "quantity",      "1");
        setCol(addItem1, "unit_price",    "29.99");
        sample.push(addItem1);

        // ROW 4 — `add` line 2 of JL-EX-001 (gets removed in row 7 below).
        const addItem2 = blank();
        setCol(addItem2, "action",        "add");
        setCol(addItem2, "order_id",      "JL-EX-001");
        setCol(addItem2, "line_position", "2");
        setCol(addItem2, "sku",           "JLI-EX-002");
        setCol(addItem2, "quantity",      "2");
        setCol(addItem2, "unit_price",    "14.99");
        sample.push(addItem2);

        // ROW 5 — header-level `update`. Only non-blank columns are touched,
        // so this updates just the notes field. line_position blank → header.
        const updateNotes = blank();
        setCol(updateNotes, "action",   "update");
        setCol(updateNotes, "order_id", "JL-EX-001");
        setCol(updateNotes, "notes",    "Updated note — call before delivery");
        sample.push(updateNotes);

        // ROW 6 — item-level `update`. line_position set → updates line 2;
        // only unit_price changes here.
        const updateItemPrice = blank();
        setCol(updateItemPrice, "action",        "update");
        setCol(updateItemPrice, "order_id",      "JL-EX-001");
        setCol(updateItemPrice, "line_position", "2");
        setCol(updateItemPrice, "unit_price",    "12.99");
        sample.push(updateItemPrice);

        // ROW 7 — `remove` line 2. line_position set → DELETE this row from
        // order_items. JL-EX-001 still has line 1 after this and remains
        // visible under the unfulfilled / all pill.
        const removeItem = blank();
        setCol(removeItem, "action",        "remove");
        setCol(removeItem, "order_id",      "JL-EX-001");
        setCol(removeItem, "line_position", "2");
        sample.push(removeItem);

        // ROW 8 — `add` JL-EX-002 (minimal header). Demonstrates the "billing
        // defaults to delivery" behaviour: only delivery is set, the BE
        // mirrors it into billing on insert.
        const addHeader2 = blank();
        setCol(addHeader2, "action",            "add");
        setCol(addHeader2, "order_id",          "JL-EX-002");
        setCol(addHeader2, "order_number",      "JL-EX-002");
        setCol(addHeader2, "sales_channel",     "bargainfox");
        setCol(addHeader2, "warehouse",         "Newport");
        setCol(addHeader2, "status",            "unfulfilled");
        setCol(addHeader2, "delivery_type",     "standard");
        setCol(addHeader2, "currency",          "GBP");
        setCol(addHeader2, "total",             "9.99");
        setCol(addHeader2, "delivery_name",     "Cancellation Demo");
        setCol(addHeader2, "delivery_line_1",   "2 Example Street");
        setCol(addHeader2, "delivery_city",     "Cardiff");
        setCol(addHeader2, "delivery_postcode", "CF10 1AB");
        setCol(addHeader2, "delivery_country",  "GB");
        sample.push(addHeader2);

        // ROW 9 — `remove` the whole order. line_position blank → flips
        // status to 'cancelled' (preserves the row for audit). JL-EX-002
        // is now visible only under the Cancelled pill.
        const cancelOrder = blank();
        setCol(cancelOrder, "action",   "remove");
        setCol(cancelOrder, "order_id", "JL-EX-002");
        sample.push(cancelOrder);

        triggerDownload("orders-template.csv", toCsv(sample));
    };

    // Flatten the current orders list into CSV rows. Emits one header row
    // per order followed by one row per line item — matching the BE's
    // "row addresses one thing" semantics. Skips fields the BE doesn't
    // accept (packed_at / resolved_at / latest_flag are read-only).
    // The list rows carry line totals, not item rows (items=summary), so the
    // export re-asks for the same orders with full lines. Rows that already
    // have their items (a search, or none omitted) export as loaded.
    const ordersForExport = async (): Promise<DispatchOrder[] | null> => {
        if (!orders.some(o => o.items_omitted)) return orders;
        try {
            const res = await apiFetch(`/orders?${listParamsRef.current}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            return Array.isArray(data.orders) ? data.orders : [];
        } catch {
            toast.error(t("server_error"), { toastId: "orders-export-err" });
            return null;
        }
    };

    const handleDownloadCurrent = async () => {
        const source = await ordersForExport();
        if (!source) return;
        const headerRow = CSV_COLUMNS as unknown as string[];
        const out: string[][] = [headerRow];
        const set = (row: string[], col: string, value: string) => {
            const idx = CSV_COLUMNS.indexOf(col as typeof CSV_COLUMNS[number]);
            if (idx >= 0) row[idx] = value;
        };
        for (const o of source) {
            // Header row (line_position blank).
            // Cast to access tax_amount / tax_rate which are returned by
            // the API but not on the DispatchOrder type today.
            const ox = o as DispatchOrder & { tax_amount?: number | null; tax_rate?: number | null };
            const da = o.delivery_address;
            const ba = o.billing_address;
            const headerRowOut = Array(CSV_COLUMNS.length).fill("");
            set(headerRowOut, "action",                "update");
            set(headerRowOut, "order_id",              o.order_id);
            set(headerRowOut, "order_number",          o.order_number ?? "");
            set(headerRowOut, "sales_channel",         o.sales_channel ?? "");
            set(headerRowOut, "warehouse",             o.warehouse ?? "");
            set(headerRowOut, "status",                o.status ?? "");
            set(headerRowOut, "delivery_type",         o.delivery_type ?? "");
            set(headerRowOut, "currency",              o.currency ?? "");
            set(headerRowOut, "total",                 numStr(o.total));
            set(headerRowOut, "shipping_paid",         numStr(o.shipping_paid));
            set(headerRowOut, "tax_amount",            numStr(ox.tax_amount));
            set(headerRowOut, "tax_rate",              numStr(ox.tax_rate));
            set(headerRowOut, "delivery_name",         da?.name ?? "");
            set(headerRowOut, "delivery_line_1",       da?.line_1 ?? "");
            set(headerRowOut, "delivery_line_2",       da?.line_2 ?? "");
            set(headerRowOut, "delivery_city",         da?.city ?? "");
            set(headerRowOut, "delivery_county",       da?.county ?? "");
            set(headerRowOut, "delivery_postcode",     da?.postcode ?? "");
            set(headerRowOut, "delivery_country",      da?.country ?? "");
            set(headerRowOut, "delivery_phone_country",da?.phone_country ?? "");
            set(headerRowOut, "delivery_phone_number", da?.phone ?? "");
            set(headerRowOut, "delivery_email",        da?.email ?? "");
            if (ba) {
                set(headerRowOut, "billing_name",         ba.name ?? "");
                set(headerRowOut, "billing_line_1",       ba.line_1 ?? "");
                set(headerRowOut, "billing_line_2",       ba.line_2 ?? "");
                set(headerRowOut, "billing_city",         ba.city ?? "");
                set(headerRowOut, "billing_county",       ba.county ?? "");
                set(headerRowOut, "billing_postcode",     ba.postcode ?? "");
                set(headerRowOut, "billing_country",      ba.country ?? "");
                set(headerRowOut, "billing_phone_country",ba.phone_country ?? "");
                set(headerRowOut, "billing_phone_number", ba.phone ?? "");
                set(headerRowOut, "billing_email",        ba.email ?? "");
            }
            set(headerRowOut, "notes",                 o.notes ?? "");
            set(headerRowOut, "channel_created_at",    o.created_at ?? "");
            out.push(headerRowOut);
            // One row per item.
            for (const it of o.items) {
                const itemRow = Array(CSV_COLUMNS.length).fill("");
                set(itemRow, "action",                "update");
                set(itemRow, "order_id",              o.order_id);
                set(itemRow, "line_position",         numStr(it.line_position));
                set(itemRow, "sku",                   it.sku);
                set(itemRow, "quantity",              numStr(it.quantity));
                set(itemRow, "unit_price",            numStr(it.unit_price));
                set(itemRow, "scanned_count",         numStr(it.scannedCount));
                set(itemRow, "missing_count",         numStr(it.missingCount));
                set(itemRow, "damaged_count",         numStr(it.damagedCount));
                set(itemRow, "wrong_item_count",      numStr(it.wrongItemCount));
                set(itemRow, "barcode_damaged_count", numStr(it.barcodeDamagedCount));
                set(itemRow, "refund_count",          numStr(it.refundCount));
                out.push(itemRow);
            }
        }
        triggerDownload("orders.csv", toCsv(out));
    };

    const handleUpload = async (file: File) => {
        if (readOnly) return;
        const fileText = await file.text();
        const parsed = parseCsv(fileText);
        if (parsed.length === 0) {
            toast.error(t("totes_upload_empty"), { toastId: "orders-upload-empty" });
            return;
        }
        const header = parsed[0].map(h => h.trim().toLowerCase());
        for (const required of ["action", "order_id"]) {
            if (!header.includes(required)) {
                toast.error(t("orders_upload_bad_header"), { toastId: "orders-bad-header" });
                return;
            }
        }
        const objRows = parsed.slice(1).map(cells => {
            const obj: Record<string, string> = {};
            header.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
            return obj;
        });

        setUploading(true);
        setLastResult(null);
        setLastResultStatus(null);
        try {
            // One atomic request: the BE validates the whole file first
            // and applies nothing when any row is bad, so a broken CSV is
            // rejected outright instead of half-importing. Sending the
            // file whole (no chunking) is what makes that possible — the
            // 5MB body limit fits thousands of order rows.
            const res = await apiFetch(`/orders/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows: objRows, atomic: true }),
            });
            if (res.status === 413) {
                toast.error(t("bulk_upload_too_large"), { toastId: "orders-upload-413" });
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult = await res.json();
            setLastResult(result);
            setLastResultStatus(null);
            if (result.applied === false) {
                toast.error(
                    t("orders_upload_rejected", { n: String(result.errors.length) }),
                    { toastId: "orders-upload-rejected" },
                );
                return;
            }
            setRefreshKey(k => k + 1);
            // applied:true with errors means some rows hit an unexpected
            // failure mid-apply (e.g. a concurrent edit racing the
            // validation pass). Rare, but it must not look like a clean
            // success — the rows that errored were NOT applied.
            if (result.errors.length > 0) {
                toast.error(
                    t("orders_upload_partial", { n: String(result.errors.length) }),
                    { toastId: "orders-upload-partial" },
                );
                return;
            }
            toast.success(
                t("totes_bulk_summary", {
                    added: String(result.added),
                    updated: String(result.updated),
                    removed: String(result.removed),
                }),
                { toastId: "orders-upload-ok" },
            );
        } catch {
            toast.error(t("server_error"), { toastId: "orders-upload-err" });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleSave = async () => {
        if (!selectedOrder || readOnly) return;
        const locked = isOrderLocked(selectedOrder);
        const pickQueueChanged = editPickQueue !== (selectedOrder.pick_queue ?? null);
        if (locked && !pickQueueChanged) return;
        // Client-side phone validation: catch the obvious "wrong length
        // for country" cases before round-tripping to the BE so the
        // operator sees the issue immediately.
        if (!locked && editPhone && !isValidNationalForDial(editPhone, editPhoneCountry, editCountry)) {
            toast.error(t("phone_invalid_for_country"), { toastId: "order-phone-invalid" });
            return;
        }
        if (!locked && editBillingPhone && !isValidNationalForDial(editBillingPhone, editBillingPhoneCountry, editBillingCountry)) {
            toast.error(t("billing_phone_invalid_for_country"), { toastId: "order-phone-invalid" });
            return;
        }
        // Notes are no longer part of this save — they live in their own
        // thread (OrderNotes) and are written the moment one is added.
        const body: Record<string, unknown> = locked ? {} : {
            delivery_type: editDeliveryType || null,
            delivery_address: {
                name: editName,
                company: editCompany || null,
                line_1: editLine1,
                line_2: editLine2 || null,
                city: editCity,
                county: editCounty || null,
                postcode: editPostcode,
                country: editCountry,
                phone_country: editPhoneCountry || null,
                phone_number: editPhone ? editPhone.replace(/\D/g, "") : null,
                email: editEmail || null,
            },
            billing_address: {
                name: editBillingName,
                company: editBillingCompany || null,
                line_1: editBillingLine1,
                line_2: editBillingLine2 || null,
                city: editBillingCity,
                county: editBillingCounty || null,
                postcode: editBillingPostcode,
                country: editBillingCountry,
                phone_country: editBillingPhoneCountry || null,
                phone_number: editBillingPhone ? editBillingPhone.replace(/\D/g, "") : null,
                email: editBillingEmail || null,
            },
        };
        if (pickQueueChanged) body.pick_queue = editPickQueue;
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(selectedOrder.order_id)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (res.status === 409) {
                const errorBody = await res.json().catch(() => ({}));
                toast.error(errorBody?.message || t("server_error"), { toastId: "search-save-conflict" });
                return;
            }
            if (!res.ok) throw new Error("save");
            setSelectedOrder(null);
            setRefreshKey(k => k + 1);
            toast.dismiss();
            toast.success(t("saved"));
        } catch {
            toast.error(t("server_error"), { toastId: "search-save-err" });
        }
    };

    // ---- Hand-edited lines (add / remove / set quantity) -----------------
    // Any order that has not started picking (unfulfilled or on hold with
    // no live allocation) accepts line edits; the BE is the judge and
    // answers 409 with a reason when it no longer does. Each call returns
    // the re-fetched order, merged over the open one so detail-only fields
    // (carrier booking, related orders) survive.
    const applyLineEdit = async (path: string, method: "POST" | "DELETE" | "PATCH", body?: unknown, successKey?: string) => {
        if (!selectedOrder || lineEditBusy || readOnly) return false;
        setLineEditBusy(true);
        try {
            const res = await apiFetch(`/orders/${encodeURIComponent(selectedOrder.order_id)}${path}`, {
                method,
                headers: { "Content-Type": "application/json" },
                ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
            });
            const data = await res.json().catch(() => ({} as Record<string, unknown>));
            if (!res.ok) {
                toast.error((data?.message as string) || t("server_error"), { toastId: "order-line-edit" });
                return false;
            }
            const updated = data?.order as DispatchOrder | undefined;
            if (updated) {
                setSelectedOrder(prev => (prev && prev.order_id === updated.order_id ? { ...prev, ...updated } : prev));
                setOrders(prev => prev.map(o => (o.order_id === updated.order_id ? { ...o, ...updated } : o)));
            }
            if (successKey) toast.success(t(successKey), { toastId: "order-line-edit" });
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "order-line-edit" });
            return false;
        } finally {
            setLineEditBusy(false);
        }
    };

    const handleAddProduct = async (p: PickedProduct, quantity: number, unitPrice: number) => {
        const ok = await applyLineEdit("/items", "POST", { sku: p.sku, quantity, unit_price: unitPrice }, "order_line_added");
        if (ok) setAddingProduct(false);
    };

    // Clicking Remove only opens the confirm; the DELETE fires from
    // confirmRemoveLine once the admin has seen the SKU and quantity.
    const handleRemoveLine = (item: DispatchOrderItem) => {
        if (item.line_position == null) return;
        setRemovePrompt(item);
    };

    const confirmRemoveLine = async () => {
        const item = removePrompt;
        if (!item || item.line_position == null) return;
        const ok = await applyLineEdit(`/items/${item.line_position}`, "DELETE", undefined, "order_line_removed");
        if (ok) setRemovePrompt(null);
    };

    // ---- Remove an order from a consolidated shipment ---------------------
    // A consolidation's lines are per-order copies, so neither the master
    // nor a child takes line edits: the way to change what ships is to
    // pull the ORDER out (DELETE /orders/:master/consolidated/:child). The
    // child goes back to unfulfilled on its own and is not merged back
    // automatically; an emptied master is cancelled. The BE is the judge of
    // whether the shipment is still early enough (409 with a reason).
    const confirmRemoveFromShipment = async () => {
        const p = detachPrompt;
        if (!p || detachBusy) return;
        setDetachBusy(true);
        try {
            const res = await apiFetch(
                `/orders/${encodeURIComponent(p.masterId)}/consolidated/${encodeURIComponent(p.childId)}`,
                { method: "DELETE" },
            );
            const data = await res.json().catch(() => ({} as Record<string, unknown>));
            if (!res.ok) {
                toast.error((data?.message as string) || t("server_error"), { toastId: "order-detach" });
                return;
            }
            setDetachPrompt(null);
            toast.success(
                t(data?.master_cancelled ? "orders_removed_from_shipment_last" : "orders_removed_from_shipment", {
                    order: p.childNumber,
                    master: p.masterNumber,
                }),
                { toastId: "order-detach" },
            );
            // Both rows changed (the master's lines, the child's status) —
            // reload whichever is open from the server and refresh the list.
            if (selectedOrder) await openRelatedOrder(selectedOrder.order_id);
            setRefreshKey(k => k + 1);
        } catch {
            toast.error(t("server_error"), { toastId: "order-detach" });
        } finally {
            setDetachBusy(false);
        }
    };

    const handleLineQuantity = (item: DispatchOrderItem, raw: string) => {
        if (item.line_position == null) return;
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 1 || n === item.quantity) return;
        if (n < item.quantity) {
            setQtyPrompt({ item, quantity: n });
            return;
        }
        void applyLineEdit(`/items/${item.line_position}/quantity`, "PATCH", { quantity: n }, "order_line_updated");
    };

    // Dismiss the reduce prompt and snap the qty box back to the real
    // quantity — the order was never changed.
    const cancelLineQuantity = () => {
        setQtyPrompt(null);
        setQtyResetTick(n => n + 1);
    };

    const confirmLineQuantity = async () => {
        const p = qtyPrompt;
        if (!p || p.item.line_position == null) return;
        const ok = await applyLineEdit(`/items/${p.item.line_position}/quantity`, "PATCH", { quantity: p.quantity }, "order_line_updated");
        // On success the re-fetched quantity remounts the input via its key;
        // on failure the order still holds the old quantity, so reset the box.
        if (ok) setQtyPrompt(null);
        else cancelLineQuantity();
    };

    return (
        <div className="dispatch-page page-data page-data-wide dispatch-orders-page" style={{ height: "calc(100vh - 56px)", overflowY: "auto", overflowX: "hidden" }}>
            <AdminPageHeader
                titleKey="orders"
                backTo="/admin"
                hideSearch
                crumbs={[
                    { labelKey: "admin_panel", to: "/admin" },
                    { labelKey: "orders" },
                ]}
            />

            {/* Hidden file input — opened by the Upload action inside the
                toolbar bulk-actions popover (see dispatch-search-toolbar). */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                }}
            />
            {/* Bulk shipping labels: many files at once, matched to orders by
                filename inside the modal (see bulk-label-upload-modal). */}
            <input
                ref={labelFilesInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                style={{ display: "none" }}
                data-testid="bulk-labels-input"
                onChange={e => {
                    const picked = Array.from(e.target.files ?? []);
                    if (picked.length > 0) setBulkLabelFiles(picked);
                    e.target.value = "";
                }}
            />
            {bulkLabelFiles && (
                <BulkLabelUploadModal files={bulkLabelFiles} onClose={() => setBulkLabelFiles(null)} />
            )}
            <input
                ref={fulfilFileInputRef}
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                style={{ display: "none" }}
                data-testid="bulk-fulfil-input"
                onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) setBulkFulfilFile(f);
                    e.target.value = "";
                }}
            />
            {bulkFulfilFile && (
                <BulkFulfilUploadModal
                    file={bulkFulfilFile}
                    warehouse={warehouse}
                    onClose={() => setBulkFulfilFile(null)}
                    onApplied={() => setRefreshKey(k => k + 1)}
                />
            )}

            {lastResult && (
                <div className={`totes-admin-result${lastResult.errors.length > 0 ? " totes-admin-result--rejected" : ""}`}>
                    {lastResult.errors.length > 0
                        ? <AlertCircle strokeWidth={3} size={16} className="totes-admin-result-icon" />
                        : <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-icon" />}
                    <span>
                        {lastResult.applied === false
                            ? t("orders_upload_rejected", { n: String(lastResult.errors.length) })
                            : lastResultStatus
                                ? t("bulk_status_summary", {
                                    n: String(lastResult.updated),
                                    status: t(`status_${lastResultStatus}`),
                                })
                                : t("totes_bulk_summary", {
                                    added: String(lastResult.added),
                                    updated: String(lastResult.updated),
                                    removed: String(lastResult.removed),
                                })}
                        {lastResult.consolidated && lastResult.consolidated.length > 0 && (
                            <> — {releaseConsolidatedText(lastResult.consolidated)}</>
                        )}
                    </span>
                    {lastResult.errors.length > 0 && (
                        // Open by default — the errors ARE the message;
                        // don't hide them a click away.
                        <details className="totes-admin-errors" open>
                            <summary>
                                <AlertCircle size={14} />
                                {lastResult.errors.length} {lastResult.errors.length === 1 ? "error" : "errors"}
                            </summary>
                            <ul>
                                {lastResult.errors.slice(0, 50).map((e, i) => (
                                    <li key={i}>
                                        {t("totes_row_label", { row: String(e.row) })}: {e.reason}
                                    </li>
                                ))}
                                {lastResult.errors.length > 50 && (
                                    <li>{t("orders_more_errors", { n: String(lastResult.errors.length - 50) })}</li>
                                )}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            {/* Single toolbar card holds the search input, date-preset segmented
                control, custom date range, and warehouse filter on one line at
                desktop widths. Wraps gracefully on narrow viewports. */}
            <div className="dispatch-search-toolbar">
                <div className="dispatch-search-toolbar-search">
                    <Search size={16} />
                    <input
                        autoFocus
                        type="search"
                        value={queryDraft}
                        onChange={e => {
                            const v = e.target.value;
                            setQueryDraft(v);
                            // Clearing the field (native clear button or
                            // Ctrl+A → Delete) should apply immediately so the
                            // filter doesn't linger after the input looks empty.
                            if (v === "") setQuery("");
                        }}
                        onKeyDown={e => {
                            if (e.key === "Enter") setQuery(queryDraft);
                        }}
                        placeholder={t("search_placeholder")}
                    />
                </div>

                <div className="dispatch-search-toolbar-divider" aria-hidden="true" />

                {/* Date filter — collapsed to a single dropdown. Picking
                    "Custom" reveals a small popover with From/To inputs so
                    those fields don't take up permanent toolbar real estate.
                    Shows the range for the selected pill's group: blank on
                    open pills, last 7 days on Fulfilled / Refunded /
                    Cancelled (see OrderDates). */}
                <div className="dispatch-search-field dispatch-date-picker" ref={customDateRef}>
                    <span className="dispatch-search-field-label">{t("filter_date")}</span>
                    <select
                        className="dispatch-search-select"
                        value={shownDate.preset}
                        onChange={e => {
                            const v = e.target.value as DatePreset;
                            applyPreset(v);
                            setCustomDateOpen(v === "custom");
                        }}
                    >
                        {DATE_PRESETS.map(p => (
                            <option key={p} value={p}>{t(p === "all" ? "filter_all_dates" : `filter_${p}`)}</option>
                        ))}
                        <option value="custom">{t("filter_custom")}</option>
                    </select>
                    {shownDate.preset === "custom" && customDateOpen && (
                        <div className="picking-custom-popover dispatch-date-popover" role="dialog" aria-label={t("filter_custom")}>
                            <label className="picking-custom-field">
                                <span className="picking-custom-field-label">{t("filter_from")}</span>
                                <input
                                    type="date"
                                    className="picking-custom-input"
                                    value={shownDate.from}
                                    onChange={e => setShownDate({ ...shownDate, from: e.target.value })}
                                />
                            </label>
                            <label className="picking-custom-field">
                                <span className="picking-custom-field-label">{t("filter_to")}</span>
                                <input
                                    type="date"
                                    className="picking-custom-input"
                                    value={shownDate.to}
                                    onChange={e => setShownDate({ ...shownDate, to: e.target.value })}
                                />
                            </label>
                            <div className="picking-custom-actions">
                                <button
                                    type="button"
                                    className="picking-custom-clear"
                                    onClick={() => setShownDate({ ...shownDate, from: "", to: "" })}
                                    disabled={!shownDate.from && !shownDate.to}
                                >
                                    {t("picking_filter_custom_clear")}
                                </button>
                                <button
                                    type="button"
                                    className="picking-custom-done"
                                    onClick={() => setCustomDateOpen(false)}
                                >
                                    {t("picking_filter_custom_done")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="dispatch-search-toolbar-divider" aria-hidden="true" />

                {/* Delivery-type toggle — icon-only Express / Standard segments
                    mirroring the picking page's filter pattern. Clicking the
                    active segment clears back to "all". Each segment carries
                    the count it would show if selected. */}
                <div className="dispatch-delivery-filter" role="tablist" aria-label={t("picking_filter_delivery_aria")}>
                    {(["express", "standard", "collection"] as const).map(seg => {
                        const active = deliveryFilter === seg;
                        const count = facetCounts[seg];
                        const label = seg === "express"
                            ? t("delivery_type_express")
                            : seg === "collection"
                                ? t("delivery_type_collection")
                                : t("delivery_type_standard");
                        return (
                            <button
                                key={seg}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                className={`dispatch-delivery-filter-btn${active ? " dispatch-delivery-filter-btn--on" : ""}`}
                                onClick={() => setDeliveryFilter(active ? "all" : seg)}
                                title={`${label} · ${count}`}
                                aria-label={label}
                            >
                                {seg === "express"
                                    ? <Zap size={14} fill="currentColor" stroke="none" aria-hidden="true" />
                                    : seg === "collection"
                                        ? <Store size={14} strokeWidth={2} aria-hidden="true" />
                                        : <Truck size={14} strokeWidth={2} aria-hidden="true" />}
                                {count > 0 && <span className="dispatch-delivery-filter-count">{count}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* Shipping-state toggles — Unshipped (anything not yet
                    shipped) and Needs-to-go (unshipped AND due today or
                    earlier under the 13:00 / weekend / Monday-standard
                    rule). Independent checkboxes, not a segmented radio:
                    both can be on together, alongside Express/Standard. */}
                <div className="dispatch-delivery-filter" role="group" aria-label={t("filter_shipping_state_aria")}>
                    {([
                        { key: "unshipped", on: unshippedOnly, set: setUnshippedOnly, label: t("filter_unshipped"), count: facetCounts.unshipped },
                        { key: "due",       on: dueTodayOnly,  set: setDueTodayOnly,  label: t("filter_due_today"), count: facetCounts.due },
                    ] as const).map(tg => (
                        <button
                            key={tg.key}
                            type="button"
                            aria-pressed={tg.on}
                            className={`dispatch-delivery-filter-btn${tg.on ? " dispatch-delivery-filter-btn--on" : ""}`}
                            onClick={() => tg.set(!tg.on)}
                            title={`${tg.label} · ${tg.count}`}
                            aria-label={tg.label}
                        >
                            {tg.key === "unshipped"
                                ? <PackageOpen size={14} strokeWidth={2} aria-hidden="true" />
                                : <Clock size={14} strokeWidth={2} aria-hidden="true" />}
                            {tg.count > 0 && <span className="dispatch-delivery-filter-count">{tg.count}</span>}
                        </button>
                    ))}
                </div>

                <div className="dispatch-search-toolbar-divider" aria-hidden="true" />

                <div className="dispatch-search-toolbar-fields">
                    {/* Channel dropdown — options are the channels present in
                        the current results (every other filter applied), each
                        with the count it would show if picked. */}
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_channel")}</span>
                        <select
                            className="dispatch-search-select dispatch-search-select--channel"
                            value={channelFilter}
                            onChange={e => setChannelFilter(e.target.value)}
                        >
                            <option value="">{t("filter_all_channels")} ({channelTotal})</option>
                            {channelOptions.map(c => (
                                <option key={c.key} value={c.key}>{channelLabel(c.key, t)} ({c.count})</option>
                            ))}
                        </select>
                    </label>
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_warehouse")}</span>
                        {/* Other warehouses = N (D15): own warehouse only, shown as a fixed value. */}
                        {!warehouseScope.canCross ? (
                            <select className="dispatch-search-select dispatch-search-select--warehouse" value={warehouseScope.own} disabled aria-disabled>
                                <option value={warehouseScope.own}>{warehouseScope.own}</option>
                            </select>
                        ) : (
                        <select
                            className="dispatch-search-select dispatch-search-select--warehouse"
                            value={warehouse}
                            onChange={e => setWarehouse(e.target.value)}
                        >
                            <option value="">{t("filter_all_warehouses")}</option>
                            {warehousesByCountry.map(g => (
                                <optgroup key={g.country} label={g.country}>
                                    <option value={`country:${g.country}`}>
                                        {t("filter_country_all", { country: g.country })}
                                    </option>
                                    {g.warehouses.map(w => (
                                        <option key={w} value={w}>{w}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        )}
                    </label>
                    {/* Sort dropdown — kept on the same toolbar line as the
                        other filters. Selecting a column resets the
                        direction to desc; the column-header arrow toggles
                        direction once a column is active. */}
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("sort_by")}</span>
                        <select
                            className="dispatch-search-select"
                            value={sortKey}
                            onChange={e => { setSortKey(e.target.value as SortKey); setSortDir("desc"); }}
                        >
                            {SORT_OPTIONS.map(k => (
                                <option key={k} value={k}>{t(SORT_LABEL_KEY[k])}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="dispatch-search-sort-dir"
                            onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
                            title={sortDir === "asc" ? t("sort_ascending") : t("sort_descending")}
                            aria-label={sortDir === "asc" ? t("sort_ascending") : t("sort_descending")}
                        >
                            {sortDir === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                        </button>
                    </label>
                    {/* Manual refresh — re-runs the /orders fetch on demand.
                        Deliberately not an auto-poll: sorting, pagination and
                        row selection are all client-side, so data swapping
                        underneath the user would shuffle rows mid-read. */}
                    <button
                        type="button"
                        className={`dispatch-search-sort-dir dispatch-search-refresh${loading ? " dispatch-search-refresh--busy" : ""}`}
                        onClick={() => setRefreshKey(k => k + 1)}
                        disabled={loading}
                        title={t("refresh")}
                        aria-label={t("refresh")}
                    >
                        <RefreshCw size={14} />
                    </button>
                </div>

                {/* Right-hand action group: ⋯ bulk-actions menu (Download
                    Template / Download Current / Upload CSV) followed by the
                    orange + New order button. One non-wrapping flex item so
                    the pair always sits together on the same row. */}
                <div className="dispatch-toolbar-end">
                <div className="dispatch-toolbar-actions" ref={actionsMenuRef}>
                    <button
                        type="button"
                        className="dispatch-toolbar-actions-trigger"
                        onClick={() => setActionsMenuOpen(o => !o)}
                        aria-haspopup="menu"
                        aria-expanded={actionsMenuOpen}
                        aria-label={t("orders_bulk_actions")}
                        title={t("orders_bulk_actions")}
                    >
                        <MoreVertical size={16} />
                    </button>
                    {actionsMenuOpen && (
                        <div className="dispatch-toolbar-actions-menu" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                className="dispatch-toolbar-actions-item"
                                onClick={() => { setActionsMenuOpen(false); handleDownloadTemplate(); }}
                            >
                                <Download size={14} />
                                <span>{t("totes_download_template")}</span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="dispatch-toolbar-actions-item"
                                onClick={() => { setActionsMenuOpen(false); void handleDownloadCurrent(); }}
                                disabled={orders.length === 0}
                            >
                                <Download size={14} />
                                <span>{t("totes_download_current")}</span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="dispatch-toolbar-actions-item dispatch-toolbar-actions-item--primary"
                                onClick={() => { setActionsMenuOpen(false); fileInputRef.current?.click(); }}
                                disabled={uploading || readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Upload size={14} />
                                <span>
                                    {uploading ? t("submitting") : t("totes_upload_csv")}
                                </span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="dispatch-toolbar-actions-item"
                                onClick={() => { setActionsMenuOpen(false); labelFilesInputRef.current?.click(); }}
                                disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Tag size={14} />
                                <span>{t("orders_bulk_labels")}</span>
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="dispatch-toolbar-actions-item"
                                onClick={() => { setActionsMenuOpen(false); fulfilFileInputRef.current?.click(); }}
                                disabled={readOnly || !isSingleWarehouse(warehouse)}
                                title={readOnly ? t("read_only_mode") : !isSingleWarehouse(warehouse) ? t("bulk_fulfil_need_warehouse") : undefined}
                            >
                                <PackageCheck size={14} />
                                <span>{t("orders_bulk_fulfil")}</span>
                            </button>
                        </div>
                    )}
                </div>
                {canSendReplacement && (
                    <button
                        type="button"
                        className="dispatch-toolbar-new-order"
                        onClick={() => setCreateOpen(true)}
                        disabled={readOnly}
                        aria-disabled={readOnly}
                        title={readOnly ? t("read_only_mode") : t("new_order")}
                        aria-label={t("new_order")}
                    >
                        <Plus size={16} strokeWidth={2.5} />
                    </button>
                )}
                </div>
            </div>

            <div className="dispatch-status-filters" role="tablist" aria-label={t("filter_by_status")}>
                <button
                    className={`dispatch-status-filter ${statusFilter === "all" ? "dispatch-status-filter--active" : ""}`}
                    onClick={() => setStatusFilter("all")}
                >
                    {t("status_all")}
                    <span className="dispatch-status-count">{facetCounts.all}</span>
                </button>
                {FILTER_STATUSES.filter(s => !HIDDEN_FILTER_PILLS.has(s)).map(s => (
                    <button
                        key={s}
                        className={`dispatch-status-filter ${statusFilter === s ? "dispatch-status-filter--active" : ""} ${statusClass(s)}`}
                        onClick={() => setStatusFilter(s)}
                    >
                        {statusLabel(s)}
                        {(facetCounts.status[s] || 0) > 0 && (
                            <span className="dispatch-status-count">{facetCounts.status[s]}</span>
                        )}
                    </button>
                ))}
                {/* Bulk-status-change dropdown — right-aligned within the
                    filter pills row. Disabled until at least one row is
                    selected; the BE refuses changes on dispatch /
                    consolidated orders and surfaces those as row errors. */}
                <div className="dispatch-bulk-actions">
                    {selectedOrderIds.size > 0 && (
                        <span className="dispatch-bulk-count">
                            {t("selected_count", { n: String(selectedOrderIds.size) })}
                        </span>
                    )}
                    <select
                        className="dispatch-bulk-select"
                        value={bulkStatus}
                        onChange={e => setBulkStatus(e.target.value as OrderStatus | "")}
                        disabled={selectedOrderIds.size === 0 || bulkApplying || readOnly}
                    >
                        <option value="">{t("change_status_to")}</option>
                        {BULK_STATUS_TARGETS.map(s => (
                            <option key={s} value={s}>{statusLabel(s)}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="btn btn-primary dispatch-bulk-apply"
                        onClick={handleBulkStatusChange}
                        disabled={selectedOrderIds.size === 0 || !bulkStatus || bulkApplying || readOnly}
                        title={readOnly ? t("read_only_mode") : undefined}
                    >
                        {bulkApplying ? t("submitting") : t("apply")}
                    </button>
                </div>
            </div>

            {/* Results summary — visible whenever there's data, helps confirm
                the filter set is doing what the operator expects. */}
            {!loading && orders.length > 0 && (
                <div className="dispatch-search-summary">
                    {results.length === orders.length
                        ? t("search_results_count", { count: results.length })
                        : t("search_results_count_filtered", { count: results.length, total: orders.length })}
                </div>
            )}

            {loading ? (
                <div className="dispatch-flagged-empty">
                    <p>{t("loading")}</p>
                </div>
            ) : results.length === 0 ? (
                <div className="dispatch-flagged-empty">
                    <Package size={48} strokeWidth={1.5} />
                    <p>{t("no_order_found")}</p>
                </div>
            ) : (
                <>
                    <div className="dispatch-orders-table-wrap">
                        <table className="dispatch-orders-table">
                            <thead>
                                <tr>
                                    <th className="dispatch-orders-th-select">
                                        <input
                                            type="checkbox"
                                            aria-label={t("select_all_visible")}
                                            checked={allVisibleSelected}
                                            onChange={toggleSelectAllVisible}
                                        />
                                    </th>
                                    <SortableTh sk="order_number" sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort} className="dispatch-orders-th-order">{t("col_order_number")}</SortableTh>
                                    <SortableTh sk="status"       sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_status")}</SortableTh>
                                    <th className="dispatch-orders-th-ready" title={t("ready_low_title")}>{t("col_ready_low")}</th>
                                    <SortableTh sk="customer"     sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_customer")}</SortableTh>
                                    <SortableTh sk="channel"      sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_channel")}</SortableTh>
                                    <SortableTh sk="items"        sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort} className="dispatch-orders-th-num">{t("col_items")}</SortableTh>
                                    <SortableTh sk="value"        sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort} className="dispatch-orders-th-num">{t("col_value")}</SortableTh>
                                    <SortableTh sk="created_at"   sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_order_date")}</SortableTh>
                                    <SortableTh sk="packed_at"    sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_fulfilled_date")}</SortableTh>
                                    <SortableTh sk="packed_by"    sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_dispatched_by")}</SortableTh>
                                    <SortableTh sk="delivery_type" sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_delivery_type")}</SortableTh>
                                    <SortableTh sk="notes"        sortKey={sortKey} sortDir={sortDir} onSort={onHeaderSort}>{t("col_notes")}</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                            {pagedResults.map(order => {
                                    const itemCount = orderItemCount(order);
                                    const cur = order.currency || "GBP";
                                    // Open flag reason (if any) wins over the customer
                                    // note in the Notes column so resolvers see why it's
                                    // flagged at a glance — the order's status badge
                                    // already tells them it's flagged.
                                    const flagReason = order.latest_flag?.flagged_reason;
                                    const noteText = flagReason || order.notes || "";
                                    const noteTruncated = truncateCell(noteText);
                                    const customerName = order.customer?.name || "";
                                    // Only a fulfilled order names a packer — see the cell below.
                                    const packerName = (order.packed_at && order.packed_by_name) || "";
                                    const dt = order.delivery_type;
                                    const isSelected = selectedOrderIds.has(order.order_id);
                                    return (
                                        <tr
                                            key={order.order_id}
                                            className={`dispatch-orders-row${isSelected ? " dispatch-orders-row--selected" : ""}`}
                                            onClick={() => openOrderDetail(order)}
                                        >
                                            {/* Stop the row's onClick when the
                                                checkbox is toggled — clicking
                                                the cell shouldn't open the modal. */}
                                            <td
                                                className="dispatch-orders-cell-select"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <input
                                                    type="checkbox"
                                                    aria-label={t("select_row")}
                                                    checked={isSelected}
                                                    onChange={() => toggleSelectOne(order.order_id)}
                                                />
                                            </td>
                                            <td className="dispatch-orders-cell-order dispatch-orders-cell-text">#{order.order_number}</td>
                                            <td>
                                                {order.status === "unfulfilled" && order.stock_blocked === true ? (
                                                    // Blocked orders read as Stock blocked, not
                                                    // Unfulfilled + a chip (per Alex, 2026-08-25).
                                                    <span className="dispatch-status-badge dispatch-status-badge--stock-blocked">
                                                        {t("status_stock_blocked")}
                                                    </span>
                                                ) : (
                                                    <span className={orderStatusClass(order)}>
                                                        {orderStatusLabel(order)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="dispatch-orders-cell-ready" title={isLowReady(order) ? t("ready_low_title") : undefined}>
                                                {isLowReady(order) ? (
                                                    <CheckCircle
                                                        size={16}
                                                        className="dispatch-orders-ready-tick"
                                                        aria-label={t("ready_low_title")}
                                                    />
                                                ) : null}
                                            </td>
                                            <td className="dispatch-orders-cell-text" title={customerName || undefined}>
                                                {customerName ? truncateCell(customerName) : <span className="dispatch-orders-cell-muted">—</span>}
                                            </td>
                                            <td className="dispatch-orders-cell-channel dispatch-orders-cell-text">{channelLabel(channelOf(order), t)}</td>
                                            <td className="dispatch-orders-cell-num">{itemCount}</td>
                                            <td className="dispatch-orders-cell-num">{formatMoney(orderValue(order), cur)}</td>
                                            <td>
                                                <span className="dispatch-orders-date">
                                                    <span className="dispatch-orders-date-time">{formatTimeShort(order.created_at)}</span>
                                                    {formatDateShort(order.created_at)}
                                                </span>
                                            </td>
                                            <td>
                                                {order.packed_at ? (
                                                    <span className="dispatch-orders-date">
                                                        <span className="dispatch-orders-date-time">{formatTimeShort(order.packed_at)}</span>
                                                        {formatDateShort(order.packed_at)}
                                                    </span>
                                                ) : (
                                                    <span className="dispatch-orders-cell-muted">—</span>
                                                )}
                                            </td>
                                            {/* Who packed it. Gated on packed_at rather than on the
                                                name: a reversed fulfilment clears packed_at but keeps
                                                packed_by_name, and orders loaded outside the desk (the
                                                JLI_* bulk uploads) have neither. */}
                                            <td className="dispatch-orders-cell-text" title={packerName || undefined}>
                                                {packerName
                                                    ? truncateCell(packerName)
                                                    : <span className="dispatch-orders-cell-muted">—</span>}
                                            </td>
                                            <td className="dispatch-orders-cell-text">{dt ? t(`delivery_type_${dt}`) : "—"}</td>
                                            <td
                                                className={`dispatch-orders-cell-notes dispatch-orders-cell-text ${flagReason ? "dispatch-orders-cell-notes--flag" : ""}`}
                                                title={noteText || undefined}
                                            >
                                                {noteText ? noteTruncated : <span className="dispatch-orders-cell-muted">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {/* Page-size + paging controls. Reuses the locations-table-*
                        classes so the visual matches Products / Locations / Totes. */}
                    <div className="locations-table-footer">
                        <div className="locations-table-footer-left">
                            <span>{t("locations_showing_range", {
                                from: String(rangeFrom),
                                to: String(rangeTo),
                                total: String(results.length),
                            })}</span>
                            <label className="dispatch-orders-page-size">
                                <span>{t("per_page")}</span>
                                <select
                                    value={pageSize}
                                    onChange={e => setPageSize(Number(e.target.value))}
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Order detail / edit modal — wide two-column layout */}
            {selectedOrder && (() => {
                // Subtotal of items (qty × unit_price). Order header tracks
                // `total` separately (channel-reported), but for the modal
                // we want the breakdown items + shipping + tax = total so
                // the user can see how the order total is composed.
                const itemsSubtotal = lineSummary(selectedOrder)?.value ?? selectedOrder.items.reduce(
                    (s, i) => s + (i.unit_price || 0) * (i.quantity || 1),
                    0,
                );
                const cur = selectedOrder.currency || "GBP";
                // Cast for tax_amount/tax_rate which the API returns but
                // aren't on DispatchOrder yet.
                const sx = selectedOrder as DispatchOrder & { tax_amount?: number | null };
                const shippingAmount = selectedOrder.shipping_paid ?? 0;
                const taxAmount = sx.tax_amount ?? 0;
                // `total` is 0 (not null) when the channel didn't report
                // financials, so `?? fallback` never fired — orderValue
                // reconstructs the gross from the lines in that case and
                // keeps the modal total consistent with the table column.
                const orderTotal = orderValue(selectedOrder);
                const unitCount = orderItemCount(selectedOrder);
                // Per-line shipped/unshipped split, diffed from what packing
                // recorded in the parcels. Hoisted out of the status blocks
                // lower down so the order-items list can flag the outstanding
                // lines with the same numbers rather than a second derivation.
                const shippedPool = new Map<string, number>();
                for (const parcel of selectedOrder.parcels ?? []) {
                    for (const it of parcel.items) {
                        shippedPool.set(it.sku, (shippedPool.get(it.sku) ?? 0) + it.quantity);
                    }
                }
                // Fully refunded order whose lines carry no refund_count
                // (refunded before the column existed, or channel-side):
                // every line came back, so paint them all as refunded
                // rather than showing a refunded order with no red lines.
                const fullRefundNoCounts = selectedOrder.status === "refunded"
                    && selectedOrder.items.every(i => !i.refundCount && !i.refundPending);
                const itemDetails = selectedOrder.items.map(it => {
                    // Decided refunds — issued plus still pending — both
                    // leave the line's outstanding count; the badges below
                    // tell them apart.
                    const issued = fullRefundNoCounts ? (it.quantity || 0) : (it.refundCount ?? 0);
                    const pendingRefund = fullRefundNoCounts ? 0 : (it.refundPending ?? 0);
                    const refunded = issued + pendingRefund;
                    const outstanding = Math.max(0, (it.quantity || 0) - refunded);
                    // Two order lines can carry the same SKU (a re-order of
                    // the same product on one order), so draw from a shared
                    // pool — crediting each line with the full SKU total
                    // would hide the second line's shortfall.
                    const pool = shippedPool.get(it.sku) ?? 0;
                    const shipped = Math.min(pool, outstanding);
                    shippedPool.set(it.sku, pool - shipped);
                    return { item: it, refunded, issued, pendingRefund, shipped, unshipped: outstanding - shipped };
                });
                // Refund maths for the item list + totals block. The
                // Refunded status section lower down shares these.
                const refundTotal = orderRefundTotal(selectedOrder);
                const pendingRefundTotal = orderPendingRefundTotal(selectedOrder);
                const pendingRefundUnits = orderPendingRefundUnits(selectedOrder);
                // A consolidated shipment whose pending refund is its customer orders': each is
                // refunded from its own order (listed on Pending Refund), not from here — unless
                // Issue Refund pays it through the channel, which refunds every part in one press.
                const refundOnParts = selectedOrder.is_consolidation_master === true && (selectedOrder.refund_pending_parts?.length ?? 0) > 0;
                const issuedFromMaster = !!selectedOrder.consolidated_into && refundsViaChannel(selectedOrder);
                const refundedByItemId = new Map(
                    itemDetails.filter(d => d.refunded > 0).map(d => [d.item.item_id, d] as const),
                );
                const replacementsBySku = replacementLinksBySku(selectedOrder);
                // A consolidated child ships inside its MASTER's parcels — it
                // has none of its own, so the diff above would read
                // "Not Shipped" forever. Its 'fulfilled' status (flipped when
                // the master ships) is the truth: show shipped-via-parent.
                const shippedViaParent = !!selectedOrder.consolidated_into
                    && selectedOrder.status === "fulfilled";
                // Only worth flagging lines once SOMETHING has shipped —
                // on an unfulfilled order every line is outstanding and the
                // highlight would just wash the whole list amber.
                const unshippedByItemId = new Map(
                    (selectedOrder.parcels ?? []).length > 0 && !shippedViaParent
                        ? itemDetails.filter(d => d.unshipped > 0).map(d => [d.item.item_id, d] as const)
                        : [],
                );
                const isDelivery = activeAddressTab === "delivery";
                const locked = isOrderLocked(selectedOrder);
                const queueNames = queueNamesByWarehouse[selectedOrder.warehouse || getUserWarehouse()]
                    ?? DEFAULT_SPECIAL_QUEUE_NAMES;
                return (
                <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="modal-card dispatch-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header dispatch-detail-header">
                            <div className="dispatch-detail-header-titles">
                                <h2>{t("order_title_label")} #{selectedOrder.order_number}</h2>
                                {selectedOrder.customer?.name && (
                                    <h3 className="dispatch-detail-header-customer">{selectedOrder.customer.name}</h3>
                                )}
                            </div>
                            <div className="dispatch-detail-header-meta">
                                <span className={orderStatusClass(selectedOrder)}>
                                    {orderStatusLabel(selectedOrder)}
                                </span>
                                {selectedOrder.pick_queue && (
                                    <span className="dispatch-pick-queue-chip">
                                        {selectedOrder.pick_queue === 4
                                            ? t("pick_to_tote")
                                            : selectedOrder.pick_queue === 5
                                                ? t("pallet_picks")
                                                : selectedOrder.pick_queue === 6
                                                    ? t("bulk_pick")
                                                    : queueNames[selectedOrder.pick_queue]}
                                    </span>
                                )}
                                {/* An order the returns portal raised (a replacement or
                                    an exchange) says so: its sales channel is the
                                    original's, so nothing else here would. */}
                                {channelOf(selectedOrder) === RETURNS_CHANNEL_KEY && (
                                    <span className="dispatch-pick-queue-chip" data-testid="order-detail-channel">
                                        {channelLabel(RETURNS_CHANNEL_KEY, t)}
                                    </span>
                                )}
                                <span className="dispatch-detail-header-date">
                                    <span className="dispatch-detail-header-date-label">{t("placed_at")}</span>
                                    {formatDate(selectedOrder.created_at)}
                                </span>
                                {/* Send replacement sits in the header, not the
                                    footer: it is an action ON this order rather
                                    than a way to save the edit form, and the
                                    footer's Save/Cancel pair only exists on the
                                    Details tab. Offered at any status except the
                                    ones already made good financially — a lost
                                    parcel needs replacing whether or not the
                                    original has shipped yet. partially_refunded
                                    stays replaceable: the unrefunded units still
                                    shipped, and the draft caps each line at them. */}
                                {canSendReplacement
                                    && !NOT_REPLACEABLE_STATUSES.has(selectedOrder.status)
                                    && (
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        onClick={() => {
                                            setReplacementParcel(null);
                                            setReplacementFor(selectedOrder);
                                        }}
                                    >
                                        <PackagePlus size={14} />
                                        {t("send_replacement")}
                                    </button>
                                )}
                                <button className="modal-close" onClick={() => setSelectedOrder(null)}><X size={18} /></button>
                            </div>
                        </div>
                        {/* Top-level tab strip — flips the modal body between
                            the rich order detail view and a per-order logs
                            table. Logs uses the full modal width since audit
                            rows are wide; Details keeps the two-column layout. */}
                        <div className="dispatch-detail-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeDetailTab === "details"}
                                className={`dispatch-detail-tab${activeDetailTab === "details" ? " dispatch-detail-tab--active" : ""}`}
                                onClick={() => setActiveDetailTab("details")}
                            >
                                {t("order_tab_details")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeDetailTab === "logs"}
                                className={`dispatch-detail-tab${activeDetailTab === "logs" ? " dispatch-detail-tab--active" : ""}`}
                                onClick={() => setActiveDetailTab("logs")}
                            >
                                {t("logs")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeDetailTab === "attachments"}
                                className={`dispatch-detail-tab${activeDetailTab === "attachments" ? " dispatch-detail-tab--active" : ""}`}
                                onClick={() => setActiveDetailTab("attachments")}
                            >
                                {t("attachments_tab")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeDetailTab === "claims"}
                                className={`dispatch-detail-tab${activeDetailTab === "claims" ? " dispatch-detail-tab--active" : ""}`}
                                onClick={() => setActiveDetailTab("claims")}
                            >
                                {t("claims_tab")}
                            </button>
                        </div>
                        {/* Held by a packer at a desk (desk session): who, where,
                            since when, with a leader Release. Renders nothing
                            without a claim. */}
                        <OrderPackClaim
                            orderId={selectedOrder.order_id}
                            orderNumber={selectedOrder.order_number}
                            status={selectedOrder.status}
                        />
                        {/* The sales channel's own sales invoices and credit
                            notes for this order (BargainFox), each PDF fetched
                            from the channel on click. Renders nothing unless
                            the channel's Integrations switch is on and there
                            is something to list. */}
                        <OrderChannelDocuments orderId={selectedOrder.order_id} />
                        {/* The card top-up paid on the BargainFox Returns site
                            for this exchange order: the sales invoice, every
                            refund with its credit note, and Refund (part or
                            all, refund_issue only). Renders nothing when the
                            order has no returns payment. */}
                        <OrderReturnsPaymentPanel orderId={selectedOrder.order_id} canIssueRefund={canIssueRefund} />
                        {/* BargainFox returns touching this order (the original,
                            a replacement or an exchange), with links to the
                            Returns page and any invoices. Renders nothing
                            when there are none. */}
                        <OrderReturnsPanel orderId={selectedOrder.order_id} onOpenOrder={openRelatedOrder} />
                        {/* Split-order linkage. Shown whenever this order is
                            part of a multi-warehouse / multi-vendor split:
                            its parent reference plus clickable chips to each
                            sibling sub-order. */}
                        {selectedOrder.parent_order_id && (
                            <div className="dispatch-detail-parent-bar">
                                <Link2 size={14} />
                                <span className="dispatch-detail-parent-label">
                                    {t("orders_part_of_parent", {
                                        parent: selectedOrder.parent_order_number || selectedOrder.parent_order_id,
                                    })}
                                </span>
                                {selectedOrder.related_orders && selectedOrder.related_orders.length > 0 && (
                                    <span className="dispatch-detail-related">
                                        <span className="dispatch-detail-related-label">
                                            {t("orders_related_orders")}:
                                        </span>
                                        {selectedOrder.related_orders.map(rel => (
                                            <button
                                                key={rel.order_id}
                                                type="button"
                                                className="dispatch-detail-related-chip"
                                                onClick={() => openRelatedOrder(rel.order_id)}
                                                title={t("orders_view_related")}
                                            >
                                                {rel.warehouse || rel.order_id}
                                                <span className={`dispatch-detail-related-status dispatch-detail-related-status--${rel.status}`}>
                                                    {rel.status}
                                                </span>
                                            </button>
                                        ))}
                                    </span>
                                )}
                            </div>
                        )}
                        {/* Order-consolidation linkage — distinct from the
                            split bar above so operators can't conflate a
                            multi-warehouse split with a merged shipment.
                            A master lists its consolidated children; a
                            child links back to its master shipment. */}
                        {selectedOrder.is_consolidation_master && (() => {
                            // Orders can be pulled out only while the shipment
                            // is still fully pre-pick (the BE re-checks, and
                            // also refuses once a wave has claimed it).
                            const parked = (selectedOrder.consolidated_orders ?? []).filter(c => c.status === "consolidated");
                            const canDetach = canEditOrderLines
                                && (selectedOrder.status === "unfulfilled" || selectedOrder.status === "on_hold");
                            return (
                                <>
                                    <div className="dispatch-detail-parent-bar dispatch-detail-parent-bar--consolidated">
                                        <Boxes size={14} />
                                        <span className="dispatch-detail-parent-label">
                                            {t("orders_consolidated_children")}:
                                        </span>
                                        <span className="dispatch-detail-related">
                                            {(selectedOrder.consolidated_orders ?? []).map(child => (
                                                <span key={child.order_id} className="dispatch-detail-related-group">
                                                    <button
                                                        type="button"
                                                        className="dispatch-detail-related-chip"
                                                        onClick={() => openRelatedOrder(child.order_id)}
                                                        title={t("orders_view_related")}
                                                    >
                                                        {child.order_number || child.order_id}
                                                        <span className={`dispatch-detail-related-status dispatch-detail-related-status--${child.status}`}>
                                                            {child.status}
                                                        </span>
                                                    </button>
                                                    {canDetach && child.status === "consolidated" && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost dispatch-detail-related-remove dispatch-detail-item-remove-btn"
                                                            onClick={() => setDetachPrompt({
                                                                masterId: selectedOrder.order_id,
                                                                masterNumber: selectedOrder.order_number || selectedOrder.order_id,
                                                                childId: child.order_id,
                                                                childNumber: child.order_number || child.order_id,
                                                                last: parked.length === 1,
                                                            })}
                                                            disabled={detachBusy}
                                                            title={t("orders_remove_from_shipment")}
                                                            aria-label={`${t("orders_remove_from_shipment")} ${child.order_number || child.order_id}`}
                                                        >
                                                            <Trash2 size={14} />
                                                            {t("orders_remove_from_shipment")}
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                    {canDetach && (
                                        <p className="admin-settings-section-hint">{t("orders_consolidated_lines_hint")}</p>
                                    )}
                                </>
                            );
                        })()}
                        {selectedOrder.consolidated_into && (
                            <div className="dispatch-detail-parent-bar dispatch-detail-parent-bar--consolidated">
                                <Boxes size={14} />
                                <span className="dispatch-detail-parent-label">
                                    {t("orders_consolidated_into", {
                                        master: selectedOrder.consolidated_master?.order_number
                                            || selectedOrder.consolidated_into,
                                    })}
                                </span>
                                <span className="dispatch-detail-related">
                                    <button
                                        type="button"
                                        className="dispatch-detail-related-chip"
                                        onClick={() => openRelatedOrder(selectedOrder.consolidated_into as string)}
                                        title={t("orders_view_related")}
                                    >
                                        {selectedOrder.consolidated_into}
                                        {selectedOrder.consolidated_master?.status && (
                                            <span className={`dispatch-detail-related-status dispatch-detail-related-status--${selectedOrder.consolidated_master.status}`}>
                                                {selectedOrder.consolidated_master.status}
                                            </span>
                                        )}
                                    </button>
                                    {/* Same action from the child's side: only while
                                        this order is still parked and its shipment
                                        has not started picking. */}
                                    {canEditOrderLines
                                        && selectedOrder.status === "consolidated"
                                        && (selectedOrder.consolidated_master?.status === "unfulfilled"
                                            || selectedOrder.consolidated_master?.status === "on_hold") && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost dispatch-detail-related-remove dispatch-detail-item-remove-btn"
                                            onClick={() => setDetachPrompt({
                                                masterId: selectedOrder.consolidated_into as string,
                                                masterNumber: selectedOrder.consolidated_master?.order_number
                                                    || (selectedOrder.consolidated_into as string),
                                                childId: selectedOrder.order_id,
                                                childNumber: selectedOrder.order_number || selectedOrder.order_id,
                                                last: false,
                                            })}
                                            disabled={detachBusy}
                                            title={t("orders_remove_from_shipment")}
                                        >
                                            <Trash2 size={14} />
                                            {t("orders_remove_from_shipment")}
                                        </button>
                                    )}
                                </span>
                            </div>
                        )}
                        {/* Replacement linkage, both directions — same
                            treatment as the consolidation bars above, because
                            it answers the same question: what other order is
                            this one bound to? */}
                        {(selectedOrder.replacement_orders ?? []).length > 0 && (
                            <div className="dispatch-detail-parent-bar dispatch-detail-parent-bar--replacement">
                                <PackagePlus size={14} />
                                <span className="dispatch-detail-parent-label">
                                    {t("orders_replacements_raised")}:
                                </span>
                                <span className="dispatch-detail-related">
                                    {(selectedOrder.replacement_orders ?? []).map(rep => (
                                        <button
                                            key={rep.order_id}
                                            type="button"
                                            className="dispatch-detail-related-chip"
                                            onClick={() => openRelatedOrder(rep.order_id)}
                                            title={t("orders_view_related")}
                                        >
                                            {rep.order_number || rep.order_id}
                                            <span className={`dispatch-detail-related-status dispatch-detail-related-status--${rep.status}`}>
                                                {rep.status}
                                            </span>
                                        </button>
                                    ))}
                                </span>
                            </div>
                        )}
                        {selectedOrder.replacement_of && (
                            <div className="dispatch-detail-parent-bar dispatch-detail-parent-bar--replacement">
                                <PackagePlus size={14} />
                                <span className="dispatch-detail-parent-label">
                                    {t("orders_replacement_of")}:
                                </span>
                                <span className="dispatch-detail-related">
                                    <button
                                        type="button"
                                        className="dispatch-detail-related-chip"
                                        onClick={() => openRelatedOrder(selectedOrder.replacement_of as string)}
                                        title={t("orders_view_related")}
                                    >
                                        {selectedOrder.replacement_of_order?.order_number
                                            || selectedOrder.replacement_of}
                                        {selectedOrder.replacement_of_order?.status && (
                                            <span className={`dispatch-detail-related-status dispatch-detail-related-status--${selectedOrder.replacement_of_order.status}`}>
                                                {selectedOrder.replacement_of_order.status}
                                            </span>
                                        )}
                                    </button>
                                </span>
                            </div>
                        )}
                        {/* Hold-reason banner. status alone tells operators
                            nothing actionable, so the BE derives WHY at read
                            time (unmapped lines / stock shortfall / manual)
                            and this bar says what unblocks the order. */}
                        {selectedOrder.status === "on_hold" && (
                            <div className="dispatch-detail-hold-banner">
                                <AlertCircle size={14} />
                                <span>
                                    {selectedOrder.on_hold_reason === "unmapped_items"
                                        ? t("hold_reason_unmapped")
                                        : selectedOrder.on_hold_reason === "insufficient_stock"
                                            ? ((selectedOrder.on_hold_warehouses?.length ?? 0) > 1
                                                ? t("hold_reason_stock_multi", { warehouses: (selectedOrder.on_hold_warehouses ?? []).join(` ${t("hold_reason_or")} `) })
                                                : t("hold_reason_stock", { warehouse: selectedOrder.warehouse || "" }))
                                            : selectedOrder.on_hold_reason === "pallet_order"
                                                ? t("hold_reason_pallet_order")
                                                : t("hold_reason_manual")}
                                </span>
                                {/* A stock hold is the same shortage as
                                    stock-blocked one step earlier — same
                                    release-short exit, which also takes
                                    the order off hold. */}
                                {heldInsufficientStock(selectedOrder) && (
                                    <button
                                        type="button"
                                        className="btn btn-danger dispatch-modal-btn"
                                        style={{ marginLeft: "auto" }}
                                        disabled={releasing || readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        onClick={() => openReleaseShort(selectedOrder.order_id)}
                                    >
                                        {t("orders_release_short_btn")}
                                    </button>
                                )}
                            </div>
                        )}
                        {/* Exchange banner (BE migration 263): the order is the
                            new items of a returns-portal exchange. It holds its
                            stock and moves to Unfulfilled by itself when the
                            customer's return is sent / delivered / received;
                            the return (Returns page) releases or cancels it.
                            The status edit here refuses it on purpose. */}
                        {selectedOrder.status === "exchange" && (
                            <div className="dispatch-detail-hold-banner dispatch-detail-hold-banner--exchange">
                                <AlertCircle size={14} />
                                <span>{t("exchange_order_banner")}</span>
                            </div>
                        )}
                        {/* Stock-blocked banner: the order fails the wave
                            candidacy stock gate and will sit unfulfilled
                            forever unless stock arrives — or the admin
                            releases it short right here. */}
                        {selectedOrder.status === "unfulfilled" && selectedOrder.stock_blocked === true && (
                            <div className="dispatch-detail-hold-banner">
                                <AlertCircle size={14} />
                                <span>{t("stock_blocked_banner")}</span>
                                <button
                                    type="button"
                                    className="btn btn-danger dispatch-modal-btn"
                                    style={{ marginLeft: "auto" }}
                                    disabled={releasing || readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                    onClick={() => openReleaseShort(selectedOrder.order_id)}
                                >
                                    {t("orders_release_short_btn")}
                                </button>
                            </div>
                        )}
                        {/* Send to packing: skip picking and hand the order
                            to the dispatch desk under a temporary TMP tote. */}
                        {canEditOrderLines && (
                            <SendToPackPanel
                                orderId={selectedOrder.order_id}
                                status={selectedOrder.status}
                                packedAt={selectedOrder.packed_at}
                                readOnly={readOnly}
                                onSent={markSentToPack}
                            />
                        )}
                        {/* Remove-line confirm: names the SKU and quantity
                            about to leave the order. The BE logs the same
                            details plus the actor on the audit row. */}
                        {removePrompt && (
                            <div className="modal-overlay" onClick={() => !lineEditBusy && setRemovePrompt(null)}>
                                <div className="modal-card" onClick={e => e.stopPropagation()} role="dialog" aria-label={t("order_line_remove_title")}>
                                    <div className="modal-card-header">
                                        <h2>{t("order_line_remove_title")}</h2>
                                        <button className="modal-close" onClick={() => setRemovePrompt(null)} disabled={lineEditBusy} aria-label={t("close")}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="modal-card-body">
                                        <p className="admin-settings-section-hint">
                                            {t("order_line_remove_body", { order: selectedOrder.order_number })}
                                        </p>
                                        <ul className="dispatch-detail-status-list">
                                            <li>
                                                <span className="dispatch-detail-status-qty">−{removePrompt.quantity}</span>
                                                <span className="dispatch-detail-status-title">{removePrompt.title}</span>
                                                <span className="dispatch-detail-status-sku">{removePrompt.sku}</span>
                                            </li>
                                        </ul>
                                        <div className="modal-confirm-buttons">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-lg"
                                                onClick={() => setRemovePrompt(null)}
                                                disabled={lineEditBusy}
                                            >
                                                {t("cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-lg"
                                                onClick={() => { void confirmRemoveLine(); }}
                                                disabled={lineEditBusy}
                                            >
                                                {lineEditBusy
                                                    ? t("submitting")
                                                    : t("order_line_remove_confirm", {
                                                        n: String(removePrompt.quantity),
                                                        sku: removePrompt.sku,
                                                    })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Remove-from-shipment confirm: names the order leaving
                            the consolidated shipment and warns when it is the
                            last one (the emptied master is cancelled). */}
                        {detachPrompt && (
                            <div className="modal-overlay" onClick={() => !detachBusy && setDetachPrompt(null)}>
                                <div className="modal-card" onClick={e => e.stopPropagation()} role="dialog" aria-label={t("orders_remove_from_shipment_title")}>
                                    <div className="modal-card-header">
                                        <h2>{t("orders_remove_from_shipment_title")}</h2>
                                        <button className="modal-close" onClick={() => setDetachPrompt(null)} disabled={detachBusy} aria-label={t("close")}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="modal-card-body">
                                        <p className="admin-settings-section-hint">
                                            {t("orders_remove_from_shipment_body", {
                                                order: detachPrompt.childNumber,
                                                master: detachPrompt.masterNumber,
                                            })}
                                            {detachPrompt.last && (
                                                <>{" "}{t("orders_remove_from_shipment_last_note", { master: detachPrompt.masterNumber })}</>
                                            )}
                                        </p>
                                        <div className="modal-confirm-buttons">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-lg"
                                                onClick={() => setDetachPrompt(null)}
                                                disabled={detachBusy}
                                            >
                                                {t("cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-lg"
                                                onClick={() => { void confirmRemoveFromShipment(); }}
                                                disabled={detachBusy}
                                            >
                                                {detachBusy
                                                    ? t("submitting")
                                                    : t("orders_remove_from_shipment_confirm", { order: detachPrompt.childNumber })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Reduce-quantity confirm: a lowered qty is a partial
                            removal, so it gets the same stop as Remove. */}
                        {qtyPrompt && (
                            <div className="modal-overlay" onClick={() => !lineEditBusy && cancelLineQuantity()}>
                                <div className="modal-card" onClick={e => e.stopPropagation()} role="dialog" aria-label={t("order_line_reduce_title")}>
                                    <div className="modal-card-header">
                                        <h2>{t("order_line_reduce_title")}</h2>
                                        <button className="modal-close" onClick={cancelLineQuantity} disabled={lineEditBusy} aria-label={t("close")}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="modal-card-body">
                                        <p className="admin-settings-section-hint">
                                            {t("order_line_reduce_body", {
                                                order: selectedOrder.order_number,
                                                from: String(qtyPrompt.item.quantity),
                                                to: String(qtyPrompt.quantity),
                                            })}
                                        </p>
                                        <ul className="dispatch-detail-status-list">
                                            <li>
                                                <span className="dispatch-detail-status-qty">−{qtyPrompt.item.quantity - qtyPrompt.quantity}</span>
                                                <span className="dispatch-detail-status-title">{qtyPrompt.item.title}</span>
                                                <span className="dispatch-detail-status-sku">{qtyPrompt.item.sku}</span>
                                            </li>
                                        </ul>
                                        <div className="modal-confirm-buttons">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-lg"
                                                onClick={cancelLineQuantity}
                                                disabled={lineEditBusy}
                                            >
                                                {t("cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-lg"
                                                onClick={() => { void confirmLineQuantity(); }}
                                                disabled={lineEditBusy}
                                            >
                                                {lineEditBusy
                                                    ? t("submitting")
                                                    : t("order_line_remove_confirm", {
                                                        n: String(qtyPrompt.item.quantity - qtyPrompt.quantity),
                                                        sku: qtyPrompt.item.sku,
                                                    })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Release-short confirm: exactly which units get
                            marked missing, before it becomes a decision. */}
                        {releasePrompt && (
                            <div className="modal-overlay" onClick={() => !releasing && setReleasePrompt(null)}>
                                <div className="modal-card" onClick={e => e.stopPropagation()}>
                                    <div className="modal-card-header">
                                        <h2>{t("orders_release_short_title")}</h2>
                                        <button className="modal-close" onClick={() => setReleasePrompt(null)} disabled={releasing}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="modal-card-body">
                                        <p className="admin-settings-section-hint">
                                            {releasePrompt.lines.length === 0
                                                ? t("orders_release_hold_none_body")
                                                : t("orders_release_short_body")}
                                        </p>
                                        {selectedOrder.status === "on_hold" && releasePrompt.lines.length > 0 && (
                                            <p className="admin-settings-section-hint">
                                                {t("orders_release_short_hold_note")}
                                            </p>
                                        )}
                                        <ul className="dispatch-detail-status-list">
                                            {releasePrompt.lines.map(l => (
                                                <li key={`${l.line_position}`}>
                                                    <span className="dispatch-detail-status-qty">−{l.shortfall}</span>
                                                    <span className="dispatch-detail-status-title">{l.title}</span>
                                                    <span className="dispatch-detail-status-sku">{l.sku}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="modal-confirm-buttons">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-lg"
                                                onClick={() => setReleasePrompt(null)}
                                                disabled={releasing}
                                            >
                                                {t("cancel")}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-lg"
                                                onClick={confirmReleaseShort}
                                                disabled={releasing || readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                            >
                                                {releasing
                                                    ? t("submitting")
                                                    : releasePrompt.lines.length === 0
                                                        ? t("orders_release_hold_confirm")
                                                        : t("orders_release_short_confirm", {
                                                            count: String(releasePrompt.lines.reduce((s, l) => s + l.shortfall, 0)),
                                                        })}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeDetailTab === "attachments" ? (
                            <div className="modal-card-body dispatch-detail-logs-body">
                                <OrderAttachments orderId={selectedOrder.order_id} t={t} />
                            </div>
                        ) : activeDetailTab === "claims" ? (
                            <div className="modal-card-body dispatch-detail-logs-body">
                                <OrderClaims order={selectedOrder} t={t} />
                            </div>
                        ) : activeDetailTab === "logs" ? (
                            <div className="modal-card-body dispatch-detail-logs-body">
                                <DispatchOrderLogs orderId={selectedOrder.order_id} t={t} />
                            </div>
                        ) : (
                        <div className="modal-card-body dispatch-detail-body">
                            {/* Left — order breakdown */}
                            <div className="dispatch-detail-left">
                                <p className="dispatch-edit-label" style={{ marginTop: 0 }}>{t("order_items")}</p>
                                <div className="dispatch-detail-items">
                                    {/* A summary list row's lines arrive with the detail fetch. */}
                                    {selectedOrder.items_omitted && (
                                        <div className="locations-table-empty">{t("loading")}</div>
                                    )}
                                    {selectedOrder.items.map(item => {
                                        const needsMapping = item.is_mapped === false && !item.is_non_stock;
                                        const showOriginalSku = item.external_sku && item.external_sku !== item.sku;
                                        const editable = selectedOrder.status === "on_hold" || selectedOrder.status === "unfulfilled";
                                        // A consolidation master's lines are copies of
                                        // its orders' lines — the BE refuses line edits
                                        // on it, so don't offer them. SKU mapping stays.
                                        const linesEditable = editable && !selectedOrder.is_consolidation_master;
                                        const short = item.line_position != null ? shortLines?.get(item.line_position) : undefined;
                                        // Partially fulfilled orders: the lines the
                                        // parcels didn't cover. Flagged here as well as
                                        // in the Not Shipped block below so the gap is
                                        // visible without scrolling past the shipment.
                                        const outstanding = unshippedByItemId.get(item.item_id);
                                        // Units credited back by a flagged-order
                                        // resolution — badge the line so the list
                                        // agrees with the adjusted total below.
                                        const refundedLine = refundedByItemId.get(item.item_id);
                                        const itemReplacements = replacementsBySku.get(item.sku);
                                        // First scannable barcode only (product's own
                                        // barcode before any sku_barcodes alternates).
                                        // Hidden when the BE fell back to the SKU.
                                        const firstBarcode = displayBarcodes(item, 1).shown[0];
                                        return (
                                        <div
                                            key={item.item_id}
                                            className={`dispatch-detail-item${needsMapping ? " dispatch-detail-item--unmapped" : ""}${item.is_non_stock ? " dispatch-detail-item--non-stock" : ""}${short ? " dispatch-detail-item--short" : ""}${outstanding ? " dispatch-detail-item--unshipped" : ""}${refundedLine ? " dispatch-detail-item--refunded" : ""}`}
                                        >
                                            {item.image_url ? (
                                                <img src={item.image_url} alt="" className="dispatch-detail-item-img" />
                                            ) : (
                                                <div className="dispatch-detail-item-img dispatch-detail-item-img--placeholder">
                                                    <Package size={16} />
                                                </div>
                                            )}
                                            <div className="dispatch-detail-item-info">
                                                <p className="dispatch-detail-item-title">{item.title || item.external_sku || item.sku}</p>
                                                <p className="dispatch-detail-item-sku">
                                                    {item.sku}
                                                    {showOriginalSku && (
                                                        <span className="dispatch-detail-item-external">
                                                            {" "}({t("sku_map_original")}: {item.external_sku})
                                                        </span>
                                                    )}
                                                </p>
                                                {firstBarcode && (
                                                    <p className="dispatch-detail-item-barcode">{firstBarcode}</p>
                                                )}
                                                {needsMapping && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--warn">
                                                        <AlertCircle size={12} /> {t("sku_map_needs_mapping")}
                                                    </span>
                                                )}
                                                {item.is_non_stock && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--muted">
                                                        <Ban size={12} /> {t("sku_map_non_stock")}
                                                    </span>
                                                )}
                                                {short && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--short">
                                                        <AlertCircle size={12} />{" "}
                                                        {t("stock_blocked_line_missing", {
                                                            missing: String(short.shortfall),
                                                            need: String(short.need),
                                                        })}
                                                    </span>
                                                )}
                                                {outstanding && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--unshipped">
                                                        <AlertCircle size={12} />{" "}
                                                        {t("order_item_awaiting_fulfilment", {
                                                            n: String(outstanding.unshipped),
                                                            total: String(item.quantity),
                                                        })}
                                                    </span>
                                                )}
                                                {refundedLine && refundedLine.issued > 0 && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--refunded">
                                                        <RotateCcw size={12} />{" "}
                                                        {t("order_item_refunded_badge", {
                                                            n: String(refundedLine.issued),
                                                            total: String(item.quantity),
                                                        })}
                                                    </span>
                                                )}
                                                {refundedLine && refundedLine.pendingRefund > 0 && (
                                                    <span className="dispatch-detail-item-badge dispatch-detail-item-badge--refund-pending">
                                                        <Clock size={12} />{" "}
                                                        {t("order_item_refund_pending_badge", {
                                                            n: String(refundedLine.pendingRefund),
                                                            total: String(item.quantity),
                                                        })}
                                                    </span>
                                                )}
                                                {itemReplacements?.map(rep => (
                                                    <button
                                                        key={rep.order_id}
                                                        type="button"
                                                        className="dispatch-detail-item-badge dispatch-detail-item-badge--replacement"
                                                        onClick={() => openRelatedOrder(rep.order_id)}
                                                        title={t("orders_view_related")}
                                                    >
                                                        <PackagePlus size={12} />{" "}
                                                        {t("order_item_replacement_badge", {
                                                            order: rep.order_number,
                                                            n: String(rep.quantity),
                                                        })}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="dispatch-detail-item-nums">
                                                {linesEditable && canEditOrderLines && item.line_position != null ? (
                                                    <label className="order-line-qty">
                                                        <span>{t("qty")}:</span>
                                                        <input
                                                            key={`${item.item_id}-${item.quantity}-${qtyResetTick}`}
                                                            type="number"
                                                            min={1}
                                                            step={1}
                                                            defaultValue={item.quantity}
                                                            disabled={lineEditBusy || readOnly}
                                                            aria-label={`${t("qty")} ${item.sku}`}
                                                            onBlur={e => handleLineQuantity(item, e.target.value)}
                                                            onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                                        />
                                                    </label>
                                                ) : (
                                                    <span>{t("qty")}: {item.quantity}</span>
                                                )}
                                                <span>{formatMoney(item.unit_price || 0, cur)}</span>
                                                <span className="dispatch-detail-item-weight">{((item.weight_grams || 0) / 1000).toFixed(2)} kg</span>
                                                {editable && item.line_position != null && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-small dispatch-detail-item-map-btn"
                                                        onClick={() => setMappingItem(item)}
                                                        disabled={readOnly}
                                                        title={readOnly ? t("read_only_mode") : undefined}
                                                    >
                                                        <Link2 size={14} />
                                                        {needsMapping ? t("sku_map_action_map") : t("sku_map_action_remap")}
                                                    </button>
                                                )}
                                                {linesEditable && canEditOrderLines && item.line_position != null && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-small dispatch-detail-item-map-btn dispatch-detail-item-remove-btn"
                                                        onClick={() => handleRemoveLine(item)}
                                                        disabled={lineEditBusy || readOnly}
                                                        aria-label={`${t("remove_line")} ${item.sku}`}
                                                        title={readOnly ? t("read_only_mode") : t("remove_line")}
                                                    >
                                                        <Trash2 size={14} />
                                                        {t("remove_line")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                                {/* Add product — only while the order has not started
                                    picking. The BE refuses with a reason once a wave
                                    has allocated stock to it. Follows Orders write like
                                    the other line edits (POST /items is ADMIN_WRITE),
                                    not the replacement key. */}
                                {canEditOrderLines
                                    && !selectedOrder.is_consolidation_master
                                    && (selectedOrder.status === "on_hold" || selectedOrder.status === "unfulfilled") && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm dispatch-detail-add-product"
                                        onClick={() => setAddingProduct(true)}
                                        disabled={lineEditBusy || readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        <Plus size={14} />
                                        {t("add_product")}
                                    </button>
                                )}
                                <div className="dispatch-detail-totals">
                                    <div className="dispatch-detail-totals-row">
                                        <span>
                                            {unitCount}{" "}
                                            {unitCount === 1 ? t("item_singular") : t("item_plural")}
                                        </span>
                                        <span>{formatMoney(itemsSubtotal, cur)}</span>
                                    </div>
                                    <div className="dispatch-detail-totals-row">
                                        <span>{t("order_shipping")}</span>
                                        <span>{formatMoney(shippingAmount, cur)}</span>
                                    </div>
                                    <div className="dispatch-detail-totals-row">
                                        <span>{t("order_tax")}</span>
                                        <span>{formatMoney(taxAmount, cur)}</span>
                                    </div>
                                    {refundTotal > 0 && (
                                        <>
                                            <div className="dispatch-detail-totals-row dispatch-detail-totals-row--refund">
                                                <span>{t("refunded_items")}</span>
                                                <span>−{formatMoney(refundTotal, cur)}</span>
                                            </div>
                                            {!refundsViaChannel(selectedOrder) && (
                                                <p className="dispatch-detail-totals-refund-note">
                                                    {t("refund_manual_channel_note")}
                                                </p>
                                            )}
                                        </>
                                    )}
                                    {pendingRefundTotal > 0 && (
                                        <div className="dispatch-detail-totals-row dispatch-detail-totals-row--refund-pending">
                                            <span>{t("refund_pending_items")}</span>
                                            <span>−{formatMoney(pendingRefundTotal, cur)}</span>
                                        </div>
                                    )}
                                    <div className="dispatch-detail-totals-row dispatch-detail-totals-row--total">
                                        <span>{t("order_total")}</span>
                                        <span>{formatMoney(orderTotal - refundTotal - pendingRefundTotal, cur)}</span>
                                    </div>
                                </div>

                                {/* Pending Refund (2026-10-23): the refund
                                    manager issues the money by hand in the
                                    channel, then marks it here. On a BargainFox
                                    order (2026-10-31) Issue Refund pays it
                                    through BargainFox instead. */}
                                {pendingRefundUnits > 0 && (
                                    <div className="dispatch-detail-refund-pending">
                                        <div className="dispatch-detail-refund-pending-text">
                                            <strong>{t("refund_pending_title", {
                                                n: String(pendingRefundUnits),
                                                amount: formatMoney(pendingRefundTotal, cur),
                                            })}</strong>
                                            <span>{t(refundsViaChannel(selectedOrder) ? "refund_pending_channel_detail" : "refund_pending_detail", { channel: channelLabel(selectedOrder.sales_channel, t) })}</span>
                                            {/* Which lines to refund, right by the button, so the
                                                refund manager doesn't hunt the item list for the
                                                pending badges (Alex, 2026-10-27). */}
                                            <ul className="dispatch-detail-refund-pending-lines" aria-label={t("refund_pending_lines")}>
                                                {itemDetails.filter(d => d.pendingRefund > 0).map(d => (
                                                    <li key={d.item.item_id}>
                                                        <span className="dispatch-detail-refund-pending-qty">{d.pendingRefund} ×</span>
                                                        <span className="dispatch-detail-refund-pending-name">
                                                            {d.item.title || d.item.external_sku || d.item.sku}
                                                            <span className="dispatch-detail-refund-pending-sku">{d.item.sku}</span>
                                                        </span>
                                                        <span className="dispatch-detail-refund-pending-amount">
                                                            {formatMoney((d.item.unit_price || 0) * d.pendingRefund, cur)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                            {/* Why it is owed (Jack, 2026-11-03): every path that
                                                puts units here writes a reason. Absent until the
                                                detail fetch lands (or on an older BE); an empty
                                                list means none was recorded. */}
                                            {selectedOrder.refund_reasons && (
                                                <div className="dispatch-detail-refund-pending-why">
                                                    <strong>{t("refund_pending_why")}</strong>
                                                    {selectedOrder.refund_reasons.length === 0 ? (
                                                        <p className="dispatch-detail-refund-pending-why-none">{t("refund_pending_why_none")}</p>
                                                    ) : (
                                                        <ul aria-label={t("refund_pending_why")}>
                                                            {selectedOrder.refund_reasons.map((r, i) => (
                                                                <li key={`${r.created_at}-${i}`}>
                                                                    <span>{r.body}</span>
                                                                    <span className="dispatch-detail-refund-pending-why-meta">
                                                                        {formatDate(r.created_at)}{r.created_by_name ? ` · ${r.created_by_name}` : ""}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            )}
                                            {/* A consolidated shipment's refund belongs to its customer
                                                orders (Jack, 2026-11-03): each is listed on Pending
                                                Refund and refunded from its own order. */}
                                            {refundOnParts && (
                                                <div className="dispatch-detail-refund-pending-why">
                                                    <strong>{t("refund_pending_parts_title")}</strong>
                                                    <p className="dispatch-detail-refund-pending-why-none">
                                                        {t("refund_pending_parts_hint", { channel: channelLabel(selectedOrder.sales_channel, t) })}
                                                    </p>
                                                    <ul aria-label={t("refund_pending_parts_title")}>
                                                        {(selectedOrder.refund_pending_parts ?? []).map(part => (
                                                            <li key={part.order_id}>
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-detail-related-chip"
                                                                    onClick={() => openRelatedOrder(part.order_id)}
                                                                    title={t("orders_view_related")}
                                                                >
                                                                    #{part.order_number || part.order_id}
                                                                </button>
                                                                <span className="dispatch-detail-refund-pending-why-meta">
                                                                    {t("refund_pending_part_row", { n: String(part.units), amount: formatMoney(part.amount, cur) })}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {issuedFromMaster && (
                                                <p className="dispatch-detail-refund-pending-why-none">
                                                    {t("refund_pending_issued_from_master", { order: selectedOrder.consolidated_master?.order_number || (selectedOrder.consolidated_into as string) })}
                                                </p>
                                            )}
                                        </div>
                                        {canIssueRefund && !(refundOnParts && !refundsViaChannel(selectedOrder)) && !issuedFromMaster && (
                                            <button
                                                type="button"
                                                className="btn btn-primary dispatch-modal-btn"
                                                disabled={issuingRefund}
                                                onClick={() => markRefundIssued(selectedOrder)}
                                            >
                                                <CheckCircle size={14} />
                                                {refundsViaChannel(selectedOrder)
                                                    ? (issuingRefund ? t("refund_issue_channel_saving") : t("refund_issue_channel_button"))
                                                    : (issuingRefund ? t("refund_issue_saving") : t("refund_issue_button"))}
                                            </button>
                                        )}
                                        {canIssueRefund && markOnlyOffer === selectedOrder.order_id && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary dispatch-modal-btn"
                                                disabled={issuingRefund}
                                                onClick={() => markRefundIssued(selectedOrder, { markOnly: true })}
                                            >
                                                {t("refund_issue_mark_only_button", { channel: channelLabel(selectedOrder.sales_channel, t) })}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Shipment table — only shown for orders that
                                    have packed parcels (i.e. fulfilled or
                                    refunded after shipping). Empty otherwise. */}
                                {selectedOrder.parcels && selectedOrder.parcels.length > 0 && (
                                    <div className="dispatch-detail-parcels">
                                        {(() => {
                                            const printable = printableParcels(selectedOrder.parcels);
                                            return (
                                                <div className="dispatch-detail-parcels-head">
                                                    <p className="dispatch-edit-label">{t("shipment_details")}</p>
                                                    {/* Send extra box: one more courier parcel on this
                                                        shipped order, for an item found after dispatch.
                                                        Sits with the parcels it adds to. */}
                                                    {extraBoxOffered(selectedOrder) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary btn-sm"
                                                            disabled={readOnly}
                                                            title={readOnly ? t("read_only_mode") : undefined}
                                                            onClick={() => setExtraBoxFor(selectedOrder)}
                                                        >
                                                            <PackagePlus size={14} />
                                                            {t("extra_box_button")}
                                                        </button>
                                                    )}
                                                    {/* Printer pill so the operator picks the Zebra BEFORE
                                                        reprinting; Print all only earns its place with
                                                        two or more reprintable parcels. */}
                                                    {printable.length > 0 && (
                                                        <div className="dispatch-detail-parcels-head-actions">
                                                            {printable.length > 0 && (
                                                                <DispatchPrinterButton onModeChange={parcelLabels.detectPrinter} />
                                                            )}
                                                            {printable.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={parcelLabels.printAll}
                                                                    disabled={parcelLabels.busy !== null}
                                                                >
                                                                    {parcelLabels.busy === "*"
                                                                        ? <Loader2 size={14} className="spin" />
                                                                        : <Printer size={14} />}
                                                                    {t("print_all")}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <table className="dispatch-detail-parcels-table">
                                            <thead>
                                                <tr>
                                                    <th>{t("col_parcel")}</th>
                                                    <th>{t("col_courier")}</th>
                                                    <th>{t("col_service")}</th>
                                                    <th>{t("col_weight")}</th>
                                                    <th>{t("col_tracking_number")}</th>
                                                    <th>{t("col_status")}</th>
                                                    <th>{t("col_link")}</th>
                                                    <th>{t("col_label")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedOrder.parcels.flatMap((p, i) => {
                                                    const totalParcels = selectedOrder.parcels!.length;
                                                    const pos = p.box_position ?? (i + 1);
                                                    const wKg = ((p.weight_grams ?? 0) / 1000).toFixed(2);
                                                    const isExpanded = expandedParcel === p.tracking_number;
                                                    const hasItems = p.items && p.items.length > 0;
                                                    const out = [
                                                        <tr
                                                            key={p.tracking_number}
                                                            className={isExpanded ? "dispatch-parcel-row--expanded" : ""}
                                                        >
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-parcel-toggle"
                                                                    onClick={() => setExpandedParcel(prev => prev === p.tracking_number ? null : p.tracking_number)}
                                                                    aria-expanded={isExpanded}
                                                                    aria-label={t(isExpanded ? "hide_parcel_items" : "show_parcel_items")}
                                                                >
                                                                    {pos}/{totalParcels}
                                                                </button>
                                                                {p.extra_box && (
                                                                    <span className="dispatch-parcel-extra-tag" title={t("extra_box_tag_hint")}>
                                                                        {t("extra_box_tag")}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>{p.carrier || "—"}</td>
                                                            <td>{p.service || "—"}</td>
                                                            <td>{wKg} kg</td>
                                                            <td
                                                                className="dispatch-detail-parcels-tracking"
                                                                title={displayTracking(p.tracking_number)}
                                                            >
                                                                {displayTracking(p.tracking_number)}
                                                            </td>
                                                            <td>
                                                                {(() => {
                                                                    if (!p.status) return "—";
                                                                    const chip = PARCEL_STATUS_CHIPS[p.status];
                                                                    // Tooltip prefers the carrier's own wording
                                                                    // ("DEPOT SORT SCAN") over the coarse bucket,
                                                                    // and says when we last asked.
                                                                    return (
                                                                        <span
                                                                            className={`dispatch-parcel-status ${chip?.cls ?? "is-unknown"}`}
                                                                            title={parcelStatusTooltip(p, t)}
                                                                        >
                                                                            {chip ? t(chip.key) : p.status}
                                                                        </span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {safeHttpUrl(p.tracking_url) ? (
                                                                    <a
                                                                        href={safeHttpUrl(p.tracking_url)!}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title={safeHttpUrl(p.tracking_url)!}
                                                                    >
                                                                        {t("track")}
                                                                    </a>
                                                                ) : "—"}
                                                            </td>
                                                            <td>
                                                                {/* Reprint / download the label the pack desk
                                                                    printed — served from the stored booking ZPL
                                                                    (or the own-label attachment), never re-booked. */}
                                                                <ParcelLabelButtons
                                                                    parcel={p}
                                                                    index={i}
                                                                    busy={parcelLabels.busy}
                                                                    onPrint={parcelLabels.printParcel}
                                                                    onDownload={parcelLabels.downloadParcel}
                                                                />
                                                                {/* An extra box can be voided on its own, until
                                                                    the courier scans it; the original
                                                                    shipment never can be from here. */}
                                                                {p.extra_box && canSendExtraBox && (!p.status || p.status === "created") && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-ghost btn-sm dispatch-parcel-extra-void"
                                                                        onClick={() => setVoidExtraBoxPrompt(p.tracking_number)}
                                                                        disabled={readOnly || voidingExtraBox !== null}
                                                                        title={t("extra_box_void_hint")}
                                                                    >
                                                                        {voidingExtraBox === p.tracking_number
                                                                            ? <Loader2 size={14} className="spin" />
                                                                            : <Ban size={14} />}
                                                                        {t("extra_box_void")}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>,
                                                    ];
                                                    if (isExpanded) {
                                                        out.push(
                                                            <tr
                                                                key={`${p.tracking_number}-items`}
                                                                className="dispatch-parcel-items-row"
                                                            >
                                                                <td colSpan={8}>
                                                                    {hasItems ? (
                                                                        <ul className="dispatch-parcel-items">
                                                                            {p.items.map(it => (
                                                                                <li key={it.sku}>
                                                                                    <span className="dispatch-parcel-item-qty">{it.quantity}×</span>
                                                                                    <span className="dispatch-parcel-item-title">
                                                                                        {it.title || it.sku}
                                                                                    </span>
                                                                                    {it.title && (
                                                                                        <span className="dispatch-parcel-item-sku">{it.sku}</span>
                                                                                    )}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <span className="dispatch-parcel-items-empty">
                                                                            {t("no_parcel_items_recorded")}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    return out;
                                                })}
                                            </tbody>
                                        </table>
                                        {/* Off-screen collection labels for reprint — see
                                            collectionPrintRef. One child per collection
                                            parcel, matched by data-tracking. */}
                                        {selectedOrder.parcels.some(isCollectionParcel) && (
                                            <div style={{ position: "absolute", left: "-9999px", top: 0, visibility: "hidden" }} aria-hidden="true">
                                                <div ref={collectionPrintRef}>
                                                    {(() => {
                                                        const cols = printableParcels(selectedOrder.parcels).filter(isCollectionParcel);
                                                        return cols.map((p, idx) => (
                                                            <div key={p.tracking_number} data-tracking={p.tracking_number}>
                                                                <CollectionLabel
                                                                    orderNumber={selectedOrder.order_number || selectedOrder.order_id}
                                                                    customerName={selectedOrder.customer?.name || ""}
                                                                    boxIndex={p.box_position ?? idx + 1}
                                                                    boxCount={cols.length}
                                                                    warehouse={selectedOrder.warehouse}
                                                                    packedAt={p.created_at ? new Date(p.created_at) : undefined}
                                                                />
                                                            </div>
                                                        ));
                                                    })()}
                                                </div>
                                            </div>
                                        )}
                                        {/* Freshness of the statuses above. Without
                                            this, an order whose parcels have never
                                            been polled is indistinguishable from one
                                            the carrier simply hasn't scanned yet —
                                            both are a row of grey "Label created"
                                            chips. Say which it is. */}
                                        {(() => {
                                            const checked = lastCarrierCheck(selectedOrder.parcels!);
                                            // A fresh label has nothing to check yet — InPost push
                                            // events by webhook and the poll backs off, so "never
                                            // checked" is only a warning once a parcel has sat
                                            // unchecked for hours.
                                            const oldestUnchecked = Math.min(...selectedOrder.parcels!.map((p) => new Date(p.created_at ?? Date.now()).getTime()));
                                            const staleForHours = (Date.now() - oldestUnchecked) / 3_600_000;
                                            if (!checked && staleForHours < 6) return null;
                                            return checked ? (
                                                <p className="dispatch-detail-parcels-note">
                                                    {t("carrier_status_last_checked", {
                                                        ago: carrierCheckedAgo(checked, t),
                                                    })}
                                                </p>
                                            ) : (
                                                <p className="dispatch-detail-parcels-note dispatch-detail-parcels-note--warn">
                                                    <AlertCircle size={12} />
                                                    {t("carrier_status_poller_idle")}
                                                </p>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* Estimated carrier cost — the invoice-model
                                    prediction stamped at booking time. The
                                    reconciliation anchor for future APC
                                    invoices (estimate vs billed). */}
                                {selectedOrder.carrier_booking?.estimated_cost != null && (
                                    <div className="dispatch-detail-est-cost">
                                        <p className="dispatch-edit-label">{t("carrier_estimated_cost")}</p>
                                        <div className="dispatch-detail-est-cost-row">
                                            <span className="dispatch-detail-est-cost-value">
                                                {formatMoney(selectedOrder.carrier_booking.estimated_cost, cur)}
                                            </span>
                                            <span className="dispatch-detail-est-cost-meta">
                                                {selectedOrder.carrier_booking.service_code}
                                                {selectedOrder.carrier_booking.consignments > 1 &&
                                                    ` · ${t("carrier_estimated_cost_consignments", { n: String(selectedOrder.carrier_booking.consignments) })}`}
                                            </span>
                                        </div>
                                        {(selectedOrder.carrier_booking.estimate_breakdown ?? []).some(c => c.surcharges.length > 0)
                                            && (selectedOrder.carrier_booking.estimate_breakdown ?? []).flatMap(c => c.surcharges).map((s, i) => (
                                                <span key={i} className="dispatch-detail-est-cost-surcharge">
                                                    {s.label}: {formatMoney(s.cost, cur)}
                                                </span>
                                            ))}
                                        <p className="dispatch-detail-est-cost-note">{t("carrier_estimated_cost_note", { carrier: selectedOrder.carrier_booking.carrier ?? "APC" })}</p>
                                    </div>
                                )}

                                {/* Per-item status — what hasn't been shipped
                                    yet. Computed by diffing each order item's
                                    quantity against the running total in the
                                    parcels table. Refunds live in the totals
                                    block and the red item-list highlights.
                                    Sections only render when they have rows. */}
                                {(() => {
                                    // itemDetails / shippedViaParent are derived once at
                                    // the top of the modal — the order-items list above
                                    // flags the same outstanding lines.
                                    const unshippedItems = itemDetails.filter(d => d.unshipped > 0);
                                    const shippedParcels = selectedOrder.parcels ?? [];
                                    return (
                                        <>
                                            {shippedParcels.length > 0 && (
                                                <div className="dispatch-detail-status-block">
                                                    <p className="dispatch-edit-label">
                                                        {t("shipped_items")}{" "}
                                                        {shippedParcels.length > 1 && (
                                                            <span className="dispatch-detail-status-meta">
                                                                ({shippedParcels.length} {t("parcels_count")})
                                                            </span>
                                                        )}
                                                    </p>
                                                    {shippedParcels.map((p, idx) => (
                                                        <div key={p.tracking_number} className="dispatch-detail-shipped-parcel">
                                                            <div className="dispatch-detail-shipped-parcel-head">
                                                                <span className="dispatch-detail-shipped-parcel-num">
                                                                    {t("parcel")} {p.box_position ?? (idx + 1)}/{shippedParcels.length}
                                                                </span>
                                                                <span className="dispatch-detail-shipped-parcel-carrier">
                                                                    {p.carrier || "—"}{p.service ? ` · ${p.service}` : ""}
                                                                </span>
                                                                <span
                                                                    className="dispatch-detail-shipped-parcel-tracking"
                                                                    title={displayTracking(p.tracking_number)}
                                                                >
                                                                    {displayTracking(p.tracking_number)}
                                                                </span>
                                                                {p.status && (
                                                                    <span
                                                                        className={`dispatch-parcel-status ${PARCEL_STATUS_CHIPS[p.status]?.cls ?? "is-unknown"}`}
                                                                        title={parcelStatusTooltip(p, t)}
                                                                    >
                                                                        {PARCEL_STATUS_CHIPS[p.status]
                                                                            ? t(PARCEL_STATUS_CHIPS[p.status].key)
                                                                            : p.status}
                                                                    </span>
                                                                )}
                                                                {safeHttpUrl(p.tracking_url) ? (
                                                                    <a
                                                                        href={safeHttpUrl(p.tracking_url)!}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="dispatch-detail-shipped-parcel-link"
                                                                    >
                                                                        {t("track")}
                                                                    </a>
                                                                ) : null}
                                                                {/* Lost-parcel replacement. The header button
                                                                    replaces any part of the ORDER; this one is
                                                                    scoped to the parcel that went missing, so
                                                                    the draft only offers what was in this box
                                                                    and ticks all of it. Deliberately quiet —
                                                                    the whole-order action stays the primary
                                                                    one. Hidden when packing recorded no
                                                                    contents: there'd be nothing to scope to. */}
                                                                {canSendReplacement
                                                                    && !NOT_REPLACEABLE_STATUSES.has(selectedOrder.status)
                                                                    && (p.items?.length ?? 0) > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        className="dispatch-detail-shipped-parcel-replace"
                                                                        title={t("send_replacement_parcel_hint")}
                                                                        onClick={() => {
                                                                            setReplacementParcel({
                                                                                label: `${t("parcel")} ${p.box_position ?? (idx + 1)}/${shippedParcels.length}`,
                                                                                tracking_number: p.tracking_number,
                                                                                items: p.items.map(it => ({
                                                                                    sku: it.sku,
                                                                                    quantity: it.quantity,
                                                                                })),
                                                                            });
                                                                            setReplacementFor(selectedOrder);
                                                                        }}
                                                                    >
                                                                        <PackagePlus size={12} />
                                                                        {t("send_replacement")}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {p.items && p.items.length > 0 ? (
                                                                <ul className="dispatch-detail-status-list">
                                                                    {p.items.map(it => (
                                                                        <li key={`${p.tracking_number}-${it.sku}`}>
                                                                            <span className="dispatch-detail-status-qty">{it.quantity}×</span>
                                                                            <span className="dispatch-detail-status-title">
                                                                                {it.title || it.sku}
                                                                            </span>
                                                                            {it.title && (
                                                                                <span className="dispatch-detail-status-sku">{it.sku}</span>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <span className="dispatch-parcel-items-empty">
                                                                    {t("no_parcel_items_recorded")}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Cancel label — only while the consignment is
                                                still cancellable (before the carrier's manifest
                                                cutoff). Voids the label at the carrier and
                                                returns the order to dispatch. */}
                                            {shippedParcels.length > 0 && selectedOrder.carrier_booking?.cancellable && (
                                                <div className="dispatch-detail-cancel-label">
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger dispatch-modal-btn"
                                                        disabled={cancellingLabel || readOnly}
                                                        title={readOnly ? t("read_only_mode") : undefined}
                                                        onClick={() => setCancelLabelPrompt(true)}
                                                    >
                                                        {cancellingLabel ? t("loading") : t("cancel_carrier_label")}
                                                    </button>
                                                    <span className="dispatch-detail-cancel-label-note">
                                                        {selectedOrder.carrier_booking.cancel_cutoff
                                                            ? t("cancel_carrier_label_note", { cutoff: selectedOrder.carrier_booking.cancel_cutoff })
                                                            : t("cancel_carrier_label_note_until_scan", { carrier: selectedOrder.carrier_booking.carrier ?? t("carrier_generic") })}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Why is the label being cancelled? The answer picks
                                                the flow: kill the order with it, or send it back to
                                                dispatch to be re-packed from its original tote. */}
                                            {cancelLabelPrompt && (
                                                <div className="modal-overlay" onClick={() => setCancelLabelPrompt(false)}>
                                                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                                                        <div className="modal-card-header">
                                                            <h2>{t("cancel_label_why_title")}</h2>
                                                            <button className="modal-close" onClick={() => setCancelLabelPrompt(false)}>
                                                                <X size={18} />
                                                            </button>
                                                        </div>
                                                        <div className="modal-card-body">
                                                            <div className="dispatch-override-grid">
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-override-option"
                                                                    disabled={cancellingLabel}
                                                                    onClick={() => { setCancelLabelPrompt(false); cancelCarrierLabel(selectedOrder.order_id, "reship"); }}
                                                                >
                                                                    <RefreshCw size={26} />
                                                                    <span>{t("cancel_label_opt_reship")}</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="dispatch-override-option"
                                                                    disabled={cancellingLabel}
                                                                    onClick={() => { setCancelLabelPrompt(false); cancelCarrierLabel(selectedOrder.order_id, "cancel_order"); }}
                                                                >
                                                                    <Ban size={26} />
                                                                    <span>{t("cancel_label_opt_cancel_order")}</span>
                                                                </button>
                                                            </div>
                                                            <p className="admin-settings-section-hint" style={{ marginTop: 12 }}>
                                                                {t("cancel_label_why_hint")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {shippedViaParent && (
                                                <div className="dispatch-detail-status-block">
                                                    <p className="dispatch-edit-label">{t("shipped_items")}</p>
                                                    <div className="dispatch-detail-parent-bar dispatch-detail-parent-bar--consolidated">
                                                        <Boxes size={14} />
                                                        <span className="dispatch-detail-parent-label">
                                                            {t("orders_shipped_via_master", {
                                                                master: selectedOrder.consolidated_master?.order_number
                                                                    || selectedOrder.consolidated_into
                                                                    || "",
                                                            })}
                                                        </span>
                                                        <span className="dispatch-detail-related">
                                                            <button
                                                                type="button"
                                                                className="dispatch-detail-related-chip"
                                                                onClick={() => openRelatedOrder(selectedOrder.consolidated_into as string)}
                                                                title={t("orders_view_related")}
                                                            >
                                                                {selectedOrder.consolidated_master?.order_number
                                                                    || selectedOrder.consolidated_into}
                                                            </button>
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                            {unshippedItems.length > 0 && !shippedViaParent && (
                                                <div className="dispatch-detail-status-block">
                                                    <p className="dispatch-edit-label">{t("not_shipped")}</p>
                                                    <ul className="dispatch-detail-status-list">
                                                        {unshippedItems.map(d => (
                                                            <li key={d.item.item_id}>
                                                                <span className="dispatch-detail-status-qty">{d.unshipped}×</span>
                                                                <span className="dispatch-detail-status-title">
                                                                    {d.item.title || d.item.sku}
                                                                </span>
                                                                {d.item.title && (
                                                                    <span className="dispatch-detail-status-sku">{d.item.sku}</span>
                                                                )}
                                                                <span className="dispatch-detail-status-meta">
                                                                    {t("of_quantity", { n: String(d.item.quantity) })}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Right — address + note form. Tabs let the user
                                flip between delivery and billing addresses;
                                Delivery is the default since it's by far the
                                more common edit path. */}
                            <div className="dispatch-detail-right">
                                {/* Order-level fields (delivery type + note) live in
                                    their own soft-grey card above the address tabs.
                                    These edits apply to the order as a whole rather
                                    than just one of the two address tabs, so they
                                    sit outside that toggle. */}
                                {/* Fulfilled = complete and shipped → the whole
                                    form goes read-only. Partially fulfilled
                                    orders skip this and stay editable for the
                                    unshipped remainder. */}
                                {locked && (
                                    <p className="dispatch-detail-locked-note">
                                        {t("order_locked_fulfilled")}
                                    </p>
                                )}
                                <div className="dispatch-order-meta-card">
                                    <div className="dispatch-order-meta-fields">
                                    <div>
                                    <label className="dispatch-edit-label">{t("delivery_type")}</label>
                                    <select
                                        className="dispatch-delivery-type-select"
                                        value={editDeliveryType}
                                        disabled={locked}
                                        onChange={e => setEditDeliveryType(e.target.value as "" | "standard" | "priority" | "express" | "collection")}
                                    >
                                        {/* Hidden placeholder so an order with a missing /
                                            unrecognised delivery_type opens with no
                                            phantom selection — once the user picks one
                                            of the three real options they can't go back
                                            to the placeholder. */}
                                        <option value="" disabled hidden>—</option>
                                        <option value="standard">{t("delivery_type_standard")}</option>
                                        <option value="priority">{t("delivery_type_priority")}</option>
                                        <option value="express">{t("delivery_type_express")}</option>
                                        <option value="collection">{t("delivery_type_collection")}</option>
                                    </select>
                                    </div>
                                    <div>
                                        <label className="dispatch-edit-label">{t("orders_pick_queue_label")}</label>
                                        <select
                                            className="dispatch-delivery-type-select"
                                            value={editPickQueue ?? ""}
                                            onChange={e => setEditPickQueue(
                                                e.target.value === "" ? null : Number(e.target.value) as PickQueueId,
                                            )}
                                        >
                                            <option value="">{t("orders_pick_queue_none")}</option>
                                            {([1, 2, 3] as const).map(queueId => (
                                                <option key={queueId} value={queueId}>{queueNames[queueId]}</option>
                                            ))}
                                            <option value={6}>{t("bulk_pick")}</option>
                                            <option value={4}>{t("pick_to_tote")}</option>
                                            <option value={5}>{t("pallet_picks")}</option>
                                        </select>
                                        {editPickQueue === 6 && (
                                            <span className="dispatch-edit-hint">{t("orders_bulk_pick_hint")}</span>
                                        )}
                                        {editPickQueue === 4 && (
                                            <span className="dispatch-edit-hint">{t("orders_pick_to_tote_hint")}</span>
                                        )}
                                        {editPickQueue === 5 && (
                                            <span className="dispatch-edit-hint">{t("orders_pallet_picks_hint")}</span>
                                        )}
                                    </div>
                                    </div>

                                    {/* Notes thread — deliberately NOT gated on `locked`:
                                        a fulfilled order can still take a note. Each note
                                        saves itself; the modal's Save button is not involved. */}
                                    <OrderNotes
                                        orderId={selectedOrder.order_id}
                                        t={t}
                                        onLatestChange={latest => {
                                            const id = selectedOrder.order_id;
                                            const notes = latest ?? undefined;
                                            setSelectedOrder(prev => (prev && prev.order_id === id ? { ...prev, notes } : prev));
                                            setOrders(prev => prev.map(o => (o.order_id === id ? { ...o, notes } : o)));
                                        }}
                                    />
                                </div>

                                <div className="dispatch-address-tabs" role="tablist">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={isDelivery}
                                        className={`dispatch-address-tab ${isDelivery ? "dispatch-address-tab--active" : ""}`}
                                        onClick={() => setActiveAddressTab("delivery")}
                                    >
                                        {t("delivery_address")}
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={!isDelivery}
                                        className={`dispatch-address-tab ${!isDelivery ? "dispatch-address-tab--active" : ""}`}
                                        onClick={() => setActiveAddressTab("billing")}
                                    >
                                        {t("billing_address")}
                                    </button>
                                </div>
                                <div className="dispatch-address-form dispatch-address-form--compact">
                                    {/* Disabled fieldset greys every address input in
                                        one go while the Delivery/Billing tabs above
                                        stay clickable for read-only viewing. */}
                                    <fieldset
                                        disabled={locked}
                                        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
                                    >
                                    {isDelivery ? (
                                        <>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("name")}</label>
                                                    <input value={editName} onChange={e => setEditName(e.target.value)} placeholder={t("name")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("company")}</label>
                                                    <input value={editCompany} onChange={e => setEditCompany(e.target.value)} placeholder={t("company_optional")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("address_line_1")}</label>
                                                    <input value={editLine1} onChange={e => setEditLine1(e.target.value)} placeholder={t("address_line_1")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("address_line_2")}</label>
                                                    <input value={editLine2} onChange={e => setEditLine2(e.target.value)} placeholder={t("address_line_2")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("city")}</label>
                                                    <input value={editCity} onChange={e => setEditCity(e.target.value)} placeholder={t("city")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("county")}</label>
                                                    <input value={editCounty} onChange={e => setEditCounty(e.target.value)} placeholder={t("county")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("postcode")}</label>
                                                    <input value={editPostcode} onChange={e => setEditPostcode(e.target.value)} placeholder={t("postcode")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("country")}</label>
                                                    <select
                                                        value={editCountry}
                                                        onChange={e => {
                                                            const next = e.target.value;
                                                            setEditCountry(next);
                                                            // Auto-update dial code on country change
                                                            // unless the operator has manually overridden it.
                                                            const dc = dialCodeForCountry(next);
                                                            if (dc) setEditPhoneCountry(dc);
                                                        }}
                                                    >
                                                        <option value="">—</option>
                                                        {EU_COUNTRY_OPTIONS.map(c => (
                                                            <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("mobile")}</label>
                                                    <div className="dispatch-phone-input-group">
                                                        <select
                                                            className="dispatch-phone-dial-select"
                                                            value={editPhoneCountry}
                                                            onChange={e => setEditPhoneCountry(e.target.value)}
                                                            aria-label={t("phone_country_code")}
                                                            title={t("phone_country_code")}
                                                        >
                                                            <option value="">—</option>
                                                            {EU_COUNTRY_OPTIONS.map(c => (
                                                                <option key={c.code} value={c.dialCode}>{c.dialCode}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            className="dispatch-phone-national"
                                                            value={editPhone}
                                                            onChange={e => setEditPhone(e.target.value)}
                                                            placeholder={EU_PHONE_FORMATS[editCountry]?.mobileExample || t("mobile")}
                                                            type="tel"
                                                            inputMode="numeric"
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("email")}</label>
                                                    <input value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder={t("email")} type="email" />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("name")}</label>
                                                    <input value={editBillingName} onChange={e => setEditBillingName(e.target.value)} placeholder={t("name")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("company")}</label>
                                                    <input value={editBillingCompany} onChange={e => setEditBillingCompany(e.target.value)} placeholder={t("company_optional")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("address_line_1")}</label>
                                                    <input value={editBillingLine1} onChange={e => setEditBillingLine1(e.target.value)} placeholder={t("address_line_1")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("address_line_2")}</label>
                                                    <input value={editBillingLine2} onChange={e => setEditBillingLine2(e.target.value)} placeholder={t("address_line_2")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("city")}</label>
                                                    <input value={editBillingCity} onChange={e => setEditBillingCity(e.target.value)} placeholder={t("city")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("county")}</label>
                                                    <input value={editBillingCounty} onChange={e => setEditBillingCounty(e.target.value)} placeholder={t("county")} />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("postcode")}</label>
                                                    <input value={editBillingPostcode} onChange={e => setEditBillingPostcode(e.target.value)} placeholder={t("postcode")} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("country")}</label>
                                                    <select
                                                        value={editBillingCountry}
                                                        onChange={e => {
                                                            const next = e.target.value;
                                                            setEditBillingCountry(next);
                                                            const dc = dialCodeForCountry(next);
                                                            if (dc) setEditBillingPhoneCountry(dc);
                                                        }}
                                                    >
                                                        <option value="">—</option>
                                                        {EU_COUNTRY_OPTIONS.map(c => (
                                                            <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", gap: 6 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("mobile")}</label>
                                                    <div className="dispatch-phone-input-group">
                                                        <select
                                                            className="dispatch-phone-dial-select"
                                                            value={editBillingPhoneCountry}
                                                            onChange={e => setEditBillingPhoneCountry(e.target.value)}
                                                            aria-label={t("phone_country_code")}
                                                            title={t("phone_country_code")}
                                                        >
                                                            <option value="">—</option>
                                                            {EU_COUNTRY_OPTIONS.map(c => (
                                                                <option key={c.code} value={c.dialCode}>{c.dialCode}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            className="dispatch-phone-national"
                                                            value={editBillingPhone}
                                                            onChange={e => setEditBillingPhone(e.target.value)}
                                                            placeholder={EU_PHONE_FORMATS[editBillingCountry]?.mobileExample || t("mobile")}
                                                            type="tel"
                                                            inputMode="numeric"
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="dispatch-edit-label">{t("email")}</label>
                                                    <input value={editBillingEmail} onChange={e => setEditBillingEmail(e.target.value)} placeholder={t("email")} type="email" />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    </fieldset>
                                </div>

                                <div className="modal-confirm-buttons dispatch-modal-actions" style={{ marginTop: 12 }}>
                                    <button className="btn btn-ghost dispatch-modal-btn" onClick={() => setSelectedOrder(null)}>
                                        {t("cancel")}
                                    </button>
                                    <button
                                        className="btn btn-success dispatch-modal-btn"
                                        onClick={handleSave}
                                        disabled={readOnly || (locked
                                            ? editPickQueue === (selectedOrder.pick_queue ?? null)
                                            : !editHasChanges)}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        <CheckCircle strokeWidth={3} size={10} />
                                        {t("save")}
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </div>
                );
            })()}

            {mappingItem && selectedOrder && (
                <SkuMappingModal
                    order={selectedOrder}
                    item={mappingItem}
                    onMapped={(updated) => {
                        setSelectedOrder(updated);
                        setOrders(prev => prev.map(o => o.order_id === updated.order_id ? updated : o));
                        setMappingItem(null);
                    }}
                    onClose={() => setMappingItem(null)}
                />
            )}

            {replacementFor && (
                <ReplacementOrderModal
                    order={replacementFor}
                    parcel={replacementParcel ?? undefined}
                    onClose={() => { setReplacementFor(null); setReplacementParcel(null); }}
                    onCreated={(replacementNumber) => {
                        setReplacementFor(null);
                        setReplacementParcel(null);
                        toast.success(t("replacement_created", { order: replacementNumber }));
                        // Refresh the list so the new REP order appears, and
                        // re-fetch the original's detail so its new
                        // "Replacements raised" bar shows immediately.
                        setRefreshKey(k => k + 1);
                        hydrateRelatedOrders(replacementFor.order_id);
                    }}
                />
            )}

            {extraBoxFor && (
                <ExtraBoxModal
                    order={extraBoxFor}
                    onClose={() => setExtraBoxFor(null)}
                    // The new box joins the Shipment table as soon as it is recorded.
                    onRecorded={() => hydrateRelatedOrders(extraBoxFor.order_id)}
                />
            )}

            {voidExtraBoxPrompt && selectedOrder && (
                <div className="modal-overlay" onClick={() => !voidingExtraBox && setVoidExtraBoxPrompt(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} role="dialog" aria-label={t("extra_box_void")}>
                        <div className="modal-card-header">
                            <h2>{t("extra_box_void")}</h2>
                            <button className="modal-close" onClick={() => setVoidExtraBoxPrompt(null)} disabled={voidingExtraBox !== null} aria-label={t("close")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="admin-settings-section-hint">
                                {t("extra_box_void_confirm", { tracking: voidExtraBoxPrompt })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost dispatch-modal-btn"
                                    onClick={() => setVoidExtraBoxPrompt(null)}
                                    disabled={voidingExtraBox !== null}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger dispatch-modal-btn"
                                    onClick={() => { void voidExtraBox(selectedOrder, voidExtraBoxPrompt); }}
                                    disabled={voidingExtraBox !== null}
                                >
                                    {voidingExtraBox ? <Loader2 size={14} className="spin" /> : <Ban size={14} />}
                                    {t("extra_box_void")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {createOpen && (
                <CreateOrderModal
                    warehouses={warehousesByCountry.flatMap(g => g.warehouses)}
                    defaultWarehouse={warehouse || getUserWarehouse()}
                    onClose={() => setCreateOpen(false)}
                    onCreated={(order, orderNumber) => {
                        setCreateOpen(false);
                        toast.success(t("order_created", { order: orderNumber }));
                        // The new order is on hold, so it may be outside the
                        // current status/date filter — refresh the list and
                        // open it directly so the admin sees it regardless.
                        setRefreshKey(k => k + 1);
                        if (order) openOrderDetail(order);
                    }}
                />
            )}

            {addingProduct && selectedOrder && (
                <OrderProductPicker
                    subtitle={`${t("order_title_label")} #${selectedOrder.order_number}`}
                    warehouse={selectedOrder.warehouse || undefined}
                    // A held order's lines are not yet in the warehouse's
                    // reserved demand, so they come off the free-stock cap
                    // here; an unfulfilled order's already are.
                    committedQty={sku => selectedOrder.status === "on_hold"
                        ? selectedOrder.items.filter(i => i.sku === sku && !i.is_non_stock).reduce((s, i) => s + (i.quantity || 0), 0)
                        : 0}
                    busy={lineEditBusy}
                    onPick={handleAddProduct}
                    onClose={() => setAddingProduct(false)}
                />
            )}

            {fulfilConfirm && (
                <ManualFulfilModal
                    orders={fulfilConfirm}
                    busy={bulkApplying}
                    onCancel={() => setFulfilConfirm(null)}
                    onConfirm={(tracking) => { void applyBulkStatus("fulfilled", tracking); }}
                />
            )}
        </div>
    );
};

// Activity rows from the unified /orders/:order_id/activity endpoint
// mix audit logs (numeric id), picking events ("pick:42"), flags
// ("flag:5:raised"), and lifecycle synthetics ("lifecycle:packed:...").
// Same shape as LogRow otherwise, so the rendering helpers below treat
// them uniformly.
type ActivityRow = Omit<LogRow, "id"> & { id: number | string };

// Compact timestamp formatter shared by the per-order logs view. Matches
// the formatTs helper in LogsAdmin so timestamps read identically across
// the global logs page and this in-modal view.
const formatLogTs = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Storage locations are stored as a scannable barcode and resolved to
// their display name by the BE ("V27L3" — aisle, bay, level). Operators
// read them in those three parts, so split them on the way to the screen.
// Anything that isn't that shape (areas, lanes, put-wall slots, free
// text) is left exactly as it came back.
const formatLogLocation = (v: string) => {
    const m = /^([A-Za-z]+)(\d+)(L\d+)$/.exec(v.trim());
    return m ? `${m[1]}-${m[2]}-${m[3]}` : v;
};

// Context chips for the Logs tab table — mirrors renderContextChips in
// LogsAdmin so the chip styling lines up with the global logs page.
const renderLogContextChips = (r: ActivityRow) => {
    const chips: { label: string; value: string }[] = [];
    if (r.tote_id)  chips.push({ label: "Tote",   value: r.tote_id });
    if (r.location) chips.push({ label: "Loc",    value: formatLogLocation(r.location) });
    if (r.order_no) chips.push({ label: "Order",  value: r.order_no });
    if (r.po)       chips.push({ label: "PO",     value: r.po });
    if (r.sku)      chips.push({ label: "SKU",    value: r.sku });
    if (r.barcode && r.barcode !== r.sku) chips.push({ label: "Barcode", value: r.barcode });
    if (r.item)     chips.push({ label: "Item",   value: r.item });
    if (chips.length === 0) return <span className="logs-context-empty">—</span>;
    return (
        <span className="logs-context-chips">
            {chips.map(c => (
                <span key={c.label} className="logs-context-chip">
                    <span className="logs-context-chip-label">{c.label}:</span>
                    <span className="logs-context-chip-value">{c.value}</span>
                </span>
            ))}
        </span>
    );
};

// Per-order logs view. Mounted inside the order detail modal when the
// user picks the Logs tab. Hits /admin/logs with the order_number as a
// single-column search so the BE filters server-side via an indexed
// ILIKE rather than streaming the full audit table to us. Clicking a
// row opens a nested log-detail modal layered on top of the order
// modal — the inner modal's overlay click and Escape are isolated to
// just the inner modal so closing it doesn't close the order view.
const DispatchOrderLogs = ({
    orderId,
    t,
}: {
    orderId: string;
    t: (k: string, p?: Record<string, string | number>) => string;
}) => {
    const [rows, setRows] = useState<ActivityRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<ActivityRow | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                // Unified activity endpoint — merges lifecycle (created /
                // packed), flag history, picking_events, and audit logs into
                // one timestamp-sorted feed so the modal shows every action
                // that touched this order regardless of which subsystem
                // logged it.
                const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/activity`, {});
                if (!r.ok || !alive) return;
                const data: { rows: ActivityRow[]; total: number } = await r.json();
                if (!alive) return;
                setRows(Array.isArray(data.rows) ? data.rows : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "order-logs-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [orderId, t]);

    if (loading) return <div className="locations-table-empty">{t("loading")}</div>;
    if (rows.length === 0) return <div className="locations-table-empty">{t("logs_no_matches")}</div>;

    return (
        <>
            <div className="locations-table-wrap dispatch-order-logs-wrap">
                <table className="locations-table">
                    <thead>
                        <tr>
                            <th>{t("logs_col_ts")}</th>
                            <th>{t("logs_col_user")}</th>
                            <th>{t("logs_col_department")}</th>
                            <th>{t("logs_col_action")}</th>
                            <th>{t("logs_col_status")}</th>
                            <th>{t("logs_col_warehouse")}</th>
                            <th>{t("logs_col_context")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr
                                key={r.id}
                                onClick={() => setSelected(r)}
                                className="logs-row"
                                style={{ cursor: "pointer" }}
                            >
                                <td className="logs-cell-ts">{formatLogTs(r.ts)}</td>
                                <td>{r.user_name ?? "—"}</td>
                                <td>{r.department}</td>
                                <td className="logs-cell-action">{r.action}</td>
                                <td>
                                    <span className={`logs-status logs-status--${r.status}`}>{r.status}</span>
                                </td>
                                <td>{r.warehouse ?? "—"}</td>
                                <td>{(() => {
                                    // Customer emails (sent / failed / queued) show who and
                                    // when instead of the generic context chips.
                                    const email = customerEmailOf(r);
                                    return email ? customerEmailChips(email, t) : renderLogContextChips(r);
                                })()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Footer mirrors the LogsAdmin "showing X–Y of Z" affordance
                so admins know whether they're seeing every log for the
                order or just the most-recent 200. */}
            <div className="locations-table-footer">
                <span>{t("locations_showing_range", {
                    from: rows.length === 0 ? "0" : "1",
                    to: String(rows.length),
                    total: String(total),
                })}</span>
            </div>
            {selected && (customerEmailOf(selected)
                // A customer email opens its own view: every detail of the
                // send and the email itself (preview / HTML / plain text).
                ? <CustomerEmailLogModal action={selected.action} email={customerEmailOf(selected)!} onClose={() => setSelected(null)} t={t} />
                : <OrderLogDetailModal row={selected} onClose={() => setSelected(null)} t={t} />
            )}
        </>
    );
};

// Nested modal for a single log row inside the order Logs tab. Mirrors
// the LogDetailDrawer from LogsAdmin (same layout, classes, content)
// but rendered as a sibling overlay on top of the order modal. Click
// outside closes only this inner modal; the click is stopPropagation'd
// so it doesn't bubble up to the order overlay below.
const OrderLogDetailModal = ({
    row, onClose, t,
}: {
    row: ActivityRow;
    onClose: () => void;
    t: (k: string, p?: Record<string, string | number>) => string;
}) => {
    const contextRows: { label: string; value: string }[] = [];
    if (row.tote_id)     contextRows.push({ label: "Tote",     value: row.tote_id });
    if (row.location)    contextRows.push({ label: "Location", value: formatLogLocation(row.location) });
    if (row.order_no)    contextRows.push({ label: "Order",    value: row.order_no });
    if (row.po)          contextRows.push({ label: "PO",       value: row.po });
    if (row.sku)         contextRows.push({ label: "SKU",      value: row.sku });
    if (row.barcode)     contextRows.push({ label: "Barcode",  value: row.barcode });
    if (row.barcode_type) contextRows.push({ label: "Type",    value: row.barcode_type });
    if (row.item)        contextRows.push({ label: "Item",     value: row.item });

    return (
        <div
            className="modal-overlay"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
            <div
                className="modal-card logs-detail-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-card-header">
                    <h2>{row.action}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">
                    <dl className="logs-detail-grid">
                        <dt>{t("logs_col_ts")}</dt>           <dd>{formatLogTs(row.ts)}</dd>
                        <dt>{t("logs_col_user")}</dt>         <dd>{row.user_name ?? "—"}{row.uid != null ? ` (${row.uid})` : ""}</dd>
                        <dt>{t("logs_filter_permission")}</dt><dd>{row.permissions ?? "—"}</dd>
                        <dt>{t("logs_col_warehouse")}</dt>    <dd>{row.warehouse ?? "—"}</dd>
                        <dt>{t("logs_col_department")}</dt>   <dd>{row.department}</dd>
                        <dt>{t("logs_col_status")}</dt>
                        <dd>
                            <span className={`logs-status logs-status--${row.status}`}>{row.status}</span>
                            {row.status_code != null && <span className="logs-detail-muted"> ({row.status_code})</span>}
                        </dd>
                        <dt>{t("logs_drawer_method")}</dt>    <dd><code>{row.method}</code></dd>
                        <dt>{t("logs_drawer_path")}</dt>      <dd><code>{row.path}</code></dd>
                        <dt>{t("logs_drawer_duration")}</dt>  <dd>{row.duration_ms != null ? `${row.duration_ms} ms` : "—"}</dd>
                    </dl>

                    {contextRows.length > 0 && (
                        <>
                            <h3 className="logs-detail-subhead">{t("logs_col_context")}</h3>
                            <dl className="logs-detail-grid">
                                {contextRows.map(c => (
                                    <span key={c.label} style={{ display: "contents" }}>
                                        <dt>{c.label}</dt>
                                        <dd><code>{c.value}</code></dd>
                                    </span>
                                ))}
                            </dl>
                        </>
                    )}

                    {row.metadata && Object.keys(row.metadata).length > 0 && (
                        <>
                            <h3 className="logs-detail-subhead">{t("logs_drawer_metadata")}</h3>
                            <pre className="logs-detail-meta">{JSON.stringify(row.metadata, null, 2)}</pre>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminOrders;
